// ============================================================
// STATE
// ============================================================
const STATE = {
  currentStep: 0,
  schedule: { rows: [], currentRun: null, nextRun: null },
  orderForm: { raw: [], columns: {}, products: [] },
  soh: { raw: [], columns: {}, items: [] },
  monthlySales: { raw: [], months: [], items: [], s12Items: [], s6Items: [], s3Items: [], sPriorItems: [] },
  pos: [],
  backOrders: { raw: [], columns: {}, items: [] },
  config: { runNumber: 86, lowCoverage: 2, highCoverage: 4, coverageManuallySet: false, dataDate: '', sohDate: '' },
  results: { section1: [], section2: [], flags: [] },
  lifecycleOverrides: loadLifecycleOverrides(),
  reviewedProducts: loadReviewedProducts()
};

// Google Sheets sync endpoint
const SYNC_API = 'https://script.google.com/a/macros/kawada.com.au/s/AKfycbwz2yeUgqS2jfTbl1Qau3dEVGDFf5KhHJfbgWFq8ugnAQ45HkGK39A_FM1jcGt16tE/exec';
let _syncUser = localStorage.getItem('kawada_user_name') || '';

function loadLifecycleOverrides() {
  try { return JSON.parse(localStorage.getItem('kawada_lifecycle_overrides') || '{}'); }
  catch(e) { return {}; }
}

function saveLifecycleOverrides() {
  try { localStorage.setItem('kawada_lifecycle_overrides', JSON.stringify(STATE.lifecycleOverrides || {})); }
  catch(e) { /* storage full or unavailable */ }
  // Also sync to Google Sheets (fire and forget)
  syncOverridesToSheet();
}

// Load shared overrides from Google Sheet on startup (fetch — requires "Anyone" deployment)
function loadSharedOverrides() {
  fetch(SYNC_API + '?action=load')
    .then(function(resp) { return resp.json(); })
    .then(function(data) {
      if (data && data.overrides) {
        var remote = {};
        Object.keys(data.overrides).forEach(function(code) {
          if (data.overrides[code] && data.overrides[code].lifecycle) {
            remote[code] = data.overrides[code].lifecycle;
          }
        });
        var merged = Object.assign({}, remote, STATE.lifecycleOverrides);
        STATE.lifecycleOverrides = merged;
        localStorage.setItem('kawada_lifecycle_overrides', JSON.stringify(merged));
        if (typeof updateLifecycleSummary === 'function') try { updateLifecycleSummary(); } catch(e) {}
        if (typeof rebuildCategoryPanels === 'function') try { rebuildCategoryPanels(); } catch(e) {}
      }
    })
    .catch(function(err) { console.log('[Sync] Failed to load overrides:', err); });
}

// Debounced sync to Google Sheet (fetch POST — requires "Anyone" deployment)
let _syncTimer = null;
function syncOverridesToSheet() {
  clearTimeout(_syncTimer);
  _syncTimer = setTimeout(function() {
    const data = STATE.lifecycleOverrides || {};
    if (Object.keys(data).length === 0) return;
    fetch(SYNC_API + '?action=save', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ data: data, user: _syncUser || 'unknown' })
    }).catch(function(err) { console.log('[Sync] Override save error:', err); });
    console.log('[Sync] Sent ' + Object.keys(data).length + ' overrides to Google Sheet');
  }, 1500);
}

// Prompt for user name if not set (for tracking who changed what)
function promptSyncUser() {
  if (_syncUser) return;
  setTimeout(function() {
    const name = prompt('Enter your name (for tracking category changes across the team):');
    if (name && name.trim()) {
      _syncUser = name.trim();
      localStorage.setItem('kawada_user_name', _syncUser);
    }
  }, 2000);
}

// Reviewed products — products confirmed by user, removed from "Needs Review"
const REVIEWED_STORAGE_KEY = 'kawada_reviewed_products';
function loadReviewedProducts() {
  try { return new Set(JSON.parse(localStorage.getItem(REVIEWED_STORAGE_KEY) || '[]')); }
  catch(e) { return new Set(); }
}
function saveReviewedProducts() {
  try { localStorage.setItem(REVIEWED_STORAGE_KEY, JSON.stringify([...STATE.reviewedProducts])); }
  catch(e) {}
}
function markProductReviewed(nc) {
  if (!STATE.reviewedProducts) STATE.reviewedProducts = new Set();
  STATE.reviewedProducts.add(nc);
  saveReviewedProducts();
}

// ============================================================
// SAVED SCHEDULE (localStorage persistence)
// ============================================================
const SCHEDULE_STORAGE_KEY = 'kawada_schedule_rows';

function loadSavedSchedule() {
  try { return JSON.parse(localStorage.getItem(SCHEDULE_STORAGE_KEY) || 'null'); }
  catch(e) { return null; }
}

function saveScheduleToStorage() {
  try {
    // Save the essential fields for each row (no Date objects — those get rebuilt)
    const toSave = STATE.schedule.rows.map(r => ({
      run: r.run, launchDate: r.launchDate, dueDate: r.dueDate,
      exFactory: r.exFactory, vesselDeparture: r.vesselDeparture || '',
      auPortArrival: r.auPortArrival || '', crossDocksReady: r.crossDocksReady || '',
      newItems: r.newItems, status: r.status
    }));
    localStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(toSave));
  } catch(e) { /* storage full or unavailable */ }
  // Also sync to Google Sheet so all team members see updates
  syncScheduleToSheet();
}

// Debounced sync schedule to Google Sheet (fetch POST — requires "Anyone" deployment)
let _schedSyncTimer = null;
function syncScheduleToSheet() {
  clearTimeout(_schedSyncTimer);
  _schedSyncTimer = setTimeout(function() {
    if (!STATE.schedule.rows || STATE.schedule.rows.length === 0) return;
    const rows = STATE.schedule.rows.map(r => ({
      run: r.run || '', dueDate: r.dueDate || '', exFactory: r.exFactory || '',
      vesselDeparture: r.vesselDeparture || '', auPortArrival: r.auPortArrival || '',
      crossDocksReady: r.crossDocksReady || '', status: r.status || ''
    }));
    var statusEl = document.getElementById('sched-sync-status');
    if (statusEl) { statusEl.textContent = 'Syncing...'; statusEl.style.color = '#FF9800'; }
    fetch(SYNC_API + '?action=schedule_save', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ rows: rows, user: _syncUser || localStorage.getItem('kawada_user_name') || 'unknown' })
    }).then(function() {
      if (statusEl) { statusEl.textContent = 'Synced to team'; statusEl.style.color = '#4CAF50'; }
    }).catch(function(err) {
      console.log('[Sync] Schedule save error:', err);
      if (statusEl) { statusEl.textContent = 'Sync failed'; statusEl.style.color = '#f44336'; }
    });
  }, 2000);
}

// Load schedule from Google Sheet via fetch (requires "Anyone" deployment)
function loadScheduleFromSheet() {
  return fetch(SYNC_API + '?action=schedule_load')
    .then(function(resp) { return resp.json(); })
    .then(function(data) {
      if (data && data.rows && data.rows.length > 0) return data.rows;
      return null;
    })
    .catch(function(err) {
      console.log('[Sync] Failed to load schedule:', err);
      return null;
    });
}

function clearSavedSchedule() {
  if (!confirm('Clear saved schedule? You can paste a new one.')) return;
  localStorage.removeItem(SCHEDULE_STORAGE_KEY);
  STATE.schedule = { rows: [], currentRun: null, nextRun: null, runAfterNext: null };
  document.getElementById('saved-schedule-card').style.display = 'none';
  document.getElementById('paste-schedule-card').style.display = '';
  document.getElementById('paste-schedule').value = '';
  document.getElementById('preview-schedule').innerHTML = '';
  document.getElementById('coverage-card').style.display = 'none';
  updateDataStatus();
}

function schedDateToISO(value) {
  if (!value) return '';
  // If already ISO format (YYYY-MM-DD), return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const d = parseDate(value);
  if (d && d > new Date(2024, 0, 1)) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  return '';
}

function schedFormatDate(isoVal) {
  if (!isoVal) return '';
  // Parse as local date parts to avoid UTC timezone shift
  const parts = isoVal.split('-');
  if (parts.length === 3) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return parseInt(parts[2]) + ' ' + months[parseInt(parts[1]) - 1] + ' ' + parts[0];
  }
  const d = new Date(isoVal);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
}

function schedDateCell(idx, field, value) {
  const val = (value || '').toString().trim();
  const isTBC = !val || val.toLowerCase().includes('tbc') || val === '—';
  const isNA = val.toLowerCase().includes('n/a');
  const isoDate = schedDateToISO(val);
  const hasDate = !isTBC && !isNA && isoDate;
  const displayText = isNA ? 'N/A' : (hasDate ? schedFormatDate(isoDate) : 'TBC');
  const labelClass = hasDate ? 'sched-date-label sched-has-date' : 'sched-date-label sched-tbc';

  return `<td class="sched-date-cell">
    <input type="date" class="sched-date-hidden" value="${isoDate}"
      data-idx="${idx}" data-field="${field}" onchange="schedDatePicked(this)">
    <span class="${labelClass}" onclick="schedOpenPicker(this)">${displayText}</span>
  </td>`;
}

function schedOpenPicker(label) {
  const input = label.parentElement.querySelector('.sched-date-hidden');
  if (input.showPicker) { input.showPicker(); } else { input.click(); }
}

function schedDatePicked(input) {
  const idx = parseInt(input.dataset.idx);
  const field = input.dataset.field;
  if (!input.value) {
    // Clear was clicked — set back to TBC
    updateScheduleCell(idx, field, '');
    return;
  }
  updateScheduleCell(idx, field, schedFormatDate(input.value));
}

function renderScheduleEditor() {
  const tbody = document.getElementById('schedule-edit-body');
  if (!tbody) return;
  const STATUS_OPTIONS = ['Delivered','Ordered','On Water','Order Placed','Current Order','Upcoming',''];
  tbody.innerHTML = STATE.schedule.rows.map((r, i) => {
    const isCurrent = (r === STATE.schedule.currentRun);
    const statusOpts = STATUS_OPTIONS.map(s =>
      `<option value="${s}" ${r.status.trim() === s ? 'selected' : ''}>${s || '—'}</option>`
    ).join('');
    return `<tr class="${isCurrent ? 'current-run' : ''}" data-idx="${i}">
      <td contenteditable="true" data-field="run">${r.run}</td>
      ${schedDateCell(i, 'dueDate', r.dueDate)}
      ${schedDateCell(i, 'exFactory', r.exFactory)}
      ${schedDateCell(i, 'vesselDeparture', r.vesselDeparture)}
      ${schedDateCell(i, 'auPortArrival', r.auPortArrival)}
      ${schedDateCell(i, 'crossDocksReady', r.crossDocksReady)}
      <td class="arrival-cell">${r.leadTime || '—'}</td>
      <td><select class="status-select" data-field="status" onchange="updateScheduleCell(${i},'status',this.value)">${statusOpts}</select></td>
      <td><button class="btn-del-row" onclick="deleteScheduleRow(${i})" title="Remove row">&times;</button></td>
    </tr>`;
  }).join('');

  // Attach blur handlers for contenteditable cells (Run name only now)
  tbody.querySelectorAll('td[contenteditable]').forEach(td => {
    td.addEventListener('blur', function() {
      const idx = parseInt(this.parentElement.dataset.idx);
      const field = this.dataset.field;
      updateScheduleCell(idx, field, this.textContent.trim());
    });
    td.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') { e.preventDefault(); this.blur(); }
    });
  });
}

function updateScheduleCell(idx, field, value) {
  const row = STATE.schedule.rows[idx];
  if (!row) return;
  row[field] = value;
  // Recalculate derived fields
  rebuildScheduleDerived();
  saveScheduleToStorage();
  renderScheduleEditor();
  recalcCoverage();
}

function rebuildScheduleDerived() {
  const TRANSIT_WEEKS = 6;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  STATE.schedule.rows.forEach(r => {
    const runMatch = r.run.match(/(\d+\.?\d*)/);
    r.runNum = runMatch ? parseFloat(runMatch[1]) : 0;
    r.statusLower = (r.status || '').trim().toLowerCase();
    r.exFactoryDate = parseDate(r.exFactory);
    r.dueDateParsed = parseDate(r.dueDate);
    r.arrivalDate = null;
    r.arrivalStr = '';
    // Prefer Cross Docks Ready date (actual warehouse arrival) if available,
    // then fall back to ex-factory + transit weeks estimate
    var cdrDate = parseDate(r.crossDocksReady);
    if (cdrDate && cdrDate > new Date(2025, 0, 1)) {
      r.arrivalDate = cdrDate;
      r.arrivalStr = `${cdrDate.getDate()} ${months[cdrDate.getMonth()]} ${cdrDate.getFullYear()}`;
    } else if (r.exFactoryDate && r.exFactoryDate > new Date(2025, 0, 1)) {
      r.arrivalDate = new Date(r.exFactoryDate);
      r.arrivalDate.setDate(r.arrivalDate.getDate() + TRANSIT_WEEKS * 7);
      r.arrivalStr = `${r.arrivalDate.getDate()} ${months[r.arrivalDate.getMonth()]} ${r.arrivalDate.getFullYear()}`;
    }
    // Recalculate lead time: Due Date → Cross Docks Ready (or AU Port, or EXF+6wk default)
    r.leadTime = '';
    if (r.dueDateParsed) {
      const actualEnd = parseDate(r.crossDocksReady) || parseDate(r.auPortArrival);
      if (actualEnd) {
        const diffMs = actualEnd - r.dueDateParsed;
        const diffWeeks = Math.round(diffMs / (1000 * 60 * 60 * 24 * 7));
        r.leadTime = diffWeeks > 0 ? diffWeeks + ' wks' : '';
      } else if (r.exFactoryDate) {
        // Default: 6 weeks from Ex Factory
        const defaultEnd = new Date(r.exFactoryDate);
        defaultEnd.setDate(defaultEnd.getDate() + 6 * 7);
        const diffMs = defaultEnd - r.dueDateParsed;
        const diffWeeks = Math.round(diffMs / (1000 * 60 * 60 * 24 * 7));
        r.leadTime = diffWeeks > 0 ? '~' + diffWeeks + ' wks' : '';
      }
    }
  });
  // Find current run
  const launched = STATE.schedule.rows.filter(r =>
    r.statusLower.includes('launch') || r.statusLower.includes('order sheet') || r.statusLower === 'current order');
  const currentRun = launched.length > 0 ? launched[launched.length - 1] : null;
  STATE.schedule.currentRun = currentRun;
  if (currentRun) {
    STATE.config.runNumber = currentRun.runNum;
    const currentIdx = STATE.schedule.rows.indexOf(currentRun);
    const futureRuns = STATE.schedule.rows.slice(currentIdx + 1).filter(r => r.arrivalDate);
    STATE.schedule.nextRun = futureRuns.length > 0 ? futureRuns[0] : null;
    STATE.schedule.runAfterNext = futureRuns.length > 1 ? futureRuns[1] : null;
  } else {
    STATE.schedule.nextRun = null;
    STATE.schedule.runAfterNext = null;
  }
}

function recalcCoverage() {
  const currentRun = STATE.schedule.currentRun;
  if (!currentRun) return;
  if (!STATE.config.coverageManuallySet) {
    if (currentRun.arrivalDate && STATE.schedule.nextRun && STATE.schedule.nextRun.arrivalDate) {
      const lowMonths = monthsBetween(currentRun.arrivalDate, STATE.schedule.nextRun.arrivalDate);
      if (lowMonths && lowMonths > 0) STATE.config.lowCoverage = Math.round(lowMonths * 4) / 4;
    }
    if (currentRun.arrivalDate && STATE.schedule.runAfterNext && STATE.schedule.runAfterNext.arrivalDate) {
      const highMonths = monthsBetween(currentRun.arrivalDate, STATE.schedule.runAfterNext.arrivalDate);
      if (highMonths && highMonths > 0) STATE.config.highCoverage = Math.round(highMonths * 4) / 4;
    }
    document.getElementById('lowCoverage').value = STATE.config.lowCoverage;
    document.getElementById('highCoverage').value = STATE.config.highCoverage;
  }
  document.getElementById('coverageExplain').textContent =
    `Auto-calculated: LOW = months from ${currentRun.run} in warehouse (${currentRun.arrivalStr}) to ` +
    `${STATE.schedule.nextRun ? STATE.schedule.nextRun.run + ' in warehouse (' + STATE.schedule.nextRun.arrivalStr + ')' : 'next run'}, ` +
    `HIGH = months from ${currentRun.run} in warehouse to ` +
    `${STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run + ' in warehouse (' + STATE.schedule.runAfterNext.arrivalStr + ')' : 'run after next'}. ` +
    `(Transit = 6 weeks from ex-factory.) You can override these values.`;
  document.getElementById('coverage-card').style.display = 'block';
  renderTimeline();
}

function addScheduleRow() {
  // Auto-increment run number from last row
  const lastRow = STATE.schedule.rows[STATE.schedule.rows.length - 1];
  let nextNum = lastRow ? lastRow.runNum + (lastRow.runNum % 1 === 0 ? 1 : 0.5) : 87;
  // If last was e.g. 86, next is 86.5; if 86.5, next is 87
  if (lastRow && lastRow.runNum % 1 === 0) nextNum = lastRow.runNum + 0.5;
  else if (lastRow) nextNum = Math.ceil(lastRow.runNum);
  const newRun = `Run ${nextNum}`;
  STATE.schedule.rows.push({
    run: newRun, launchDate: '', dueDate: '', exFactory: '',
    vesselDeparture: '', auPortArrival: '', crossDocksReady: '',
    exFactoryDate: null, arrivalDate: null, arrivalStr: '',
    leadTime: '', newItems: '', status: 'Upcoming', statusLower: 'upcoming',
    runNum: nextNum, dueDateParsed: null
  });
  saveScheduleToStorage();
  renderScheduleEditor();
}

function deleteScheduleRow(idx) {
  if (STATE.schedule.rows.length <= 1) return;
  STATE.schedule.rows.splice(idx, 1);
  rebuildScheduleDerived();
  saveScheduleToStorage();
  renderScheduleEditor();
  recalcCoverage();
}

function applyScheduleRows(saved) {
  STATE.schedule.rows = saved.map(r => ({
    run: r.run || '', launchDate: r.launchDate || '', dueDate: r.dueDate || '',
    exFactory: r.exFactory || '', vesselDeparture: r.vesselDeparture || '',
    auPortArrival: r.auPortArrival || '', crossDocksReady: r.crossDocksReady || '',
    leadTime: '', newItems: r.newItems || '',
    status: r.status || '', statusLower: '', runNum: 0,
    exFactoryDate: null, dueDateParsed: null, arrivalDate: null, arrivalStr: ''
  }));
  rebuildScheduleDerived();
  document.getElementById('saved-schedule-card').style.display = '';
  document.getElementById('paste-schedule-card').style.display = 'none';
  renderScheduleEditor();
  recalcCoverage();
  markStepComplete(0);
}

function initSavedSchedule() {
  // Try Google Sheet first (shared source of truth), fall back to localStorage
  loadScheduleFromSheet().then(function(sheetRows) {
    if (sheetRows && sheetRows.length > 0) {
      applyScheduleRows(sheetRows);
      // Also update localStorage cache
      localStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(sheetRows));
      var statusEl = document.getElementById('sched-sync-status');
      if (statusEl) { statusEl.textContent = 'Loaded from team'; statusEl.style.color = '#4CAF50'; }
      return;
    }
    // Sheet is empty — fall back to localStorage and push it to the sheet
    var saved = loadSavedSchedule();
    if (saved && saved.length > 0) {
      applyScheduleRows(saved);
      // Auto-push local data to sheet so other team members can see it
      syncScheduleToSheet();
      var statusEl = document.getElementById('sched-sync-status');
      if (statusEl) { statusEl.textContent = 'Synced to team'; statusEl.style.color = '#4CAF50'; }
    }
  }).catch(function() {
    // Network error — fall back to localStorage
    var saved = loadSavedSchedule();
    if (saved && saved.length > 0) {
      applyScheduleRows(saved);
    }
  });
  // Show localStorage data immediately while fetch is in progress
  var saved = loadSavedSchedule();
  if (saved && saved.length > 0) {
    applyScheduleRows(saved);
    return true;
  }
  return false;
}

const STEPS = [
  { id: 'schedule', title: 'Production Schedule', optional: true },
  { id: 'orderform', title: 'Order Form', optional: false },
  { id: 'soh', title: 'Stock on Hand', optional: false },
  { id: 'monthly', title: 'Monthly Sales', optional: true },
  { id: 'pos', title: 'Purchase Orders', optional: true },
  { id: 'backorders', title: 'Back Orders', optional: true },
  { id: 'review', title: 'Review & Adjust', optional: false },
  { id: 'generate', title: 'Generate', optional: false },
];

// ============================================================
// UTILITIES
// ============================================================
function parseTSV(text) {
  if (!text || !text.trim()) return [];
  // Handle quoted fields (Google Sheets wraps cells with newlines in double quotes)
  // First, parse respecting quotes to correctly split rows and columns
  const rows = [];
  let current = '';
  let inQuote = false;
  const chars = text.trim();
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (ch === '"') {
      if (inQuote && i + 1 < chars.length && chars[i + 1] === '"') {
        current += '"'; i++; // escaped quote
      } else {
        inQuote = !inQuote;
      }
    } else if ((ch === '\n' || ch === '\r') && !inQuote) {
      if (ch === '\r' && i + 1 < chars.length && chars[i + 1] === '\n') i++; // skip \r\n
      if (current.trim()) {
        rows.push(current);
      }
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) rows.push(current);

  // Now split each row by tab (or detect delimiter)
  if (rows.length === 0) return [];
  const tabCols = rows[0].split('\t').length;
  let delim = '\t';
  if (tabCols <= 2) {
    const commaCols = rows[0].split(',').length;
    if (commaCols > tabCols) delim = ',';
  }
  return rows.map(line => {
    // Split by delimiter, but strip surrounding quotes from each cell
    return line.split(delim).map(cell => cell.trim().replace(/^"|"$/g, '').trim());
  });
}

function normaliseCode(code) {
  if (!code) return '';
  // Strip ALL non-alphanumeric chars so NBH-249, NBH_249, NBH 249, NBH249 all match
  return code.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function normaliseDesc(desc) {
  if (!desc) return '';
  return desc.toString().toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

function parseNum(val) {
  if (val == null || val === '' || val === '-' || val === '—') return 0;
  // Strip currency symbols, letters (US, USD, AUD etc), commas, spaces
  const cleaned = String(val).replace(/[^0-9.\-]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

function parseDate(str) {
  if (!str || str === '—' || str === '-') return null;
  str = str.trim();
  // Handle "DD Mon YYYY" or "D Mon YYYY"
  const months = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
  // Try standard format first
  let m = str.match(/(\d{1,2})\s+(\w{3,})\s+(\d{4})/);
  if (m) {
    const mon = months[m[2].toLowerCase().substring(0,3)];
    if (mon !== undefined) return new Date(parseInt(m[3]), mon, parseInt(m[1]));
  }
  // Try "Mon YYYY" (e.g. "End May 2026")
  m = str.match(/(\w{3,})\s+(\d{4})/);
  if (m) {
    const mon = months[m[1].toLowerCase().substring(0,3)];
    if (mon !== undefined) return new Date(parseInt(m[2]), mon, 28); // end of month approx
  }
  // Try native parse
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function monthsBetween(d1, d2) {
  if (!d1 || !d2) return null;
  return (d2 - d1) / (1000 * 60 * 60 * 24 * 30.44);
}

function findHeaderRow(rows, patterns) {
  const patternKeys = Object.keys(patterns);
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const row = rows[i];
    if (!row || row.length < 2) continue;
    let matches = 0;
    for (const key of patternKeys) {
      for (let j = 0; j < row.length; j++) {
        const cell = (row[j] || '').toLowerCase();
        if (patterns[key].some(p => cell.includes(p))) {
          matches++;
          break;
        }
      }
    }
    if (matches >= Math.min(2, patternKeys.length)) return i;
  }
  return 0;
}

function mapColumns(headerRow, patterns) {
  const mapping = {};
  for (const [key, keywords] of Object.entries(patterns)) {
    mapping[key] = -1;
    for (let j = 0; j < headerRow.length; j++) {
      const cell = (headerRow[j] || '').toLowerCase();
      if (keywords.some(k => cell.includes(k))) {
        mapping[key] = j;
        break;
      }
    }
  }
  return mapping;
}

function categoriseByCode(code, desc) {
  code = code || '';
  desc = desc || '';
  const dl = desc.toLowerCase();
  if (code.startsWith('NBMC_') || dl.includes('mininano')) return 'mininano';
  if (code.startsWith('NBCC_')) return 'Character';
  if (code.startsWith('NBH_')) return 'Sights to See';
  if ((code.startsWith('NBM') && !code.startsWith('NBMC')) || code.startsWith('NBA')) return 'Advanced Hobby';
  if (code.startsWith('NB_') || code.startsWith('NB-')) {
    if (['pad','tweezers','case','led','arm','plate'].some(w => dl.includes(w))) return 'Accessory';
    return 'Advanced Hobby';
  }
  if (['accessory','pad','plate','tweezers','led'].some(w => dl.includes(w))) return 'Accessory';
  return 'Mini Collection';
}

// Auto-detect lifecycle based on sales data and SOH
// Core = always in stock, strong sellers. Selling Through = may top up but winding down.
// New = new items, may not have first delivery yet. Archived = sold out, not coming back.
function detectLifecycle(s12, s6, s3, soh, incoming, sPrior) {
  sPrior = sPrior || 0;
  const wm = calcWeightedMonthly(s12, s6, s3);
  const hasAnySales = s12 > 0 || s6 > 0 || s3 > 0;
  const totalStock = soh + incoming;

  // New: no current sales, no prior-year sales — genuinely new product (may not have first delivery yet)
  if (!hasAnySales && incoming > 0 && sPrior === 0) return 'New';
  // New items with stock but zero sales and no history — just arrived, limited stock
  if (!hasAnySales && soh > 0 && soh <= 50 && incoming === 0 && sPrior === 0) return 'New';

  // Archived: sold out, not coming back — no stock AND no recent sales
  if (totalStock === 0 && !hasAnySales) return 'Archived';
  if (totalStock === 0 && s3 === 0 && s12 > 0 && s12 < 24) return 'Archived';

  // Core: strong consistent sellers — should always be in stock
  // High volume with recent activity
  if (s12 > 0 && s3 > 0 && wm >= 15) {
    const ratio = s3 / s12;
    if (ratio >= 0.5) return 'Core'; // consistent sales
  }
  if (wm >= 40) return 'Core'; // very high volume regardless
  // Solid recent sales even if 12mo isn't huge yet
  if (s3 > 0 && (s3 / 3) >= 10) return 'Core';

  // Active: regular sellers but not Core-level — decent ongoing sales
  if (s3 > 0 && wm >= 5 && totalStock > 0) return 'Active'; // steady moderate sales with stock
  if (s3 > 0 && s6 > 0 && wm >= 3 && (s3 / Math.max(s6, 1)) >= 0.4) return 'Active'; // consistent recent sales

  // Selling Through: has some sales or stock but declining/low — may top up, generally winding down
  if (!hasAnySales && incoming > 0 && sPrior > 0) return 'Selling Through'; // was selling before, now stopped
  if (hasAnySales && s3 === 0 && soh > 0) return 'Selling Through'; // stopped selling recently
  if (s3 > 0 && s12 > 0 && (s3 / s12) < 0.3 && wm < 5) return 'Selling Through'; // sharp decline
  if (sPrior > 0 && s12 > 0 && (s12 / sPrior) < 0.3 && wm < 5 && soh > 0) return 'Selling Through';
  if (hasAnySales) return 'Selling Through'; // has sales but not strong enough for Active
  if (soh > 0) return 'Selling Through'; // old stock sitting there

  return 'Archived';
}

// ============================================================
// COLUMN PATTERNS FOR AUTO-DETECTION
// ============================================================
const COL_PATTERNS = {
  orderform: {
    code: ['product code','code','item code','model','item no','product no','item','sku'],
    description: ['description','product name','name','desc','item name'],
    series: ['series','range','category','collection'],
    qtyCtn: ['qty/ctn','qty per ctn','pcs/ctn','carton qty','ctn','inner','packing','qty/c'],
    price: ['unit price','unit','fob','cost']
  },
  soh: {
    code: ['stock code','item code','code','sku','product code','stock'],
    description: ['description','name','desc','item','product'],
    qty: ['soh','on hand','stock on hand','qty','available','balance','quantity','total']
  },
  stockturn: {
    code: ['stock code','item code','code','sku'],
    description: ['description','name','desc','item','product'],
    sold: ['sold','units sold','qty sold','quantity sold','sales','units','total sold','total qty']
  },
  po: {
    code: ['item code','code','stock code','sku','product code','item'],
    description: ['description','name','desc','item name','product'],
    qty: ['qty','quantity','ordered','order qty','units','on order','po qty','pcs','cartons','ctns','cases','qty ordered','qty shipped','shipped']
  },
  backorder: {
    code: ['stockcode','stock code','item code','code','sku','product code','stock','item'],
    description: ['description','name','desc','product'],
    qty: ['bkord_quant','bkord','back order','backorder','b/o','bo qty','bo','qty','quantity','outstanding','committed','on order','allocated']
  }
};

// ============================================================
// UI RENDERING
// ============================================================
function renderStepsNav() {
  const nav = document.getElementById('stepsNav');
  nav.innerHTML = STEPS.map((s, i) => `
    <div class="step-item ${i === STATE.currentStep ? 'active' : ''} ${s.optional ? 'optional' : ''}" onclick="goToStep(${i})" id="nav-step-${i}">
      <div class="step-num"><span>${i + 1}</span></div>
      <div class="step-label">${s.title}</div>
    </div>
  `).join('');
}

function renderNavButtons() {
  const nav = document.getElementById('navButtons');
  const isFirst = STATE.currentStep === 0;
  const isLast = STATE.currentStep === STEPS.length - 1;
  nav.innerHTML = `
    <div>${!isFirst ? `<button class="btn btn-secondary" onclick="goToStep(${STATE.currentStep - 1})">← Back</button>` : ''}</div>
    <div>${!isLast ? `<button class="btn btn-primary" onclick="goToStep(${STATE.currentStep + 1})">Next →</button>` : ''}</div>
  `;
}

function goToStep(n) {
  if (n < 0 || n >= STEPS.length) return;
  // Run processing when leaving certain steps
  if (STATE.currentStep !== n) {
    processCurrentStep();
  }
  // If going to review, build the review
  if (n === 6) buildReview();
  // If going to generate, update summary
  if (n === 7) updateGenSummary();

  STATE.currentStep = n;
  document.querySelectorAll('.step-content').forEach(el => el.classList.remove('active'));
  document.getElementById(`step-${n}`).classList.add('active');
  renderStepsNav();
  renderNavButtons();
  updateDataStatus();
  window.scrollTo(0, 0);
}

function markStepComplete(n) {
  const el = document.getElementById(`nav-step-${n}`);
  if (el) el.classList.add('completed');
  updateDataStatus();
}

function updateDataStatus() {
  const bar = document.getElementById('dataStatusBar');
  if (!bar) return;

  const checks = [
    { label: 'Schedule', ok: STATE.schedule.currentRun != null, count: STATE.schedule.rows.length + ' runs', optional: true },
    { label: 'Order Form', ok: STATE.orderForm.products.length > 0, count: STATE.orderForm.products.length + ' products', optional: false },
    { label: 'SOH', ok: STATE.soh.items.length > 0, count: STATE.soh.items.length + ' items', optional: false },
    { label: 'Monthly Sales', ok: STATE.monthlySales.items.length > 0, count: STATE.monthlySales.items.length + ' products (' + STATE.monthlySales.months.length + ' months)', optional: true },
    { label: 'POs', ok: STATE.pos.length > 0, count: STATE.pos.length + ' PO' + (STATE.pos.length !== 1 ? 's' : '') + (STATE.pos.length > 0 ? ' (' + STATE.pos.reduce((a, po) => a + po.items.length, 0) + ' lines)' : ''), optional: true },
    { label: 'Back Orders', ok: STATE.backOrders.items.length > 0, count: STATE.backOrders.items.length + ' lines', optional: true },
  ];

  let html = '<div class="data-status-title">Data Collection Status</div>';
  checks.forEach(c => {
    const cls = c.ok ? 'ok' : (c.optional ? 'empty' : 'warn');
    const detail = c.ok ? c.count : (c.optional ? 'skipped' : 'needed');
    html += `<div class="ds-chip ${cls}"><span class="ds-dot"></span>${c.label}${c.ok ? ': ' + c.count : c.optional ? '' : ' ⚠'}</div>`;
  });
  bar.innerHTML = html;
}

// ============================================================
// STEP PROCESSING
// ============================================================
function processCurrentStep() {
  const step = STATE.currentStep;
  try {
    if (step === 0) processSchedule();
    if (step === 1) processOrderForm();
    if (step === 2) processSOH();
    if (step === 3) processMonthlySales();
    if (step === 4) processPOs();
    if (step === 5) processBackOrders();
  } catch(e) { console.error('Step processing error:', e); }
}

function renderCoverageGraphic() {
  renderTimeline('coverageGraphic');
}

function updateSplyInfo() {
  const el = document.getElementById('splyInfo');
  if (!el) return;
  const s1 = STATE.results.section1 || [];
  const s2 = STATE.results.section2 || [];
  // Find a product with monthly data to compute SPLY month labels
  const ref = s1.find(p => p.monthlyArr && p.monthlyArr.length >= 13) || s2.find(p => p.monthlyArr && p.monthlyArr.length >= 13);
  if (!ref) { el.innerHTML = ''; return; }
  const MNAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const arr = ref.monthlyArr;
  // Use warehouse arrival month and day if available
  const startCM = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
    ? STATE.schedule.currentRun.arrivalDate.getMonth()
    : (arr[arr.length - 1].calMonth + 1) % 12;
  const startDay = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
    ? STATE.schedule.currentRun.arrivalDate.getDate()
    : 1;
  function buildLabel(coverMonths) {
    if (coverMonths <= 0) return 'N/A';
    const firstMonthDays = _dimDefault[startCM] || 30;
    const firstMonthFrac = Math.min(1, (firstMonthDays - startDay + 1) / firstMonthDays);
    const parts = [];
    // First (possibly partial) month
    if (firstMonthFrac > 0.99) {
      parts.push(MNAMES[startCM % 12]);
    } else {
      parts.push(MNAMES[startCM % 12] + ` (${Math.round(firstMonthFrac * 100)}%)`);
    }
    let remaining = coverMonths - firstMonthFrac;
    let i = 1;
    while (remaining >= 1 - 0.01) {
      parts.push(MNAMES[(startCM + i) % 12]);
      remaining -= 1;
      i++;
    }
    if (remaining > 0.01) {
      parts.push(MNAMES[(startCM + i) % 12] + ` (${Math.round(remaining * 100)}%)`);
    }
    return parts.join(', ');
  }
  const lowM = STATE.config.lowCoverage ?? 2;
  const highM = STATE.config.highCoverage ?? 4;
  el.innerHTML = `<div style="display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:12px;color:#555;background:#f5eef8;border-radius:6px;padding:10px 14px">
    <span style="font-weight:600;color:#7B2D8E;grid-column:1/-1;margin-bottom:2px">Same Sales Period Last Year</span>
    <span style="font-weight:600;color:var(--blue)">LOW</span><span>${buildLabel(lowM)}</span>
    <span style="font-weight:600;color:var(--blue)">HIGH</span><span>${buildLabel(highM)}</span>
    <span style="color:#999;grid-column:1/-1;font-style:italic;margin-top:2px">Partial months are pro-rated based on arrival day (${startDay} ${MNAMES[startCM]})</span>
  </div>`;
}

function filterS1Table(filter, btnEl) {
  // Update button styles
  document.querySelectorAll('.s1-filter-btn').forEach(b => { b.className = 'btn btn-sm btn-outline s1-filter-btn'; });
  if (btnEl) btnEl.className = 'btn btn-sm btn-primary s1-filter-btn';
  const s1 = STATE.results.section1 || [];
  const low = STATE.config.lowCoverage ?? 2;
  const lcColors = {New:'#6a1b9a',Core:'#1565c0',Active:'#00796b','Selling Through':'#e65100',Archived:'#c62828'};
  let items;
  const isUnmatched = filter === 'unmatched';
  if (isUnmatched) {
    items = s1.filter(p => (p.avg3||0) === 0 && (p.avg6||0) === 0 && (p.avg12||0) === 0);
  } else if (filter === 'all') {
    items = s1;
  } else {
    items = s1.slice(0, 15);
  }
  const wrap = document.getElementById('s1TableWrap');
  if (!wrap) return;

  function buildRow(p) {
    const proj = p.soh + p.incoming - p.backOrder;
    const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
    const cover = ci.coverMethod === 'none' ? 'N/A' : ci.cover.toFixed(1);
    const coverSuffix = ci.dataMonths > 0 && ci.dataMonths < 13 ? '*' : '';
    const flagColor = (p.flags||'').includes('GROWING') ? '#2e7d32' : (p.flags||'').includes('DECLIN') ? '#c62828' : '#e65100';
    const nc = normaliseCode(p.code);
    let lcCell;
    if (isUnmatched) {
      const opts = ['New','Core','Active','Selling Through','Archived'].map(lc =>
        `<option value="${lc}" ${p.lifecycle === lc ? 'selected' : ''} style="color:${lcColors[lc]}">${lc}</option>`
      ).join('');
      lcCell = `<td><select data-nc="${nc}" data-section="1" onchange="updateLifecycleFromS1(this)" style="font-size:11px;color:${lcColors[p.lifecycle]||'#333'};border:1px solid #ddd;border-radius:3px;padding:1px 4px;cursor:pointer">${opts}</select></td>`;
    } else {
      lcCell = `<td style="color:${lcColors[p.lifecycle]||'#333'};font-size:11px">${p.lifecycle}</td>`;
    }
    return `<tr><td>${p.code}</td><td>${p.description}</td>${lcCell}<td class="num">${(p.avg3||0).toFixed(1)}</td><td style="font-size:10px;color:${flagColor}">${p.flags || '—'}</td><td class="num">${p.soh}</td><td class="num">${p.incoming}</td><td class="num" style="color:${p.backOrder > 0 ? '#e65100' : ''}">${p.backOrder || ''}</td><td class="num" style="color:${cover !== 'N/A' && parseFloat(cover) < low ? 'var(--red)' : ''};font-weight:${cover !== 'N/A' && parseFloat(cover) < low ? '700' : ''}" title="${ci.coverMethod === 'seasonal' ? 'Seasonal depletion' : ci.dataMonths + ' months data'}">${cover}${coverSuffix}</td></tr>`;
  }

  const rows = items.map(buildRow).join('');
  const suffix = filter === 'lowest' && s1.length > 15 ? `<tr><td colspan="9" style="color:#999;text-align:center">...and ${s1.length - 15} more (click "All" to see everything)</td></tr>` : '';
  const bulkBar = isUnmatched && items.length > 0 ? `<div style="display:flex;gap:8px;align-items:center;margin-bottom:8px">
    <button class="btn btn-sm" style="background:#6a1b9a;color:#fff" onclick="markAllUnmatchedS1('New')">Mark All as New</button>
    <button class="btn btn-sm btn-outline" onclick="markAllUnmatchedS1('Archived')">Mark All as Archived</button>
    <span style="font-size:12px;color:#666;margin-left:8px">Or change individual items using the dropdown</span>
  </div>` : '';
  wrap.innerHTML = `${bulkBar}<table class="preview-table"><tr><th>Code</th><th>Description</th><th>Lifecycle</th><th>Avg/mo</th><th>Flags</th><th>SOH</th><th>Incoming</th><th>BO</th><th>Months Cover</th></tr>${rows}${suffix}</table>`;
}

function updateLifecycleFromS1(el) {
  const nc = el.dataset.nc;
  const val = el.value;
  const lcColors = {New:'#6a1b9a',Core:'#1565c0',Active:'#00796b','Selling Through':'#e65100',Archived:'#c62828'};
  el.style.color = lcColors[val] || '#333';
  if (!STATE.lifecycleOverrides) STATE.lifecycleOverrides = {};
  STATE.lifecycleOverrides[nc] = val;
  saveLifecycleOverrides();
  const list = STATE.results.section1 || [];
  const p = list.find(x => normaliseCode(x.code) === nc);
  if (p) p.lifecycle = val;
  updateLifecycleSummary();
}

function markAllUnmatchedS1(lifecycle) {
  const s1 = STATE.results.section1 || [];
  const unmatched = s1.filter(p => (p.avg3||0) === 0 && (p.avg6||0) === 0 && (p.avg12||0) === 0);
  if (!STATE.lifecycleOverrides) STATE.lifecycleOverrides = {};
  unmatched.forEach(p => {
    const nc = normaliseCode(p.code);
    p.lifecycle = lifecycle;
    STATE.lifecycleOverrides[nc] = lifecycle;
  });
  saveLifecycleOverrides();
  updateLifecycleSummary();
  // Re-render the unmatched view to reflect changes
  filterS1Table('unmatched', document.querySelector('.s1-filter-btn[data-filter="unmatched"]'));
}

function renderTimeline(targetId) {
  const container = document.getElementById(targetId || 'timeline-graphic');
  if (!container) return;
  const rows = STATE.schedule.rows.filter(r => r.exFactoryDate);
  if (rows.length < 2) { container.style.display = 'none'; return; }

  const currentRun = STATE.schedule.currentRun;
  const nextRun = STATE.schedule.nextRun;
  const runAfterNext = STATE.schedule.runAfterNext;

  // Gather all dates to find range (including coverage end dates)
  const allDates = [];
  rows.forEach(r => {
    if (r.dueDateParsed) allDates.push(r.dueDateParsed);
    if (r.exFactoryDate) allDates.push(r.exFactoryDate);
    if (r.arrivalDate) allDates.push(r.arrivalDate);
  });
  const today = new Date();
  allDates.push(today);
  // Include coverage end date so SVG is wide enough
  const highMonthsForRange = STATE.config.highCoverage ?? 4;
  if (currentRun && currentRun.arrivalDate) {
    const coverEnd = new Date(currentRun.arrivalDate);
    coverEnd.setDate(coverEnd.getDate() + Math.round(highMonthsForRange * 30.44));
    allDates.push(coverEnd);
  }

  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  // Pad range by 2 weeks each side
  minDate.setDate(minDate.getDate() - 14);
  maxDate.setDate(maxDate.getDate() + 14);

  const totalDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);
  const W = Math.max(900, Math.min(1600, totalDays * 4));
  const LEFT = 10;
  const RIGHT = W - 10;
  const TRACK_W = RIGHT - LEFT;

  const dayToX = d => LEFT + ((d - minDate) / (1000 * 60 * 60 * 24)) / totalDays * TRACK_W;

  const fmtShort = d => {
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${m[d.getMonth()]}`;
  };
  const fmtFull = d => {
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
  };

  // Layout: row height per run — sized to fit ~8 runs on a 13" laptop screen
  const ROW_H = 64;
  const TOP_PAD = 24;
  const MONTH_AXIS_H = 16;
  const H = TOP_PAD + rows.length * ROW_H + MONTH_AXIS_H;

  let svg = `<svg class="timeline-svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">`;

  // Month axis gridlines and labels (labels at bottom of chart)
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const axisStart = new Date(minDate.getFullYear(), minDate.getMonth() + 1, 1);
  const chartBottom = TOP_PAD + rows.length * ROW_H;
  for (let d = new Date(axisStart); d <= maxDate; d.setMonth(d.getMonth() + 1)) {
    const mx = dayToX(d);
    if (mx > LEFT + 20 && mx < RIGHT - 20) {
      svg += `<line x1="${mx}" y1="${TOP_PAD}" x2="${mx}" y2="${chartBottom}" stroke="#E0E0E0" stroke-width="0.5" stroke-dasharray="4,4"/>`;
      svg += `<text x="${mx}" y="${chartBottom + 12}" text-anchor="middle" style="font-family:Arial;font-size:9px;fill:#999">${monthNames[d.getMonth()]} ${d.getFullYear()}</text>`;
    }
  }

  // Coverage shading — driven by LOW/HIGH month inputs
  const lowMonths = STATE.config.lowCoverage ?? 2;
  const highMonths = STATE.config.highCoverage ?? 4;
  if (currentRun && currentRun.arrivalDate) {
    const coverStart = dayToX(currentRun.arrivalDate);
    // LOW coverage = currentRun warehouse date + lowMonths
    const lowEnd = new Date(currentRun.arrivalDate);
    lowEnd.setDate(lowEnd.getDate() + Math.round(lowMonths * 30.44));
    const coverLowEnd = dayToX(lowEnd);
    const lowW = Math.max(0, coverLowEnd - coverStart);
    svg += `<rect x="${coverStart}" y="${TOP_PAD}" width="${lowW}" height="${rows.length * ROW_H}" fill="#2196F3" opacity="0.10" rx="4"/>`;
    svg += `<rect x="${coverStart}" y="${TOP_PAD}" width="${lowW}" height="${rows.length * ROW_H}" fill="none" stroke="#2196F3" stroke-width="1.5" stroke-dasharray="4,3" rx="4"/>`;
    // LOW label — top of bar
    if (lowW > 30) svg += `<text x="${(coverStart + coverLowEnd) / 2}" y="${TOP_PAD - 8}" text-anchor="middle" style="font-family:Arial;font-size:10px;fill:#1565C0;font-weight:700">LOW ${lowMonths}mo</text>`;
    // HIGH coverage = currentRun warehouse date + highMonths
    const highEnd = new Date(currentRun.arrivalDate);
    highEnd.setDate(highEnd.getDate() + Math.round(highMonths * 30.44));
    const coverHighEnd = dayToX(highEnd);
    const highW = Math.max(0, coverHighEnd - coverStart);
    svg += `<rect x="${coverStart}" y="${TOP_PAD}" width="${highW}" height="${rows.length * ROW_H}" fill="#FF9800" opacity="0.06" rx="4"/>`;
    svg += `<rect x="${coverStart}" y="${TOP_PAD}" width="${highW}" height="${rows.length * ROW_H}" fill="none" stroke="#FF9800" stroke-width="1" stroke-dasharray="6,3" rx="4"/>`;
    // HIGH label — top of bar, offset right to avoid overlapping LOW label
    if (highW > 30) svg += `<text x="${coverHighEnd}" y="${TOP_PAD - 8}" text-anchor="end" style="font-family:Arial;font-size:10px;fill:#E65100;font-weight:700">HIGH ${highMonths}mo</text>`;
  }

  // Today line
  const todayX = dayToX(today);
  svg += `<line x1="${todayX}" y1="${TOP_PAD - 4}" x2="${todayX}" y2="${TOP_PAD + rows.length * ROW_H + 4}" stroke="#E91E63" stroke-width="1.5" stroke-dasharray="3,2"/>`;
  svg += `<text x="${todayX}" y="28" text-anchor="middle" style="font-family:Arial;font-size:9px;fill:#E91E63;font-weight:700">TODAY</text>`;

  // Each run as a row
  rows.forEach((r, i) => {
    const y = TOP_PAD + i * ROW_H;
    const isCurrentRun = (r === currentRun);

    // Row background for current run
    if (isCurrentRun) {
      svg += `<rect x="${LEFT}" y="${y}" width="${TRACK_W}" height="${ROW_H}" fill="#FFF9C4" rx="3"/>`;
    }

    // Horizontal track line
    const trackY = y + 32;
    svg += `<line x1="${LEFT}" y1="${trackY}" x2="${RIGHT}" y2="${trackY}" stroke="#E0E0E0" stroke-width="1"/>`;

    // Due date marker (diamond)
    if (r.dueDateParsed) {
      const dueX = dayToX(r.dueDateParsed);
      svg += `<polygon points="${dueX},${trackY - 6} ${dueX + 6},${trackY} ${dueX},${trackY + 6} ${dueX - 6},${trackY}" fill="#D84315" stroke="#fff" stroke-width="0.8"/>`;
      svg += `<text x="${dueX}" y="${trackY - 10}" text-anchor="middle" class="tl-date" style="fill:#D84315">${fmtShort(r.dueDateParsed)}</text>`;
    }

    if (r.exFactoryDate) {
      const exX = dayToX(r.exFactoryDate);
      const vesselDepDate = parseDate(r.vesselDeparture);
      const auPortDate = parseDate(r.auPortArrival);
      const crossDocksDate = parseDate(r.crossDocksReady);
      // End point = cross docks > AU port > calculated arrival
      const endDate = crossDocksDate || auPortDate || r.arrivalDate;
      const endX = endDate ? dayToX(endDate) : exX;

      // Transit bar (ex-factory → final milestone)
      if (endDate) {
        const barColor = isCurrentRun ? '#1565C0' : '#90A4AE';
        svg += `<rect x="${exX}" y="${trackY - 5}" width="${endX - exX}" height="10" fill="${barColor}" opacity="0.18" rx="5"/>`;
        svg += `<line x1="${exX}" y1="${trackY}" x2="${endX}" y2="${trackY}" stroke="${barColor}" stroke-width="3" stroke-linecap="round"/>`;
      }

      // BELOW the line: Factory (start) and Truck (end)
      svg += `<text x="${exX}" y="${trackY + 18}" text-anchor="middle" style="font-size:18px">🏭</text>`;
      svg += `<text x="${exX}" y="${trackY + 28}" text-anchor="middle" class="tl-date">${fmtShort(r.exFactoryDate)}</text>`;

      // ABOVE the line: Ship (vessel depart) and Anchor (AU port)
      if (vesselDepDate) {
        const vdX = dayToX(vesselDepDate);
        svg += `<text x="${vdX}" y="${trackY - 8}" text-anchor="middle" style="font-size:18px">🚢</text>`;
      }
      if (auPortDate) {
        const apX = dayToX(auPortDate);
        svg += `<text x="${apX}" y="${trackY - 8}" text-anchor="middle" style="font-size:18px">⚓</text>`;
      }

      // Cross Docks Ready marker (truck emoji) — below line
      if (crossDocksDate) {
        const cdX = dayToX(crossDocksDate);
        svg += `<text x="${cdX}" y="${trackY + 18}" text-anchor="middle" style="font-size:18px">🚚</text>`;
        svg += `<text x="${cdX}" y="${trackY + 28}" text-anchor="middle" class="tl-date" style="font-weight:600">${fmtShort(crossDocksDate)}</text>`;
      } else if (r.arrivalDate) {
        const arrX = dayToX(r.arrivalDate);
        svg += `<text x="${arrX}" y="${trackY + 18}" text-anchor="middle" style="font-size:18px">🚚</text>`;
        svg += `<text x="${arrX}" y="${trackY + 28}" text-anchor="middle" class="tl-date" style="font-weight:600">${fmtShort(r.arrivalDate)}</text>`;
      }
    }

    // Status label (right side)
    const statusColor = r.statusLower === 'delivered' ? '#4CAF50' : r.statusLower.includes('on water') ? '#0288D1' : (r.statusLower.includes('ordered') || r.statusLower === 'order placed') ? '#2E7D32' : (r.statusLower.includes('launch') || r.statusLower === 'current order') ? '#1565C0' : '#999';
    svg += `<text x="${RIGHT - 4}" y="${y + 16}" text-anchor="end" style="font-family:Arial;font-size:10px;fill:${statusColor}">${r.status}</text>`;
  });

  // Legend is rendered in HTML outside SVG

  svg += '</svg>';

  // Build fixed left labels column
  const LABEL_W = 80;
  let labelsHtml = `<div class="tl-labels" style="width:${LABEL_W}px;flex-shrink:0;padding-top:${TOP_PAD}px">`;
  rows.forEach((r, i) => {
    const isCurrentRun = (r === currentRun);
    const bg = isCurrentRun ? 'background:#FFF9C4;' : '';
    const color = isCurrentRun ? 'color:#1565C0;font-weight:700;' : 'color:#333;';
    labelsHtml += `<div style="height:${ROW_H}px;display:flex;align-items:flex-start;padding-top:8px;font-family:Arial;font-size:11px;${color}${bg}padding-left:4px;box-sizing:border-box">${r.run}${isCurrentRun ? ' ★' : ''}</div>`;
  });
  labelsHtml += '</div>';

  container.innerHTML = `
    <div style="font-family:Arial;font-size:12px;font-weight:700;text-align:center;margin-bottom:4px">Order Timeline — Ex Factory → Cross Docks Ready</div>
    <div style="display:flex;align-items:flex-start">
      ${labelsHtml}
      <div class="tl-scroll" style="overflow-x:auto;flex:1">${svg}</div>
    </div>
    <div class="tl-legend-bar" style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;padding:8px 0 0 ${LABEL_W}px;font-family:Arial;font-size:10px;color:#666">
      <span><svg width="10" height="10"><polygon points="5,0 10,5 5,10 0,5" fill="#D84315"/></svg> Order Due</span>
      <span>🏭 Ex Factory</span>
      <span>🚢 Vessel Depart</span>
      <span>⚓ AU Port</span>
      <span>🚚 Cross Docks</span>
      <span><svg width="16" height="14"><rect x="0" y="1" width="14" height="12" fill="#2196F3" opacity="0.15" rx="2" stroke="#2196F3" stroke-width="1" stroke-dasharray="3,2"/></svg> LOW</span>
      <span><svg width="16" height="14"><rect x="0" y="1" width="14" height="12" fill="#FF9800" opacity="0.1" rx="2" stroke="#FF9800" stroke-width="1" stroke-dasharray="3,2"/></svg> HIGH</span>
      <span><svg width="8" height="12"><line x1="4" y1="0" x2="4" y2="12" stroke="#E91E63" stroke-width="1.5" stroke-dasharray="2,1"/></svg> Today</span>
    </div>`;
  container.style.display = 'block';
}

function processSchedule() {
  const text = document.getElementById('paste-schedule').value;
  if (!text.trim()) return;
  const rows = parseTSV(text);
  if (rows.length < 2) return;

  // Find header row — must have at least 3 known column names to avoid matching title rows
  const hIdx = rows.findIndex(r => {
    const joined = r.map(c => (c || '').toLowerCase()).join('|');
    let hits = 0;
    if (joined.includes('order run') || r.some(c => (c||'').toLowerCase().trim() === 'run')) hits++;
    if (joined.includes('ex factory') || joined.includes('exf')) hits++;
    if (joined.includes('due')) hits++;
    if (joined.includes('status')) hits++;
    if (joined.includes('lead time')) hits++;
    if (joined.includes('launch')) hits++;
    return hits >= 3;
  });
  const headerIdx = hIdx >= 0 ? hIdx : 0;
  const headers = rows[headerIdx] || [];
  const dataRows = rows.slice(headerIdx + 1).filter(r => r.some(c => c.trim()));

  // Detect column indices from header names (robust to column changes)
  // Order matters: check cross docks BEFORE AU port because the cross docks header
  // can contain "Arrival AU Port" in its description text
  let colRun = 0, colLaunch = 1, colDue = 2, colExF = 3;
  let colLeadTime = -1, colNewItems = -1, colStatus = -1;
  let colVessel = -1, colAUPort = -1, colCrossDocks = -1;
  for (let i = 0; i < headers.length; i++) {
    const h = (headers[i] || '').toLowerCase().trim();
    if (h.includes('cross dock') || h.startsWith('cross dock'))            colCrossDocks = i;
    else if (h.includes('lead time'))                                       colLeadTime = i;
    else if (h.includes('new item'))                                        colNewItems = i;
    else if (h.includes('vessel') && h.includes('depart'))                  colVessel = i;
    else if ((h.includes('est') && h.includes('arrival')) || (h.startsWith('est.') && h.includes('port')))  colAUPort = i;
    else if (h === 'status')                                                colStatus = i;
    else if (h.includes('launch'))                                          colLaunch = i;
    else if (h.includes('due'))                                             colDue = i;
    else if (h.includes('ex factory') || h.includes('exf'))                 colExF = i;
    else if (h.includes('order run') || h === 'run')                        colRun = i;
  }
  // Fallbacks
  if (colLeadTime < 0) colLeadTime = Math.max(4, headers.length - 3);
  if (colNewItems < 0) colNewItems = Math.max(5, headers.length - 2);
  if (colStatus < 0) colStatus = headers.length - 1; // last column
  STATE.schedule.rows = [];
  const today = new Date();

  for (const r of dataRows) {
    const run = r[colRun] || '';
    const runMatch = run.match(/(\d+\.?\d*)/);
    if (!runMatch) continue;
    const runNum = parseFloat(runMatch[1]);
    // Read status from detected column, fallback to last non-empty cell
    let statusRaw = (r[colStatus] || '').trim();
    if (!statusRaw) {
      for (let si = r.length - 1; si >= 0; si--) {
        const v = (r[si] || '').trim().toLowerCase();
        if (v === 'ordered' || v === 'current order' || v === 'upcoming' || v === 'on water' || v === 'delivered') {
          statusRaw = r[si].trim(); break;
        }
      }
    }
    const status = statusRaw.toLowerCase();

    // Parse due date
    const dueDateParsed = parseDate(r[colDue]);

    // Calculate arrival date = Ex Factory + 6 weeks (42 days)
    const TRANSIT_WEEKS = 6;
    const exFactoryDate = parseDate(r[colExF]);
    let arrivalDate = null;
    let arrivalStr = '';
    if (exFactoryDate && exFactoryDate > new Date(2025, 0, 1)) {
      arrivalDate = new Date(exFactoryDate);
      arrivalDate.setDate(arrivalDate.getDate() + TRANSIT_WEEKS * 7);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      arrivalStr = `${arrivalDate.getDate()} ${months[arrivalDate.getMonth()]} ${arrivalDate.getFullYear()}`;
    }

    // Parse vessel departure & AU port arrival dates for tracking
    const vesselDeparture = colVessel >= 0 ? (r[colVessel] || '') : '';
    const auPortArrival = colAUPort >= 0 ? (r[colAUPort] || '') : '';
    const crossDocksReady = colCrossDocks >= 0 ? (r[colCrossDocks] || '') : '';

    // Calculate lead time: Due Date → Cross Docks Ready (or AU Port Arrival, or EXF+6wk)
    let leadTimeStr = r[colLeadTime] || '';
    if (!leadTimeStr && dueDateParsed) {
      const endDate = parseDate(crossDocksReady) || parseDate(auPortArrival) || arrivalDate;
      if (endDate) {
        const diffMs = endDate - dueDateParsed;
        const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
        leadTimeStr = diffMonths > 0 ? diffMonths.toFixed(1) + ' months' : '';
      }
    }

    STATE.schedule.rows.push({
      run: run.trim(),
      runNum,
      launchDate: r[colLaunch] || '',
      dueDateParsed,
      dueDate: r[colDue] || '',
      exFactory: r[colExF] || '',
      exFactoryDate,
      arrivalDate,
      arrivalStr,
      vesselDeparture,
      auPortArrival,
      crossDocksReady,
      leadTime: leadTimeStr,
      newItems: r[colNewItems] || '',
      status: statusRaw || '',
      statusLower: status
    });
  }

  // Find current run (order sheet launched, or latest non-ordered)
  const launched = STATE.schedule.rows.filter(r =>
    r.statusLower.includes('launch') || r.statusLower.includes('order sheet') || r.statusLower === 'current order');
  const currentRun = launched.length > 0 ? launched[launched.length - 1] : null;

  if (currentRun) {
    STATE.schedule.currentRun = currentRun;
    STATE.config.runNumber = currentRun.runNum;

    // Find future runs after current (with arrival dates)
    const currentIdx = STATE.schedule.rows.indexOf(currentRun);
    const futureRuns = STATE.schedule.rows.slice(currentIdx + 1)
      .filter(r => r.arrivalDate);
    STATE.schedule.nextRun = futureRuns.length > 0 ? futureRuns[0] : null;
    STATE.schedule.runAfterNext = futureRuns.length > 1 ? futureRuns[1] : null;

    // Coverage logic: you're ordering Run X now, so you need stock to last
    // from when Run X arrives until subsequent runs arrive.
    // LOW = months from Run X arrival → next run arrival (minimum restock gap)
    // HIGH = months from Run X arrival → run after next arrival (safety buffer)
    if (!STATE.config.coverageManuallySet) {
      if (currentRun.arrivalDate && STATE.schedule.nextRun && STATE.schedule.nextRun.arrivalDate) {
        const lowMonths = monthsBetween(currentRun.arrivalDate, STATE.schedule.nextRun.arrivalDate);
        if (lowMonths && lowMonths > 0) {
          STATE.config.lowCoverage = Math.round(lowMonths * 2) / 2; // round to 0.5
        }
      }
      if (currentRun.arrivalDate && STATE.schedule.runAfterNext && STATE.schedule.runAfterNext.arrivalDate) {
        const highMonths = monthsBetween(currentRun.arrivalDate, STATE.schedule.runAfterNext.arrivalDate);
        if (highMonths && highMonths > 0) {
          STATE.config.highCoverage = Math.round(highMonths * 2) / 2;
        }
      }
    }
    document.getElementById('lowCoverage').value = STATE.config.lowCoverage;
    document.getElementById('highCoverage').value = STATE.config.highCoverage;

    document.getElementById('coverageExplain').textContent =
      `Auto-calculated: LOW = months from ${currentRun.run} in warehouse (${currentRun.arrivalStr}) to ` +
      `${STATE.schedule.nextRun ? STATE.schedule.nextRun.run + ' in warehouse (' + STATE.schedule.nextRun.arrivalStr + ')' : 'next run'}, ` +
      `HIGH = months from ${currentRun.run} in warehouse to ` +
      `${STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run + ' in warehouse (' + STATE.schedule.runAfterNext.arrivalStr + ')' : 'run after next'}. ` +
      `(Transit = 6 weeks from ex-factory.) You can override these values.`;
  }

  // Save to localStorage
  saveScheduleToStorage();

  // Switch to editable view
  document.getElementById('paste-schedule-card').style.display = 'none';
  document.getElementById('saved-schedule-card').style.display = '';
  renderScheduleEditor();
  recalcCoverage();
  markStepComplete(0);
}

function processOrderForm() {
  const text = document.getElementById('paste-orderform').value;
  if (!text.trim()) return;
  const rows = parseTSV(text);
  if (rows.length < 2) return;

  const hIdx = findHeaderRow(rows, COL_PATTERNS.orderform);
  const cols = mapColumns(rows[hIdx], COL_PATTERNS.orderform);
  STATE.orderForm.columns = cols;
  STATE.orderForm.raw = rows;

  const products = [];
  for (let i = hIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const code = cols.code >= 0 ? (r[cols.code] || '').trim() : '';
    if (!code) continue;
    products.push({
      code,
      normCode: normaliseCode(code),
      description: cols.description >= 0 ? (r[cols.description] || '').trim() : '',
      series: cols.series >= 0 ? (r[cols.series] || '').trim() : '',
      qtyCtn: cols.qtyCtn >= 0 ? parseNum(r[cols.qtyCtn]) : 0,
      price: cols.price >= 0 ? parseNum(r[cols.price]) : 0,
    });
  }
  STATE.orderForm.products = products;

  renderPreview('preview-orderform', products.slice(0, 8),
    ['Code', 'Description', 'Series', 'Qty/Ctn', 'Price'],
    p => [p.code, p.description, p.series, p.qtyCtn, '$' + p.price.toFixed(2)],
    products.length + ' products found');
  document.getElementById('paste-orderform').classList.add('has-data');
  showColMapping('colmap-orderform', rows[hIdx], cols, COL_PATTERNS.orderform);
  markStepComplete(1);
}

function processSOH() {
  const text = document.getElementById('paste-soh').value;
  if (!text.trim()) return;
  const rows = parseTSV(text);
  if (rows.length < 2) return;

  const hIdx = findHeaderRow(rows, COL_PATTERNS.soh);
  const cols = mapColumns(rows[hIdx], COL_PATTERNS.soh);
  STATE.soh.columns = cols;
  STATE.soh.raw = rows;

  const items = [];
  for (let i = hIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const code = cols.code >= 0 ? (r[cols.code] || '').trim() : '';
    const desc = cols.description >= 0 ? (r[cols.description] || '').trim() : '';
    const qty = cols.qty >= 0 ? parseNum(r[cols.qty]) : 0;
    if (!code && !desc) continue;
    items.push({ code, normCode: normaliseCode(code), description: desc, normDesc: normaliseDesc(desc), qty });
  }
  STATE.soh.items = items;

  renderPreview('preview-soh', items.slice(0, 8),
    ['Stock Code', 'Description', 'SOH'],
    p => [p.code, p.description, p.qty],
    items.length + ' items found');
  document.getElementById('paste-soh').classList.add('has-data');
  showColMapping('colmap-soh', rows[hIdx], cols, COL_PATTERNS.soh);
  markStepComplete(2);
}

function processMonthlySales() {
  const text = document.getElementById('paste-monthly').value;
  if (!text.trim()) return;
  const rows = parseTSV(text);
  if (rows.length < 2) return;

  // Find header row — look for row containing month names (Jan, Feb, etc.)
  const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','sept','oct','nov','dec'];
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 5); i++) {
    const row = rows[i];
    const monthHits = row.filter(c => {
      const cl = (c || '').toLowerCase();
      return monthNames.some(m => cl.startsWith(m)) && /\d{4}/.test(c);
    }).length;
    if (monthHits >= 3) { headerIdx = i; break; }
  }
  if (headerIdx < 0) {
    // Fallback: try first row with "stockcode" or "code"
    headerIdx = rows.findIndex(r => r.some(c => (c||'').toLowerCase().includes('stockcode') || (c||'').toLowerCase().includes('code')));
    if (headerIdx < 0) headerIdx = 0;
  }

  const header = rows[headerIdx];
  // Identify code col (0), desc col (1), then month columns, and skip TOTAL col
  let codeCol = 0, descCol = 1;
  for (let j = 0; j < header.length; j++) {
    const h = (header[j] || '').toLowerCase();
    if (h.includes('stockcode') || h.includes('stock code') || h === 'code') codeCol = j;
    if (h.includes('description') || h.includes('details') || h === 'desc') descCol = j;
  }

  // Parse month columns: find columns with "Mon YYYY" pattern
  const monthCols = [];
  for (let j = 0; j < header.length; j++) {
    const h = (header[j] || '').trim();
    const hl = h.toLowerCase();
    if (hl === 'total' || hl === 'stockcode' || hl === 'description' || hl === 'details' || hl === 'code') continue;
    // Match patterns like "Jan 2024", "Sept 2025", "February 2026"
    const match = h.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\w*\s+(\d{4})$/i);
    if (match) {
      const monStr = match[1].toLowerCase().substring(0, 3);
      const monIdx = monthNames.indexOf(monStr) >= 0 ? monthNames.indexOf(monStr) : monthNames.indexOf(monStr.replace('sept','sep'));
      // Handle sept→sep mapping
      const realMonIdx = monStr === 'sep' ? 8 : monthNames.slice(0,12).indexOf(monStr);
      const year = parseInt(match[2]);
      const monthDate = new Date(year, realMonIdx >= 0 ? realMonIdx : 0, 1);
      monthCols.push({ col: j, label: h, date: monthDate, year, monIdx: realMonIdx });
    }
  }

  // Sort month columns chronologically
  monthCols.sort((a, b) => a.date - b.date);

  if (monthCols.length === 0) {
    document.getElementById('monthly-summary').style.display = 'block';
    document.getElementById('monthly-summary').innerHTML = '<div class="alert alert-error">Could not detect month columns. Expected headers like "Jan 2024", "Feb 2025", etc.</div>';
    return;
  }

  // Parse product rows
  const items = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const code = (r[codeCol] || '').trim();
    const desc = (r[descCol] || '').trim();
    if (!code && !desc) continue;
    if (code.toLowerCase() === 'total') continue; // skip totals row

    const monthQtys = {};
    let totalQty = 0;
    monthCols.forEach(mc => {
      const qty = parseNum(r[mc.col]);
      monthQtys[mc.label] = qty;
      totalQty += qty;
    });

    // Build chronological monthly array for this product
    const monthlyArr = monthCols.map(mc => ({
      calMonth: mc.monIdx, // 0-11
      year: mc.year,
      label: mc.label,
      qty: parseNum(r[mc.col])
    }));

    items.push({
      code, normCode: normaliseCode(code), description: desc, normDesc: normaliseDesc(desc),
      monthQtys, totalQty, monthlyArr
    });
  }

  // Calculate period aggregates: determine which months fall in 3mo/6mo/12mo/prior windows
  // Use the most recent month in the data as "now"
  const latestMonth = monthCols[monthCols.length - 1].date;
  const numMonths = monthCols.length;

  // Assign each month to a period based on recency (most recent = index 0)
  const reversedMonths = [...monthCols].reverse(); // most recent first
  const last3Cols = reversedMonths.slice(0, 3);
  const last6Cols = reversedMonths.slice(0, 6);
  const last12Cols = reversedMonths.slice(0, 12);
  const priorCols = reversedMonths.slice(12, 24); // months 13-24

  // Build aggregated sales items for each period
  function aggregateForPeriod(periodCols, annualiseFactor) {
    return items.map(item => {
      const rawQty = periodCols.reduce((sum, mc) => sum + (item.monthQtys[mc.label] || 0), 0);
      const annualised = Math.round(rawQty * annualiseFactor);
      return {
        code: item.code, normCode: item.normCode,
        description: item.description, normDesc: item.normDesc,
        sold: annualised
      };
    }).filter(item => item.sold > 0);
  }

  const n3 = last3Cols.length;
  const n6 = last6Cols.length;
  const n12 = last12Cols.length;
  const nPrior = priorCols.length;

  STATE.monthlySales.raw = rows;
  STATE.monthlySales.months = monthCols.map(m => m.label);
  STATE.monthlySales.items = items;
  // Annualise: multiply by (12/monthsInPeriod) to get annual rate
  STATE.monthlySales.s12Items = n12 > 0 ? aggregateForPeriod(last12Cols, 12 / n12) : [];
  STATE.monthlySales.s6Items = n6 > 0 ? aggregateForPeriod(last6Cols, 12 / n6) : [];
  STATE.monthlySales.s3Items = n3 > 0 ? aggregateForPeriod(last3Cols, 12 / n3) : [];
  STATE.monthlySales.sPriorItems = nPrior > 0 ? aggregateForPeriod(priorCols, 12 / nPrior) : [];

  // Show summary
  const summaryDiv = document.getElementById('monthly-summary');
  summaryDiv.style.display = 'block';
  const periodInfo = [
    `Avg/mo (3mo): ${last3Cols.map(m=>m.label).join(', ')} — total ÷ ${n3}`,
    `Avg/mo (6mo): ${last6Cols.map(m=>m.label).join(', ')} — total ÷ ${n6}`,
    `Avg/mo (12mo): ${last12Cols.map(m=>m.label).join(', ')} — total ÷ ${n12}`,
    nPrior > 0 ? `Same Period LY: calculated per coverage window (LOW and HIGH separately) with partial months pro-rated based on warehouse arrival day` : 'Same Period LY: no data (need 13+ months)',
  ];
  summaryDiv.innerHTML = `
    <div class="alert alert-success">
      <strong>Detected ${monthCols.length} months</strong> (${monthCols[0].label} → ${monthCols[monthCols.length-1].label}) with ${items.length} products.<br>
      <div style="font-size:11px;margin-top:6px;color:#555">
        ${periodInfo.map(p => `• ${p}`).join('<br>')}
      </div>
    </div>`;

  // Preview
  renderPreview('preview-monthly', items.slice(0, 8),
    ['Code', 'Description', 'Avg/mo (3mo)', 'Avg/mo (6mo)', 'Avg/mo (12mo)'],
    p => {
      const find = (list) => { const f = list.find(x => x.normCode === p.normCode); return f ? Math.round(f.sold / 12) : 0; };
      return [p.code || '(blank)', p.description,
        find(STATE.monthlySales.s3Items), find(STATE.monthlySales.s6Items),
        find(STATE.monthlySales.s12Items)];
    },
    items.length + ' products parsed');

  document.getElementById('paste-monthly').classList.add('has-data');
  markStepComplete(3);
}

function processPOs() {
  STATE.pos = [];
  document.querySelectorAll('.po-section').forEach((section, idx) => {
    const text = section.querySelector('.po-paste').value;
    const preview = section.querySelector('.po-preview');
    if (!text.trim()) { preview.innerHTML = ''; preview.classList.remove('visible'); return; }
    const rows = parseTSV(text);
    if (rows.length < 2) { preview.innerHTML = '<div style="color:var(--red);font-size:12px;padding:8px">Need at least 2 rows (header + data)</div>'; return; }

    const hIdx = findHeaderRow(rows, COL_PATTERNS.po);
    const cols = mapColumns(rows[hIdx], COL_PATTERNS.po);
    const poName = (section.querySelector('.po-name').value || '').trim() || `PO ${idx + 1}`;

    // Fallback for 2-column PO data (code + qty)
    if (rows[hIdx].length === 2) {
      if (cols.code < 0 && cols.qty < 0) { cols.code = 0; cols.qty = 1; }
      else if (cols.code >= 0 && cols.qty < 0) { cols.qty = cols.code === 0 ? 1 : 0; }
      else if (cols.code < 0 && cols.qty >= 0) { cols.code = cols.qty === 0 ? 1 : 0; }
    }

    // Broader fallback: if code not found, try scanning for a column with NB/NBC/NBH-like codes
    if (cols.code < 0) {
      for (let c = 0; c < rows[hIdx].length; c++) {
        // Check a few data rows for NB-style codes
        let nbCount = 0;
        for (let r = hIdx + 1; r < Math.min(rows.length, hIdx + 6); r++) {
          if (rows[r][c] && /^NB/i.test((rows[r][c]||'').trim())) nbCount++;
        }
        if (nbCount >= 2) { cols.code = c; break; }
      }
    }
    // Fallback for qty: find best numeric column (prefer small integers = quantities, not barcodes/values)
    if (cols.qty < 0) {
      let bestCol = -1, bestScore = -1;
      const sampleEnd = Math.min(rows.length, hIdx + 25); // sample up to 24 data rows
      for (let c = 0; c < rows[hIdx].length; c++) {
        if (c === cols.code || c === cols.description) continue;
        let numCount = 0, intCount = 0, maxVal = 0, sumVal = 0;
        for (let r = hIdx + 1; r < sampleEnd; r++) {
          const v = parseNum(rows[r][c]);
          if (v > 0) { numCount++; sumVal += v; if (v > maxVal) maxVal = v; if (Number.isInteger(v) && v === Math.round(v)) intCount++; }
        }
        // Hard exclude: if ANY value > 1,000,000 this is likely a barcode/ID, not qty
        if (maxVal > 1000000) continue;
        if (numCount < 1) continue;
        const avgVal = numCount > 0 ? sumVal / numCount : 0;
        // Score: prefer columns with smaller avg values (qty) over larger (price/total)
        const intRatio = numCount > 0 ? intCount / numCount : 0;
        const sizeScore = avgVal < 100 ? 4 : avgVal < 1000 ? 3 : avgVal < 10000 ? 2 : avgVal < 100000 ? 1 : 0;
        const countScore = Math.min(numCount / 3, 2); // bonus for having more positive values
        const score = intRatio * 2 + sizeScore + countScore;
        if (score > bestScore) { bestScore = score; bestCol = c; }
      }
      if (bestCol >= 0) cols.qty = bestCol;
    }

    const items = [];
    for (let i = hIdx + 1; i < rows.length; i++) {
      const r = rows[i];
      let code = '';
      if (cols.code >= 0) code = (r[cols.code] || '').trim();
      // Fallback: check column 1 if code column gave empty
      if (!code && r[1]) code = r[1].trim();
      const desc = cols.description >= 0 ? (r[cols.description] || '').trim() : '';
      let qty = cols.qty >= 0 ? parseNum(r[cols.qty]) : 0;
      // Sanity: if qty > 100,000 it's likely a barcode/ID, not a quantity — try other columns
      if (qty > 100000) {
        qty = 0;
        // Try to find a reasonable qty in other columns
        for (let c = rows[hIdx].length - 1; c >= 0; c--) {
          if (c === cols.code || c === cols.description || c === cols.qty) continue;
          const v = parseNum(r[c]);
          if (v > 0 && v <= 100000) { qty = v; break; }
        }
      }
      // Fallback: try column 4
      if (qty === 0 && r[4] && cols.qty !== 4) { const v4 = parseNum(r[4]); if (v4 > 0 && v4 <= 100000) qty = v4; }
      if (!code || qty <= 0) continue;
      items.push({ code, normCode: normaliseCode(code), description: desc, qty });
    }
    if (items.length > 0) STATE.pos.push({ name: poName, items });

    // Show column mapping
    const colmap = section.querySelector('.po-colmap');
    if (colmap && rows[hIdx]) {
      colmap.style.display = 'flex';
      const labels = { code: 'Code', description: 'Description', qty: 'Quantity' };
      colmap.innerHTML = Object.entries(cols).map(([key, colIdx]) => {
        const options = rows[hIdx].map((h, i) => `<option value="${i}" ${i === colIdx ? 'selected' : ''}>${h || '(col ' + (i+1) + ')'}</option>`).join('');
        const color = colIdx >= 0 ? 'var(--green)' : 'var(--red)';
        return `<div class="col-map-item"><label style="color:${color}">${labels[key] || key}:</label><select style="font-size:12px;padding:2px">${options}</select></div>`;
      }).join('');
    }

    // Show preview with data table (or error)
    if (items.length > 0) {
      preview.classList.add('visible');
      const totalQty = items.reduce((s, it) => s + it.qty, 0);
      const colInfo = `Code→col${cols.code >= 0 ? cols.code+1 : '?'} (${rows[hIdx][cols.code]||'?'}), Qty→col${cols.qty >= 0 ? cols.qty+1 : '?'} (${rows[hIdx][cols.qty]||'?'})`;
      let html = `<div class="preview-header"><h4>${poName} — Data Preview</h4><span class="count">${items.length} line items, ${totalQty.toLocaleString()} total units</span></div>`;
      html += `<div style="font-size:10px;color:var(--gray);margin:-4px 0 6px;padding:0 4px">Columns: ${colInfo}</div>`;
      html += `<div class="preview-table-wrap"><table class="preview-table"><tr><th>Code</th><th>Description</th><th>Qty</th></tr>`;
      items.slice(0, 8).forEach(it => {
        html += `<tr><td>${it.code}</td><td>${it.description}</td><td>${it.qty.toLocaleString()}</td></tr>`;
      });
      if (items.length > 8) html += `<tr><td colspan="3" style="text-align:center;color:var(--gray);font-style:italic">... and ${items.length - 8} more items</td></tr>`;
      html += '</table></div>';
      preview.innerHTML = html;
      section.querySelector('.po-paste').classList.add('has-data');
    } else {
      preview.classList.add('visible');
      preview.innerHTML = `<div style="color:var(--red);font-size:12px;padding:8px">⚠ Could not parse PO data. Found columns: code=${cols.code}, qty=${cols.qty}. Headers: [${rows[hIdx].join(', ')}]. Try checking the column mapping above.</div>`;
      section.querySelector('.po-paste').classList.add('error');
    }
  });
  if (STATE.pos.length > 0) markStepComplete(4);
}

function addPOSection() {
  const container = document.getElementById('po-container');
  const n = container.children.length;
  const div = document.createElement('div');
  div.className = 'po-section card';
  div.dataset.po = n;
  div.innerHTML = `
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">
      <h4 style="margin:0">Purchase Order ${n + 1}</h4>
      <label style="font-size:12px;color:var(--gray)">PO / Run #:</label>
      <input type="text" class="po-name" placeholder="e.g. Run ${(STATE.config.runNumber || 86) - 2 + n}" style="padding:4px 10px;border:1px solid var(--border);border-radius:4px;font-size:13px;width:140px">
    </div>
    <textarea class="paste-area po-paste" placeholder="Paste PO data here..."></textarea>
    <div class="col-mapping po-colmap" style="display:none"></div>
    <div class="preview po-preview"></div>`;
  container.appendChild(div);
}

function processBackOrders() {
  const text = document.getElementById('paste-backorders').value;
  if (!text.trim()) {
    // Clear state when textarea is emptied
    STATE.backOrders = { raw: [], columns: {}, items: [] };
    document.getElementById('paste-backorders').classList.remove('has-data');
    var boPreview = document.getElementById('preview-backorders');
    if (boPreview) { boPreview.innerHTML = ''; boPreview.classList.remove('visible'); }
    return;
  }
  const rows = parseTSV(text);
  if (rows.length < 2) return;

  const hIdx = findHeaderRow(rows, COL_PATTERNS.backorder);
  const cols = mapColumns(rows[hIdx], COL_PATTERNS.backorder);

  // Fallback for simple 2-column data (code + qty): if only 2 columns and
  // auto-detect missed, assume col 0 = code, col 1 = qty
  if (rows[hIdx].length === 2 && cols.code < 0 && cols.qty < 0) {
    cols.code = 0; cols.qty = 1;
  } else if (rows[hIdx].length === 2 && cols.code >= 0 && cols.qty < 0) {
    cols.qty = cols.code === 0 ? 1 : 0;
  } else if (rows[hIdx].length === 2 && cols.code < 0 && cols.qty >= 0) {
    cols.code = cols.qty === 0 ? 1 : 0;
  }

  STATE.backOrders.columns = cols;
  STATE.backOrders.raw = rows;

  const items = [];
  for (let i = hIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const code = cols.code >= 0 ? (r[cols.code] || '').trim() : '';
    const desc = cols.description >= 0 ? (r[cols.description] || '').trim() : '';
    const qty = cols.qty >= 0 ? parseNum(r[cols.qty]) : 0;
    if ((!code && !desc) || qty <= 0) continue;
    items.push({ code, normCode: normaliseCode(code), description: desc, normDesc: normaliseDesc(desc), qty });
  }
  STATE.backOrders.items = items;

  renderPreview('preview-backorders', items.slice(0, 8),
    ['Stock Code', 'Back Order Qty'],
    p => [p.code, p.qty],
    items.length + ' back order lines found (' + items.reduce((a,b) => a + b.qty, 0) + ' total units)');
  document.getElementById('paste-backorders').classList.add('has-data');
  showColMapping('colmap-backorders', rows[hIdx], cols, COL_PATTERNS.backorder);
  markStepComplete(5);
}

// ============================================================
// PREVIEW & COLUMN MAPPING HELPERS
// ============================================================
function renderPreview(containerId, items, headers, rowFn, countText) {
  const container = document.getElementById(containerId);
  container.classList.add('visible');
  container.innerHTML = `
    <div class="preview-header"><h4>Data preview</h4><span class="count">${countText}</span></div>
    <div class="preview-table-wrap"><table class="preview-table">
      <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
      ${items.map(p => `<tr>${rowFn(p).map((v,i) => `<td${typeof v === 'number' ? ' class="num"' : ''}>${v}</td>`).join('')}</tr>`).join('')}
      ${items.length < 8 ? '' : '<tr><td colspan="' + headers.length + '" style="color:#999;text-align:center">...and more</td></tr>'}
    </table></div>`;
}

function showColMapping(containerId, headerRow, mapping, patterns) {
  const container = document.getElementById(containerId);
  container.style.display = 'flex';
  const labels = { code: 'Code', description: 'Description', series: 'Series', qtyCtn: 'Qty/Ctn', price: 'Price', qty: 'Quantity', sold: 'Sold' };
  container.innerHTML = Object.entries(mapping).map(([key, colIdx]) => {
    const options = headerRow.map((h, i) => `<option value="${i}" ${i === colIdx ? 'selected' : ''}>${h || '(col ' + (i+1) + ')'}</option>`).join('');
    const color = colIdx >= 0 ? 'var(--green)' : 'var(--red)';
    return `<div class="col-map-item">
      <label style="color:${color}">${labels[key] || key}:</label>
      <select onchange="updateColMapping('${containerId}','${key}',this.value)">${colIdx < 0 ? '<option value="-1">Not found</option>' : ''}${options}</select>
    </div>`;
  }).join('');
}

function updateColMapping(containerId, key, value) {
  // This would update the column mapping and reprocess - simplified for now
  console.log('Column mapping updated:', containerId, key, value);
}

// ============================================================
// DATA MATCHING & MERGING (Review Step)
// ============================================================
function buildReview() {
  mergeData();
  const r = STATE.results;
  const s1 = r.section1;
  const s2 = r.section2;
  const low = parseFloat(document.getElementById('lowCoverage').value) || STATE.config.lowCoverage;
  const high = parseFloat(document.getElementById('highCoverage').value) || STATE.config.highCoverage;
  STATE.config.lowCoverage = low;
  STATE.config.highCoverage = high;

  const s1WithSales = s1.filter(p => p.avg3 > 0 || p.avg6 > 0 || p.avg12 > 0).length;
  const s2WithSales = s2.filter(p => p.avg3 > 0 || p.avg6 > 0 || p.avg12 > 0).length;
  const s1Urgent = s1.filter(p => {
    const wm = p.avg3 || 0;
    if (wm === 0) return false;
    return (p.soh + p.incoming - p.backOrder) / wm < low;
  }).length;
  const s2Urgent = s2.filter(p => {
    const wm = p.avg3 || 0;
    if (wm === 0) return false;
    return (p.soh + p.incoming - p.backOrder) / wm < low;
  }).length;
  const matchedBackOrders = [...s1, ...s2].reduce((a, p) => a + p.backOrder, 0);
  const totalRawBO = STATE.backOrders.items.reduce((a, b) => a + b.qty, 0);
  const unmatchedBO = totalRawBO - matchedBackOrders;
  const allProducts = [...s1, ...s2];
  const lcCounts = { New: 0, Core: 0, Active: 0, 'Selling Through': 0, Archived: 0 };
  allProducts.forEach(p => { if (lcCounts[p.lifecycle] !== undefined) lcCounts[p.lifecycle]++; });

  // Identify unmatched order form items (no sales history)
  const unmatchedOF = s1.filter(p => !(p.avg3 > 0 || p.avg6 > 0 || p.avg12 > 0));

  // Suggest categorisations for unmatched items on the order form (no sales history).
  // Being on the order form means they're available to order — default to New.
  // Only suggest Selling Through if there's SOH but nothing incoming (clearing old stock).
  unmatchedOF.forEach(p => {
    if (p.soh > 0 && p.incoming === 0) p._suggestion = 'Selling Through';
    else p._suggestion = 'New';
  });

  const content = document.getElementById('review-content');
  content.innerHTML = `
    <div class="stats-grid">
      <div class="stat-card"><div class="stat-value">${s1.length + s2.length}</div><div class="stat-label">Total Products</div></div>
      <div class="stat-card"><div class="stat-value">${s1WithSales + s2WithSales}</div><div class="stat-label">With Sales Data</div></div>
      ${matchedBackOrders > 0 ? `<div class="stat-card"><div class="stat-value" style="color:#e65100">${matchedBackOrders.toLocaleString()}</div><div class="stat-label">Units on Back Order</div></div>` : ''}
      <div class="stat-card"><div class="stat-value" style="color:#1565c0">${lcCounts.Core}</div><div class="stat-label">Core</div></div>
      <div class="stat-card"><div class="stat-value" style="color:#00796b">${lcCounts.Active}</div><div class="stat-label">Active</div></div>
      <div class="stat-card"><div class="stat-value" style="color:#6a1b9a">${lcCounts.New}</div><div class="stat-label">New</div></div>
      <div class="stat-card"><div class="stat-value" style="color:#e65100">${lcCounts['Selling Through']}</div><div class="stat-label">Selling Through</div></div>
      <div class="stat-card"><div class="stat-value" style="color:#c62828">${lcCounts.Archived}</div><div class="stat-label">Archived</div></div>
    </div>
    <div class="card" style="border-left:4px solid var(--blue)">
      <h3 style="margin-bottom:16px">Coverage Targets</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;max-width:360px;margin-bottom:16px">
        <div style="background:var(--blue-bg);border-radius:8px;padding:14px 16px;text-align:center">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--blue);font-weight:600;margin-bottom:6px">Low (months)</div>
          <input type="number" id="reviewLow" value="${low}" step="0.25" min="0" style="width:70px;padding:8px;border:2px solid var(--blue);border-radius:6px;font-size:20px;font-weight:700;text-align:center;color:var(--blue);background:#fff" onchange="STATE.config.lowCoverage=parseFloat(this.value);STATE.config.coverageManuallySet=true;document.getElementById('lowCoverage').value=this.value;renderTimeline();updateSplyInfo();renderCoverageGraphic()" oninput="STATE.config.lowCoverage=parseFloat(this.value);STATE.config.coverageManuallySet=true;if(document.getElementById('lowCoverage'))document.getElementById('lowCoverage').value=this.value;renderTimeline();updateSplyInfo();renderCoverageGraphic()">
        </div>
        <div style="background:var(--blue-bg);border-radius:8px;padding:14px 16px;text-align:center">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--blue);font-weight:600;margin-bottom:6px">High (months)</div>
          <input type="number" id="reviewHigh" value="${high}" step="0.25" min="0" style="width:70px;padding:8px;border:2px solid var(--blue);border-radius:6px;font-size:20px;font-weight:700;text-align:center;color:var(--blue);background:#fff" onchange="STATE.config.highCoverage=parseFloat(this.value);STATE.config.coverageManuallySet=true;document.getElementById('highCoverage').value=this.value;renderTimeline();updateSplyInfo();renderCoverageGraphic()" oninput="STATE.config.highCoverage=parseFloat(this.value);STATE.config.coverageManuallySet=true;if(document.getElementById('highCoverage'))document.getElementById('highCoverage').value=this.value;renderTimeline();updateSplyInfo();renderCoverageGraphic()">
        </div>
      </div>
      ${STATE.schedule.currentRun ? `<div style="display:grid;grid-template-columns:auto 1fr;gap:6px 12px;font-size:12px;color:#555;background:#f8f9fb;border-radius:6px;padding:10px 14px;margin-bottom:12px">
        <span style="font-weight:600;color:var(--blue)">LOW</span><span>${STATE.schedule.currentRun.run} <span style="color:var(--blue);font-weight:700">&rarr;</span> ${STATE.schedule.nextRun ? STATE.schedule.nextRun.run : 'next run'} <span style="color:#666">(ETA Warehouse ${STATE.schedule.currentRun.arrivalStr} <span style="font-weight:700">&rarr;</span> ETA Warehouse ${STATE.schedule.nextRun ? STATE.schedule.nextRun.arrivalStr : '?'})</span></span>
        <span style="font-weight:600;color:var(--blue)">HIGH</span><span>${STATE.schedule.currentRun.run} <span style="color:var(--blue);font-weight:700">&rarr;</span> ${STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : 'run after next'} <span style="color:#666">(ETA Warehouse ${STATE.schedule.currentRun.arrivalStr} <span style="font-weight:700">&rarr;</span> ETA Warehouse ${STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.arrivalStr : '?'})</span></span>
        <span style="color:#999;grid-column:1/-1;font-style:italic">Transit = 6 weeks from ex-factory</span>
      </div>
      <div id="coverageGraphic" style="margin-bottom:12px"></div>` : ''}
      <div id="splyInfo"></div>
    </div>

    <!-- REVIEW STEP 1: Unmatched Order Form Items -->
    ${buildUnmatchedOrderFormSection(unmatchedOF)}

    <!-- REVIEW STEP 2: Category Walkthrough -->
    ${buildCategoryWalkthrough(s1, s2)}

    <!-- REVIEW STEP 3: Unmatched Sales Data -->
    ${buildUnmatchedSalesSection(r.unmatchedSales || [])}

    ${r.flags.length > 0 ? `
    <div class="card">
      <h3>Flags & Warnings</h3>
      <ul class="flag-list">${r.flags.map(f => `<li class="${f.type}">${f.message}</li>`).join('')}</ul>
    </div>` : ''}
  `;
  markStepComplete(6);
  setTimeout(() => { wireLifecycleControls(); updateSplyInfo(); renderCoverageGraphic(); }, 50);
}

function buildUnmatchedSalesSection(items) {
  if (!items || items.length === 0) return '';
  return `
    <div class="card" style="border-left:4px solid #ff9800">
      <h3 style="color:#e65100">Unmatched Sales Items (${items.length})</h3>
      <p>These items appear in your monthly sales data but didn't match any product on the order form or SOH. They may be discontinued items, non-nanoblock products, or items with different codes.</p>
      <div class="preview-table-wrap" style="max-height:350px;overflow-y:auto"><table class="preview-table">
        <tr><th>Code</th><th>Description</th><th>Avg 3mo</th><th>Avg 12mo</th></tr>
        ${items.sort((a, b) => (b.avg3||0) - (a.avg3||0)).map(p => {
          const wm = p.avg3 || 0;
          return `<tr>
            <td>${p.code || '—'}</td>
            <td>${p.description || '—'}</td>
            <td class="num" style="color:${wm >= 20 ? '#c62828' : wm >= 5 ? '#e65100' : '#666'}">${wm.toFixed(1)}</td>
            <td class="num">${(p.avg12||0).toFixed(1)}</td>
          </tr>`;
        }).join('')}
      </table></div>
      ${items.filter(p => (p.avg3||0) >= 5).length > 0 ?
        `<p style="margin-top:8px;font-size:12px;color:#c62828;font-weight:600">⚠ ${items.filter(p => (p.avg3||0) >= 5).length} unmatched items have significant sales velocity (5+ units/month) — these may need investigation.</p>` : ''}
    </div>`;
}

function buildUnmatchedOrderFormSection(unmatchedOF) {
  if (!unmatchedOF || unmatchedOF.length === 0) return `
    <div class="card" style="border-left:4px solid #4caf50">
      <h3 style="color:#2e7d32">Step 1: Unmatched Order Form Items</h3>
      <p style="color:#2e7d32">All order form items matched to sales data. Nothing to review here.</p>
    </div>`;

  const lcColors = { New: '#6a1b9a', Core: '#1565c0', Active: '#00796b', 'Selling Through': '#e65100', Archived: '#c62828' };
  const lcOptions = ['New', 'Core', 'Active', 'Selling Through', 'Archived'];

  return `
    <div class="card" style="border-left:4px solid #ff9800">
      <h3 style="color:#e65100">Step 1: Unmatched Order Form Items (${unmatchedOF.length})</h3>
      <p>These products are on the order form but have <strong>no sales history</strong>. Review the AI suggestion, change the category if needed, then confirm.</p>
      <div style="font-size:11px;color:#888;margin-bottom:8px">
        Has incoming stock → <span style="color:#6a1b9a;font-weight:600">New</span> &nbsp;|&nbsp;
        Has SOH but no incoming → <span style="color:#e65100;font-weight:600">Selling Through</span> &nbsp;|&nbsp;
        No stock, no incoming → <span style="color:#6a1b9a;font-weight:600">New</span> (on order form = available to order)
      </div>
      <div style="display:flex;gap:6px;margin-bottom:8px">
        <button onclick="confirmAllUnmatched()" class="btn btn-sm" style="background:#e8f5e9;color:#2e7d32;border:1px solid #4caf50;font-weight:600">Confirm All Selections</button>
      </div>
      <div class="nosales-scroll" style="max-height:400px">
        <table class="nosales-table" id="unmatched-of-table">
          <thead>
            <tr>
              <th style="width:110px">Code</th>
              <th>Description</th>
              <th style="width:50px">SOH</th>
              <th style="width:65px">Incoming</th>
              <th style="width:85px">AI Suggestion</th>
              <th style="width:130px">Select Category</th>
              <th style="width:30px;font-size:9px">✓</th>
            </tr>
          </thead>
          <tbody>
            ${unmatchedOF.map(p => {
              const nc = normaliseCode(p.code);
              const suggestion = p._suggestion || 'New';
              const lc = p.lifecycle;
              const isReviewed = STATE.reviewedProducts && STATE.reviewedProducts.has(nc);
              return `<tr data-nc="${nc}" data-reviewed="${isReviewed}" ${isReviewed ? 'style="opacity:0.5"' : ''}>
                <td>${p.code}</td>
                <td>${p.description}</td>
                <td class="num">${p.soh}</td>
                <td class="num">${p.incoming || 0}</td>
                <td style="font-size:11px;color:${lcColors[suggestion]};font-weight:600">${suggestion}</td>
                <td><select class="cat-select" data-nc="${nc}" data-section="1" onchange="updateLifecycleCat(this)" style="font-size:11px;padding:2px 4px;border-radius:4px;border:1px solid var(--border);color:${lcColors[lc] || '#333'}">
                  ${lcOptions.map(o => `<option value="${o}" ${o === lc ? 'selected' : ''} style="color:${lcColors[o]}">${o}</option>`).join('')}
                </select></td>
                <td style="text-align:center"><button class="btn-confirm-lc" data-nc="${nc}" onclick="confirmLifecycleReview(this)" title="Confirm" ${isReviewed ? 'disabled style="opacity:0.3"' : ''}>✓</button></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    </div>`;
}

function buildCategoryWalkthrough(s1, s2) {
  const all = [...s1.map(p => ({...p, section: 1})), ...s2.map(p => ({...p, section: 2}))];
  if (all.length === 0) return '';

  const lcColors = { New: '#6a1b9a', Core: '#1565c0', Active: '#00796b', 'Selling Through': '#e65100', Archived: '#c62828' };
  const lcOptions = ['New', 'Core', 'Active', 'Selling Through', 'Archived'];
  const lcDescriptions = {
    New: 'New products with limited or no sales history. May still be awaiting first delivery.',
    Core: 'Top sellers — high volume, consistent demand. Should always be in stock.',
    Active: 'Regular sellers with steady moderate demand. Considered for reorder.',
    'Selling Through': "Declining or low sales. Don't reorder.",
    Archived: 'No stock, no recent sales, no orders. Effectively discontinued.'
  };
  const lcBorderColors = { New: '#6a1b9a', Core: '#1565c0', Active: '#00796b', 'Selling Through': '#e65100', Archived: '#c62828' };

  // Walk through categories in order: New → Core → Active → Selling Through → Archived
  const walkOrder = ['New', 'Core', 'Active', 'Selling Through', 'Archived'];
  const counts = { New: 0, Core: 0, Active: 0, 'Selling Through': 0, Archived: 0 };
  all.forEach(p => { if (counts[p.lifecycle] !== undefined) counts[p.lifecycle]++; });

  // AI reclassification suggestions
  function getSuggestion(p) {
    const avg3 = p.avg3 || 0;
    const avg12 = p.avg12 || 0;
    const lc = p.lifecycle;
    const flags = p.flags || '';
    // Suggest upgrades
    if (lc === 'Active' && avg3 >= 10 && flags.includes('GROWING')) return { to: 'Core', reason: 'Strong recent sales + growing trend' };
    if (lc === 'Selling Through' && avg3 >= 5 && !flags.includes('DECLIN')) return { to: 'Active', reason: 'Still selling regularly' };
    if (lc === 'New' && avg3 >= 5 && avg12 > 0) return { to: 'Active', reason: 'Established sales pattern' };
    if (lc === 'New' && avg3 >= 10) return { to: 'Core', reason: 'High sales for a new product' };
    // Suggest downgrades
    if (lc === 'Core' && avg3 < 3 && flags.includes('DECLIN')) return { to: 'Active', reason: 'Sales declining significantly' };
    if (lc === 'Active' && avg3 === 0 && p.soh > 0) return { to: 'Selling Through', reason: 'No recent sales' };
    if (lc === 'Selling Through' && avg3 === 0 && avg12 === 0 && p.soh === 0) return { to: 'Archived', reason: 'No sales or stock' };
    return null;
  }

  let html = `
    <div class="card nosales-section">
      <h3>Step 2: Category Review</h3>
      <p style="margin-bottom:8px"><strong>Product category determines order quantity suggestions.</strong> Core and Active products will receive order recommendations. Selling Through and Archived products will not. Review each category and adjust if needed.</p>
      <div style="font-size:12px;line-height:1.8;margin-bottom:12px">
      <span style="color:#1565c0;font-weight:600">Core</span> = always reorder, strong sellers &nbsp;|&nbsp;
      <span style="color:#00796b;font-weight:600">Active</span> = reorder, regular sellers &nbsp;|&nbsp;
      <span style="color:#6a1b9a;font-weight:600">New</span> = new items, awaiting sales data &nbsp;|&nbsp;
      <span style="color:#e65100;font-weight:600">Selling Through</span> = don't reorder, sell remaining stock &nbsp;|&nbsp;
      <span style="color:#c62828;font-weight:600">Archived</span> = discontinued, no order
      </div>
      <div id="lifecycle-summary" class="nosales-controls" style="gap:16px;margin-bottom:16px">
        ${lcOptions.map(lc => `<span style="color:${lcColors[lc]};font-weight:600">${lc}: ${counts[lc]}</span>`).join('')}
      </div>
      <div style="margin-bottom:12px">
        ${walkOrder.map(lc => `<button class="btn btn-sm cat-walk-btn" data-cat="${lc}" onclick="showCategoryPanel('${lc}')" style="color:${lcColors[lc]};border:1px solid ${lcColors[lc]};background:#fff;margin-right:4px;font-weight:600">${lc} (${counts[lc]})</button>`).join('')}
        <button class="btn btn-sm cat-walk-btn" data-cat="all" onclick="showCategoryPanel('all')" style="color:#333;border:1px solid #999;background:#fff;margin-left:8px">All (${all.length})</button>
      </div>`;

  // Build a panel for each category
  walkOrder.forEach(cat => {
    const catProducts = all.filter(p => p.lifecycle === cat);
    const suggestedProducts = catProducts.filter(p => getSuggestion(p));
    html += `
      <div class="cat-panel" id="cat-panel-${cat.replace(/\s/g,'')}" style="display:none;border-left:4px solid ${lcBorderColors[cat]};padding:12px 16px;margin-bottom:12px;background:#fafbfc;border-radius:0 8px 8px 0">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <div>
            <h4 style="color:${lcColors[cat]};margin:0">${cat} (${catProducts.length} products)</h4>
            <p style="font-size:12px;color:#666;margin:4px 0 0">${lcDescriptions[cat]}</p>
          </div>
          ${suggestedProducts.length > 0 ? `<span style="background:#fff3e0;color:#e65100;font-size:11px;padding:3px 8px;border-radius:12px;font-weight:600">${suggestedProducts.length} suggestion${suggestedProducts.length > 1 ? 's' : ''}</span>` : ''}
          <button onclick="confirmCategoryAll('${cat.replace(/'/g,"\\'")}')" class="btn btn-sm" style="background:#e8f5e9;color:#2e7d32;border:1px solid #4caf50;font-weight:600;font-size:11px">Confirm All ${cat}</button>
        </div>
        <div class="nosales-scroll" style="max-height:400px">
          <table class="nosales-table cat-review-table" data-cat="${cat}">
            <thead>
              <tr>
                <th style="width:30px">S</th>
                <th style="width:110px">Code</th>
                <th>Description</th>
                <th style="width:65px">Avg3</th>
                <th style="width:65px">Avg12</th>
                <th style="width:50px">SOH</th>
                <th style="width:65px">Incoming</th>
                <th style="width:75px">Flags</th>
                <th style="width:75px">Months Cover</th>
                <th style="width:130px">Category</th>
                <th style="width:30px;font-size:9px">✓</th>
              </tr>
            </thead>
            <tbody>
              ${catProducts.map(p => {
                const nc = normaliseCode(p.code);
                const lc = p.lifecycle;
                const isReviewed = STATE.reviewedProducts && STATE.reviewedProducts.has(nc);
                const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
                const cover = ci.coverMethod === 'none' ? '—' : ci.cover.toFixed(1);
                const coverNum = ci.coverMethod === 'none' ? 999 : ci.cover;
                const coverSuffix = ci.dataMonths > 0 && ci.dataMonths < 13 ? '*' : '';
                const coverColor = coverNum < 2 ? 'var(--red)' : coverNum < 4 ? '#e65100' : '#2e7d32';
                const suggestion = getSuggestion(p);
                const flagColor = (p.flags||'').includes('GROWING') ? '#2e7d32' : (p.flags||'').includes('DECLIN') ? '#c62828' : '#e65100';
                return `<tr data-nc="${nc}" data-section="${p.section}" data-lc="${lc}" ${suggestion ? 'style="background:#fff8e1"' : ''} ${isReviewed ? 'data-reviewed="true" style="opacity:0.4"' : ''}>
                  <td style="color:${p.section === 1 ? 'var(--primary)' : 'var(--purple)'};font-weight:700">${p.section}</td>
                  <td>${p.code}</td>
                  <td>${p.description}${suggestion ? `<div style="font-size:10px;color:#e65100;margin-top:2px">→ Suggest <strong>${suggestion.to}</strong>: ${suggestion.reason}</div>` : ''}</td>
                  <td class="num">${Math.round(p.avg3) || 0}</td>
                  <td class="num">${Math.round(p.avg12) || 0}</td>
                  <td class="num">${p.soh}</td>
                  <td class="num">${p.incoming || 0}</td>
                  <td style="font-size:10px;color:${flagColor}">${p.flags || '—'}</td>
                  <td class="num" style="color:${cover !== '—' ? coverColor : '#999'};font-weight:${coverNum < 2 ? '700' : ''}">${cover}${coverSuffix}</td>
                  <td><select class="cat-select" data-nc="${nc}" data-section="${p.section}" onchange="updateLifecycleCat(this)" style="font-size:11px;padding:2px 4px;border-radius:4px;border:1px solid var(--border);color:${lcColors[lc] || '#333'}">
                    ${lcOptions.map(o => `<option value="${o}" ${o === lc ? 'selected' : ''} style="color:${lcColors[o]}">${o}</option>`).join('')}
                  </select></td>
                  <td style="text-align:center"><button class="btn-confirm-lc" data-nc="${nc}" onclick="confirmCategoryRow(this)" title="Confirm" ${isReviewed ? 'disabled style="opacity:0.3"' : ''}>✓</button></td>
                </tr>`;
              }).join('')}
              ${catProducts.length === 0 ? '<tr><td colspan="11" style="color:#999;text-align:center;padding:16px">No products in this category</td></tr>' : ''}
            </tbody>
          </table>
        </div>
      </div>`;
  });

  // All products panel
  html += `
      <div class="cat-panel" id="cat-panel-all" style="display:none;border-left:4px solid #333;padding:12px 16px;margin-bottom:12px;background:#fafbfc;border-radius:0 8px 8px 0">
        <h4 style="color:#333;margin:0 0 8px">All Products (${all.length})</h4>
        <div class="nosales-scroll" style="max-height:500px">
          <table class="nosales-table" id="lifecycle-table">
            <thead>
              <tr>
                <th style="width:30px" class="sortable" data-col="0" data-type="num" onclick="sortLifecycleTable(this)">S</th>
                <th style="width:110px" class="sortable" data-col="1" data-type="str" onclick="sortLifecycleTable(this)">Code</th>
                <th class="sortable" data-col="2" data-type="str" onclick="sortLifecycleTable(this)">Description</th>
                <th style="width:65px" class="sortable" data-col="3" data-type="num" onclick="sortLifecycleTable(this)">Avg3</th>
                <th style="width:65px" class="sortable" data-col="4" data-type="num" onclick="sortLifecycleTable(this)">Avg12</th>
                <th style="width:50px" class="sortable" data-col="5" data-type="num" onclick="sortLifecycleTable(this)">SOH</th>
                <th style="width:65px" class="sortable" data-col="6" data-type="num" onclick="sortLifecycleTable(this)">Incoming</th>
                <th style="width:50px" class="sortable" data-col="7" data-type="num" onclick="sortLifecycleTable(this)">BO</th>
                <th style="width:75px" class="sortable" data-col="8" data-type="num" onclick="sortLifecycleTable(this)">Months Cover</th>
                <th style="width:120px" class="sortable" data-col="9" data-type="str" onclick="sortLifecycleTable(this)">Lifecycle</th>
              </tr>
            </thead>
            <tbody>
              ${all.map(p => {
                const nc = normaliseCode(p.code);
                const lc = p.lifecycle;
                const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
                const cover = ci.coverMethod === 'none' ? '—' : ci.cover.toFixed(1);
                const coverNum = ci.coverMethod === 'none' ? 999 : ci.cover;
                const coverSuffix = ci.dataMonths > 0 && ci.dataMonths < 13 ? '*' : '';
                const coverColor = coverNum < 2 ? 'var(--red)' : coverNum < 4 ? '#e65100' : '#2e7d32';
                return `<tr data-nc="${nc}" data-section="${p.section}" data-lc="${lc}" data-sortvals="${p.section}||${p.code}||${(p.description||'').replace(/"/g,'&quot;')}||${Math.round(p.avg3)||0}||${Math.round(p.avg12)||0}||${p.soh}||${p.incoming||0}||${p.backOrder||0}||${coverNum}||${lc}">
                  <td style="color:${p.section === 1 ? 'var(--primary)' : 'var(--purple)'};font-weight:700">${p.section}</td>
                  <td>${p.code}</td><td>${p.description}</td>
                  <td class="num">${Math.round(p.avg3) || 0}</td><td class="num">${Math.round(p.avg12) || 0}</td>
                  <td class="num">${p.soh}</td><td class="num">${p.incoming || 0}</td>
                  <td class="num" style="color:${(p.backOrder || 0) > 0 ? '#e65100' : ''}">${(p.backOrder || 0) > 0 ? p.backOrder : ''}</td>
                  <td class="num" style="color:${cover !== '—' ? coverColor : '#999'};font-weight:${coverNum < 2 ? '700' : ''}">${cover}${coverSuffix}</td>
                  <td><select class="cat-select" data-nc="${nc}" data-section="${p.section}" onchange="updateLifecycleCat(this)" style="font-size:11px;padding:2px 4px;border-radius:4px;border:1px solid var(--border);color:${lcColors[lc] || '#333'}">
                    ${lcOptions.map(o => `<option value="${o}" ${o === lc ? 'selected' : ''} style="color:${lcColors[o]}">${o}</option>`).join('')}
                  </select></td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ${Object.keys(STATE.lifecycleOverrides || {}).length > 0 ? `<div style="font-size:12px;padding:8px 12px;background:#e3f2fd;border-radius:6px;margin-top:12px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:6px">
        <span>Shared overrides: <strong>${Object.keys(STATE.lifecycleOverrides).length}</strong> (synced across team via Google Sheet) — <a href="https://docs.google.com/spreadsheets/d/17bg3wxOSVwpqRxwhRMs7rvuxxCzOEWlR9f1vcp54J_I/edit" target="_blank" style="color:#1565c0;text-decoration:underline">View spreadsheet</a></span>
        <button onclick="if(confirm('Clear all saved lifecycle overrides for ALL users? Auto-detection will be used for all products next time.')){STATE.lifecycleOverrides={};saveLifecycleOverrides();fetch(SYNC_API+'?action=clear',{method:'POST'}).catch(function(){});location.reload()}" style="font-size:11px;padding:2px 8px;border-radius:4px;border:1px solid #90caf9;background:#fff;cursor:pointer">Clear saved</button>
      </div>` : ''}
    </div>`;

  return html;
}

let _activeCatPanel = null;
function showCategoryPanel(cat) {
  _activeCatPanel = cat;
  // Hide all panels
  document.querySelectorAll('.cat-panel').forEach(p => p.style.display = 'none');
  // Show selected
  const id = cat === 'all' ? 'cat-panel-all' : 'cat-panel-' + cat.replace(/\s/g,'');
  const panel = document.getElementById(id);
  if (panel) panel.style.display = 'block';
  // Highlight active button
  document.querySelectorAll('.cat-walk-btn').forEach(b => {
    const bCat = b.dataset.cat;
    const isActive = bCat === cat;
    if (isActive) {
      b.style.background = b.style.borderColor || '#333';
      b.style.color = '#fff';
    } else {
      b.style.background = '#fff';
      b.style.color = b.style.borderColor || '#333';
    }
  });
}

function confirmAllUnmatched() {
  const table = document.getElementById('unmatched-of-table');
  if (!table) return;
  var allProducts = (STATE.results.section1 || []).concat(STATE.results.section2 || []);
  let count = 0;
  table.querySelectorAll('tbody tr').forEach(tr => {
    const nc = tr.dataset.nc;
    if (!nc || tr.dataset.reviewed === 'true') return;
    markProductReviewed(nc);
    // Save lifecycle override
    const select = tr.querySelector('.cat-select');
    if (select) {
      STATE.lifecycleOverrides[nc] = select.value;
      var prod = allProducts.find(function(x) { return normaliseCode(x.code) === nc; });
      if (prod) prod.lifecycle = select.value;
    }
    count++;
  });
  if (count > 0) {
    saveLifecycleOverrides();
    // Replace the unmatched section with a success message
    const card = table.closest('.card');
    if (card) {
      card.innerHTML = `
        <h3 style="color:#2e7d32">Step 1: All items categorised ✓</h3>
        <p style="color:#2e7d32">${count} items confirmed and moved to their lifecycle groups below.</p>`;
    }
    // Rebuild the category walkthrough panels to include the newly categorised items
    rebuildCategoryPanels();
    updateLifecycleSummary();
  }
}

function rebuildCategoryPanels() {
  // Rebuild the category panels from current STATE.results data
  const s1 = STATE.results.section1 || [];
  const s2 = STATE.results.section2 || [];
  const all = [...s1.map(p => ({...p, section: 1})), ...s2.map(p => ({...p, section: 2}))];
  const lcColors = { New: '#6a1b9a', Core: '#1565c0', Active: '#00796b', 'Selling Through': '#e65100', Archived: '#c62828' };
  const lcOptions = ['New', 'Core', 'Active', 'Selling Through', 'Archived'];
  const lcDescriptions = {
    New: 'New products with limited or no sales history.',
    Core: 'Top sellers — high volume, consistent demand.',
    Active: 'Regular sellers with steady moderate demand. Considered for reorder.',
    'Selling Through': "Declining or low sales. Don't reorder.",
    Archived: 'No stock, no recent sales. Effectively discontinued.'
  };

  function getSuggestion(p) {
    const avg3 = p.avg3 || 0;
    const avg12 = p.avg12 || 0;
    const lc = p.lifecycle;
    const flags = p.flags || '';
    if (lc === 'Active' && avg3 >= 10 && flags.includes('GROWING')) return { to: 'Core', reason: 'Strong recent sales + growing trend' };
    if (lc === 'Selling Through' && avg3 >= 5 && !flags.includes('DECLIN')) return { to: 'Active', reason: 'Still selling regularly' };
    if (lc === 'New' && avg3 >= 5 && avg12 > 0) return { to: 'Active', reason: 'Established sales pattern' };
    if (lc === 'New' && avg3 >= 10) return { to: 'Core', reason: 'High sales for a new product' };
    if (lc === 'Core' && avg3 < 3 && flags.includes('DECLIN')) return { to: 'Active', reason: 'Sales declining significantly' };
    if (lc === 'Active' && avg3 === 0 && p.soh > 0) return { to: 'Selling Through', reason: 'No recent sales' };
    if (lc === 'Selling Through' && avg3 === 0 && avg12 === 0 && p.soh === 0) return { to: 'Archived', reason: 'No sales or stock' };
    return null;
  }

  const walkOrder = ['New', 'Core', 'Active', 'Selling Through', 'Archived'];
  walkOrder.forEach(cat => {
    const panel = document.getElementById('cat-panel-' + cat.replace(/\s/g,''));
    if (!panel) return;
    const catProducts = all.filter(p => p.lifecycle === cat);
    const suggestedProducts = catProducts.filter(p => getSuggestion(p));
    const header = panel.querySelector('h4');
    if (header) header.innerHTML = `${cat} (${catProducts.length} products)`;
    const badge = panel.querySelector('span[style*="fff3e0"]');
    if (badge && suggestedProducts.length > 0) badge.textContent = `${suggestedProducts.length} suggestion${suggestedProducts.length > 1 ? 's' : ''}`;
    else if (badge) badge.style.display = 'none';

    // Rebuild the table body
    const tbody = panel.querySelector('tbody');
    if (!tbody) return;
    tbody.innerHTML = catProducts.map(p => {
      const nc = normaliseCode(p.code);
      const lc = p.lifecycle;
      const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
      const cover = ci.coverMethod === 'none' ? '—' : ci.cover.toFixed(1);
      const coverNum = ci.coverMethod === 'none' ? 999 : ci.cover;
      const coverSuffix = ci.dataMonths > 0 && ci.dataMonths < 13 ? '*' : '';
      const coverColor = coverNum < 2 ? 'var(--red)' : coverNum < 4 ? '#e65100' : '#2e7d32';
      const suggestion = getSuggestion(p);
      const flagColor = (p.flags||'').includes('GROWING') ? '#2e7d32' : (p.flags||'').includes('DECLIN') ? '#c62828' : '#e65100';
      return `<tr data-nc="${nc}" data-section="${p.section}" data-lc="${lc}" ${suggestion ? 'style="background:#fff8e1"' : ''}>
        <td style="color:${p.section === 1 ? 'var(--primary)' : 'var(--purple)'};font-weight:700">${p.section}</td>
        <td>${p.code}</td>
        <td>${p.description}${suggestion ? `<div style="font-size:10px;color:#e65100;margin-top:2px">→ Suggest <strong>${suggestion.to}</strong>: ${suggestion.reason}</div>` : ''}</td>
        <td class="num">${Math.round(p.avg3) || 0}</td>
        <td class="num">${Math.round(p.avg12) || 0}</td>
        <td class="num">${p.soh}</td>
        <td class="num">${p.incoming || 0}</td>
        <td style="font-size:10px;color:${flagColor}">${p.flags || '—'}</td>
        <td class="num" style="color:${cover !== '—' ? coverColor : '#999'};font-weight:${coverNum < 2 ? '700' : ''}">${cover}${coverSuffix}</td>
        <td><select class="cat-select" data-nc="${nc}" data-section="${p.section}" onchange="updateLifecycleCat(this)" style="font-size:11px;padding:2px 4px;border-radius:4px;border:1px solid var(--border);color:${lcColors[lc] || '#333'}">
          ${lcOptions.map(o => `<option value="${o}" ${o === lc ? 'selected' : ''} style="color:${lcColors[o]}">${o}</option>`).join('')}
        </select></td>
      </tr>`;
    }).join('') || '<tr><td colspan="10" style="color:#999;text-align:center;padding:16px">No products in this category</td></tr>';
  });

  // Also rebuild the "All" panel
  const allPanel = document.getElementById('cat-panel-all');
  if (allPanel) {
    const tbody = allPanel.querySelector('tbody');
    if (tbody) {
      tbody.innerHTML = all.map(p => {
        const nc = normaliseCode(p.code);
        const lc = p.lifecycle;
        const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
        const cover = ci.coverMethod === 'none' ? '—' : ci.cover.toFixed(1);
        const coverNum = ci.coverMethod === 'none' ? 999 : ci.cover;
        const coverSuffix = ci.dataMonths > 0 && ci.dataMonths < 13 ? '*' : '';
        const coverColor = coverNum < 2 ? 'var(--red)' : coverNum < 4 ? '#e65100' : '#2e7d32';
        return `<tr data-nc="${nc}" data-section="${p.section}" data-lc="${lc}" data-sortvals="${p.section}||${p.code}||${(p.description||'').replace(/"/g,'&quot;')}||${Math.round(p.avg3)||0}||${Math.round(p.avg12)||0}||${p.soh}||${p.incoming||0}||${p.backOrder||0}||${coverNum}||${lc}">
          <td style="color:${p.section === 1 ? 'var(--primary)' : 'var(--purple)'};font-weight:700">${p.section}</td>
          <td>${p.code}</td><td>${p.description}</td>
          <td class="num">${Math.round(p.avg3) || 0}</td><td class="num">${Math.round(p.avg12) || 0}</td>
          <td class="num">${p.soh}</td><td class="num">${p.incoming || 0}</td>
          <td class="num" style="color:${(p.backOrder || 0) > 0 ? '#e65100' : ''}">${(p.backOrder || 0) > 0 ? p.backOrder : ''}</td>
          <td class="num" style="color:${cover !== '—' ? coverColor : '#999'};font-weight:${coverNum < 2 ? '700' : ''}">${cover}${coverSuffix}</td>
          <td><select class="cat-select" data-nc="${nc}" data-section="${p.section}" onchange="updateLifecycleCat(this)" style="font-size:11px;padding:2px 4px;border-radius:4px;border:1px solid var(--border);color:${lcColors[lc] || '#333'}">
            ${lcOptions.map(o => `<option value="${o}" ${o === lc ? 'selected' : ''} style="color:${lcColors[o]}">${o}</option>`).join('')}
          </select></td>
        </tr>`;
      }).join('');
    }
  }

  // Re-show whichever panel was active
  if (_activeCatPanel) showCategoryPanel(_activeCatPanel);
}

function updateLifecycleCat(el) {
  const nc = el.dataset.nc;
  const section = parseInt(el.dataset.section);
  const val = el.value;
  if (!STATE.lifecycleOverrides) STATE.lifecycleOverrides = {};
  STATE.lifecycleOverrides[nc] = val;
  saveLifecycleOverrides();
  // Update the corresponding product in results
  const list = section === 1 ? STATE.results.section1 : STATE.results.section2;
  if (list) {
    const p = list.find(x => normaliseCode(x.code) === nc);
    if (p) p.lifecycle = val;
  }
  // Also mark as reviewed when lifecycle is changed
  markProductReviewed(nc);
  // Rebuild all category panels so the product moves to its new panel
  rebuildCategoryPanels();
  updateLifecycleSummary();
}

function confirmLifecycleReview(btn) {
  const nc = btn.dataset.nc;
  const row = btn.closest('tr');
  const table = row && row.closest('table');
  const isUnmatchedTable = table && table.id === 'unmatched-of-table';
  markProductReviewed(nc);

  // Save the lifecycle override
  const select = row.querySelector('.cat-select');
  if (select) {
    STATE.lifecycleOverrides[nc] = select.value;
    // Update the product in results
    var allProducts = (STATE.results.section1 || []).concat(STATE.results.section2 || []);
    var prod = allProducts.find(function(x) { return normaliseCode(x.code) === nc; });
    if (prod) prod.lifecycle = select.value;
    saveLifecycleOverrides();
  }

  if (isUnmatchedTable) {
    // Animate out and remove from unmatched list
    row.style.transition = 'opacity 0.3s, max-height 0.3s';
    row.style.opacity = '0';
    row.style.maxHeight = '0';
    row.style.overflow = 'hidden';
    setTimeout(function() {
      row.remove();
      // Update the unmatched count in the heading
      var remaining = table.querySelectorAll('tbody tr').length;
      var heading = table.closest('.card').querySelector('h3');
      if (heading) {
        if (remaining === 0) {
          heading.innerHTML = '<span style="color:#2e7d32">Step 1: All items categorised ✓</span>';
        } else {
          heading.textContent = 'Step 1: Unmatched Order Form Items (' + remaining + ')';
        }
      }
    }, 300);
    // Rebuild category panels so the product appears in its new group
    rebuildCategoryPanels();
  } else {
    // Non-unmatched table: just dim the row
    row.dataset.reviewed = 'true';
    row.style.opacity = '0.5';
    btn.disabled = true;
    btn.style.opacity = '0.3';
  }
  updateLifecycleSummary();
}

// Confirm individual product in category review panel (Step 2)
function confirmCategoryRow(btn) {
  var nc = btn.dataset.nc;
  var row = btn.closest('tr');
  if (!nc || !row) return;
  markProductReviewed(nc);
  // Save lifecycle override from dropdown
  var select = row.querySelector('.cat-select');
  if (select) {
    STATE.lifecycleOverrides[nc] = select.value;
    var allProducts = (STATE.results.section1 || []).concat(STATE.results.section2 || []);
    var prod = allProducts.find(function(x) { return normaliseCode(x.code) === nc; });
    if (prod) prod.lifecycle = select.value;
    saveLifecycleOverrides();
  }
  // Dim the row
  row.dataset.reviewed = 'true';
  row.style.opacity = '0.4';
  btn.disabled = true;
  btn.style.opacity = '0.3';
  // Update the confirmed count on the category panel
  var table = row.closest('.cat-review-table');
  if (table) {
    var total = table.querySelectorAll('tbody tr[data-nc]').length;
    var confirmed = table.querySelectorAll('tbody tr[data-reviewed="true"]').length;
    var heading = table.closest('.cat-panel').querySelector('h4');
    if (heading && confirmed === total) {
      heading.innerHTML = heading.textContent.replace(/\)$/, '') + ' — all confirmed ✓)';
    }
  }
  updateLifecycleSummary();
}

// Confirm all products in a category panel (Step 2)
function confirmCategoryAll(catName) {
  var panelId = 'cat-panel-' + catName.replace(/\s/g, '');
  var panel = document.getElementById(panelId);
  if (!panel) return;
  var allProducts = (STATE.results.section1 || []).concat(STATE.results.section2 || []);
  var count = 0;
  panel.querySelectorAll('tbody tr[data-nc]').forEach(function(tr) {
    if (tr.dataset.reviewed === 'true') return;
    var nc = tr.dataset.nc;
    markProductReviewed(nc);
    var select = tr.querySelector('.cat-select');
    if (select) {
      STATE.lifecycleOverrides[nc] = select.value;
      var prod = allProducts.find(function(x) { return normaliseCode(x.code) === nc; });
      if (prod) prod.lifecycle = select.value;
    }
    tr.dataset.reviewed = 'true';
    tr.style.opacity = '0.4';
    var btn = tr.querySelector('.btn-confirm-lc');
    if (btn) { btn.disabled = true; btn.style.opacity = '0.3'; }
    count++;
  });
  if (count > 0) {
    saveLifecycleOverrides();
    var heading = panel.querySelector('h4');
    if (heading) heading.innerHTML = heading.textContent.replace(/\)$/, '') + ' — all confirmed ✓)';
    updateLifecycleSummary();
  }
}

function confirmAllVisibleReviews() {
  const table = document.getElementById('lifecycle-table');
  if (!table) return;
  const mainFilter = document.getElementById('lifecycle-filter');
  const isReviewView = mainFilter && mainFilter.value === 'review';
  let count = 0;
  table.querySelectorAll('tbody tr').forEach(tr => {
    if (tr.style.display === 'none') return; // skip hidden rows
    const nc = tr.dataset.nc;
    if (!nc || tr.dataset.reviewed === 'true') return;
    markProductReviewed(nc);
    tr.dataset.review = 'false';
    tr.dataset.reviewed = 'true';
    const btn = tr.querySelector('.btn-confirm-lc');
    if (btn) { btn.disabled = true; btn.style.opacity = '0.3'; }
    // Save lifecycle override
    if (!STATE.lifecycleOverrides[nc]) {
      const select = tr.querySelector('.cat-select');
      if (select) { STATE.lifecycleOverrides[nc] = select.value; }
    }
    if (isReviewView) {
      tr.style.transition = 'opacity 0.3s';
      tr.style.opacity = '0';
      setTimeout(() => { tr.style.display = 'none'; tr.style.opacity = '1'; }, 300);
    }
    count++;
  });
  if (count > 0) saveLifecycleOverrides();
  updateLifecycleSummary();
}

function clearReviewedProducts() {
  if (!confirm('Reset all reviewed products? They will reappear for review.')) return;
  STATE.reviewedProducts = new Set();
  localStorage.removeItem(REVIEWED_STORAGE_KEY);
  // Re-enable all confirm buttons across all tables
  document.querySelectorAll('.btn-confirm-lc').forEach(btn => {
    btn.disabled = false;
    btn.style.opacity = '1';
  });
  document.querySelectorAll('[data-reviewed]').forEach(el => {
    el.dataset.reviewed = 'false';
    el.style.opacity = '1';
  });
}

function updateLifecycleSummary() {
  const el = document.getElementById('lifecycle-summary');
  if (!el || !STATE.results) return;
  const all = [...(STATE.results.section1 || []), ...(STATE.results.section2 || [])];
  const counts = { New: 0, Core: 0, Active: 0, 'Selling Through': 0, Archived: 0 };
  all.forEach(p => { if (counts[p.lifecycle] !== undefined) counts[p.lifecycle]++; });
  const lcColors = { New: '#6a1b9a', Core: '#1565c0', Active: '#00796b', 'Selling Through': '#e65100', Archived: '#c62828' };
  el.innerHTML = Object.entries(counts).map(([lc, n]) => `<span style="color:${lcColors[lc]};font-weight:600">${lc}: ${n}</span>`).join('');
  // Update category walkthrough buttons
  document.querySelectorAll('.cat-walk-btn').forEach(btn => {
    const cat = btn.dataset.cat;
    if (cat && cat !== 'all' && counts[cat] !== undefined) {
      btn.textContent = `${cat} (${counts[cat]})`;
    } else if (cat === 'all') {
      btn.textContent = `All (${all.length})`;
    }
  });
}

function wireLifecycleControls() {
  // Apply any stored overrides to results
  if (STATE.lifecycleOverrides && STATE.results) {
    [...(STATE.results.section1 || []), ...(STATE.results.section2 || [])].forEach(p => {
      const nc = normaliseCode(p.code);
      if (STATE.lifecycleOverrides[nc]) p.lifecycle = STATE.lifecycleOverrides[nc];
    });
  }
}

// Sort lifecycle table by clicking column headers
function sortLifecycleTable(th) {
  const table = document.getElementById('lifecycle-table');
  if (!table) return;
  const col = parseInt(th.dataset.col);
  const type = th.dataset.type; // 'num' or 'str'
  const isAsc = th.classList.contains('sort-asc');
  // Clear all sort indicators
  table.querySelectorAll('th.sortable').forEach(h => h.classList.remove('sort-asc', 'sort-desc'));
  const dir = isAsc ? 'desc' : 'asc';
  th.classList.add('sort-' + dir);

  const tbody = table.querySelector('tbody');
  const rows = Array.from(tbody.querySelectorAll('tr'));
  rows.sort((a, b) => {
    const aVals = (a.dataset.sortvals || '').split('||');
    const bVals = (b.dataset.sortvals || '').split('||');
    let av = aVals[col] || '';
    let bv = bVals[col] || '';
    if (type === 'num') {
      const an = parseFloat(av) || 0;
      const bn = parseFloat(bv) || 0;
      return dir === 'asc' ? an - bn : bn - an;
    }
    av = av.toLowerCase(); bv = bv.toLowerCase();
    return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  rows.forEach(r => tbody.appendChild(r));
}

// Apply lifecycle dropdown filter on lifecycle table
// applyLifecycleFilters removed — category walkthrough panels handle filtering now

// ============================================================
// AVERAGES & FLAGS ENGINE (replaces old seasonal/trend engine)
// Show real data, flag the exceptions, let the human decide.
// ============================================================

// Days in month helper — accounts for leap years
function daysInMonthFn(calMonth, year) {
  if (calMonth === 1) { // February
    return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28;
  }
  return [31,31,31,30,31,30,31,31,30,31,30,31][calMonth] || 30;
}
// Default days-in-month array (non-leap) for cases where year is unknown
var _dimDefault = [31,28,31,30,31,30,31,31,30,31,30,31];

// Stockout detection: identify months with ZERO sales for Core products.
// Zero-sales months after a product has started selling indicate supply issues (out of stock),
// not lack of demand. These months are excluded from averages so calculations reflect true demand.
// Months before the product's first ever sale are ignored (new product, not a stockout).
function detectStockoutMonths(monthlyArr, coreSeasonalIndex) {
  if (!monthlyArr || monthlyArr.length < 6) return { stockoutIndices: new Set(), median: 0, hasStockouts: false };

  // New product detection: find first month with sales > 0.
  // Months before first sale are pre-launch — never flag them as stockouts.
  var firstSaleIdx = -1;
  for (var fi = 0; fi < monthlyArr.length; fi++) {
    if (monthlyArr[fi].qty > 0) { firstSaleIdx = fi; break; }
  }
  if (firstSaleIdx < 0) return { stockoutIndices: new Set(), median: 0, hasStockouts: false };

  // Need at least 6 ACTIVE months (since first sale) to reliably detect stockouts.
  // Products with fewer active months are too new — don't flag anything.
  var activeMonths = monthlyArr.slice(firstSaleIdx);
  if (activeMonths.length < 6) return { stockoutIndices: new Set(), median: 0, hasStockouts: false };

  // Calculate median from active months for reference
  var sortedQtys = activeMonths.map(function(m) { return m.qty; }).sort(function(a, b) { return a - b; });
  var mid = Math.floor(sortedQtys.length / 2);
  var median = sortedQtys.length % 2 === 0 ? (sortedQtys[mid - 1] + sortedQtys[mid]) / 2 : sortedQtys[mid];

  // Flag months with zero/near-zero sales (after product started selling)
  var stockoutIndices = new Set();
  for (var i = 0; i < monthlyArr.length; i++) {
    if (i < firstSaleIdx) continue; // Pre-launch — not a stockout
    if (monthlyArr[i].qty < 1) { // Catches zero and near-zero from floating point rounding
      stockoutIndices.add(i);
    }
  }
  return { stockoutIndices: stockoutIndices, median: median, hasStockouts: stockoutIndices.size > 0 };
}

// Build calendar month averages excluding stockout months, with fallback to median
function buildCleanCalMonthAvg(monthlyArr, stockoutIndices) {
  const calMonthSums = {};
  const calMonthCounts = {};
  for (let cm = 0; cm < 12; cm++) { calMonthSums[cm] = 0; calMonthCounts[cm] = 0; }
  for (let i = 0; i < monthlyArr.length; i++) {
    if (stockoutIndices.has(i)) continue; // skip stockout months
    const cm = monthlyArr[i].calMonth;
    calMonthSums[cm] += monthlyArr[i].qty;
    calMonthCounts[cm]++;
  }
  const calMonthAvg = {};
  // Overall clean average as fallback
  let cleanTotal = 0, cleanCount = 0;
  for (let i = 0; i < monthlyArr.length; i++) {
    if (!stockoutIndices.has(i)) { cleanTotal += monthlyArr[i].qty; cleanCount++; }
  }
  const cleanAvg = cleanCount > 0 ? cleanTotal / cleanCount : 0;
  for (let cm = 0; cm < 12; cm++) {
    calMonthAvg[cm] = calMonthCounts[cm] > 0 ? calMonthSums[cm] / calMonthCounts[cm] : cleanAvg;
  }
  return calMonthAvg;
}

// Calculate simple averages and flags from raw monthly data
// Returns { avg3, avg6, avg12, samePeriodLY, samePeriodLY_low, samePeriodLY_high, splyLowMonthNames, splyHighMonthNames, flags }
// arrivalStartMonth: optional 0-11 calendar month of warehouse arrival date to anchor SPLY window
// arrivalStartDay: optional 1-31 day of month — used to pro-rate the first month
function calcAveragesAndFlags(monthlyArr, coverageLow, coverageHigh, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex) {
  if (!monthlyArr || monthlyArr.length === 0) {
    return { avg3: 0, avg6: 0, avg12: 0, samePeriodLY: 0, samePeriodLY_low: 0, samePeriodLY_high: 0, splyLowMonthNames: '', splyHighMonthNames: '', flags: '', stockoutMonths: 0 };
  }

  const n = monthlyArr.length;
  // Detect stockout months (only for Core products with 6+ months of data)
  const isCore = lifecycle === 'Core';
  const stockoutInfo = (isCore && n >= 6) ? detectStockoutMonths(monthlyArr, coreSeasonalIndex) : { stockoutIndices: new Set(), median: 0, hasStockouts: false };

  // Calculate averages — for Core products, exclude stockout months from the denominator
  // globalStartIdx = the index in monthlyArr where this slice begins
  function cleanAvg(globalStartIdx, sliceLen) {
    if (sliceLen <= 0) return 0;
    if (!isCore || !stockoutInfo.hasStockouts) {
      let total = 0;
      for (let i = globalStartIdx; i < globalStartIdx + sliceLen; i++) total += monthlyArr[i].qty;
      return total / sliceLen;
    }
    let total = 0, count = 0;
    for (let i = globalStartIdx; i < globalStartIdx + sliceLen; i++) {
      if (!stockoutInfo.stockoutIndices.has(i)) {
        total += monthlyArr[i].qty;
        count++;
      }
    }
    // If all months in slice were stockouts, fall back to median
    return count > 0 ? total / count : stockoutInfo.median;
  }
  const len3 = Math.min(n, 3);
  const len6 = Math.min(n, 6);
  const len12 = Math.min(n, 12);

  const avg3 = cleanAvg(n - len3, len3);
  const avg6 = cleanAvg(n - len6, len6);
  const avg12 = cleanAvg(n - len12, len12);

  // Same Sales Period Last Year: sum the calendar months matching coverage period from 12+ months ago
  // Calculate separately for LOW and HIGH coverage windows, with pro-rated partial months
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let samePeriodLY_low = 0, samePeriodLY_high = 0;
  let splyLowMonthNames = '', splyHighMonthNames = '';

  // Pro-rate the first month based on arrival day (e.g. arriving 21 Aug = only 11/31 of Aug)
  const startDay = arrivalStartDay || 1;

  // For Core products, get clean calendar month averages (excluding stockout months)
  // Then use those averages × count for SPLY instead of raw sums
  function getCleanMonthQty(data, cm) {
    if (!isCore || !stockoutInfo.hasStockouts) {
      // No stockout adjustment — sum all data for this calendar month as before
      return data.filter(m => m.calMonth === cm).reduce((s, m) => s + m.qty, 0);
    }
    // Exclude stockout months, then average × count of all instances (imputing clean values)
    const allForCm = data.filter(m => m.calMonth === cm);
    if (allForCm.length === 0) return 0;
    // Find which are stockouts by checking against the global stockout indices
    const cleanEntries = [];
    for (let j = 0; j < data.length; j++) {
      // Find the global index of this entry in monthlyArr
      const gi = monthlyArr.indexOf(data[j]);
      if (data[j].calMonth === cm && (gi < 0 || !stockoutInfo.stockoutIndices.has(gi))) {
        cleanEntries.push(data[j].qty);
      }
    }
    if (cleanEntries.length > 0) {
      const cleanAvgForCm = cleanEntries.reduce((s, q) => s + q, 0) / cleanEntries.length;
      return cleanAvgForCm * allForCm.length; // impute for all instances
    }
    // All instances were stockouts — use median
    return stockoutInfo.median * allForCm.length;
  }

  function calcSPLY(olderData, startCalMonth, coverMonths) {
    if (!olderData.length || coverMonths <= 0) return { total: 0, label: '' };
    const firstMonthDays = _dimDefault[startCalMonth] || 30;
    const firstMonthFrac = Math.min(1, (firstMonthDays - startDay + 1) / firstMonthDays);
    let remaining = coverMonths - firstMonthFrac;
    let total = 0;
    const labels = [];
    // First (partial) month
    const firstCm = startCalMonth % 12;
    total += getCleanMonthQty(olderData, firstCm) * firstMonthFrac;
    if (firstMonthFrac > 0.99) {
      labels.push(MONTH_NAMES[firstCm]);
    } else {
      labels.push(MONTH_NAMES[firstCm] + ` (${Math.round(firstMonthFrac * 100)}%)`);
    }
    // Subsequent full months
    let i = 1;
    while (remaining >= 1 - 0.01) {
      const cm = (startCalMonth + i) % 12;
      total += getCleanMonthQty(olderData, cm);
      labels.push(MONTH_NAMES[cm]);
      remaining -= 1;
      i++;
    }
    // Final partial month
    if (remaining > 0.01) {
      const cm = (startCalMonth + i) % 12;
      total += getCleanMonthQty(olderData, cm) * remaining;
      labels.push(MONTH_NAMES[cm] + ` (${Math.round(remaining * 100)}%)`);
    }
    return { total: Math.round(total), label: labels.join(', ') };
  }

  if (n >= 13) {
    // Use warehouse arrival month if provided, otherwise fall back to month after last sales data
    const startCalMonth = (arrivalStartMonth !== undefined && arrivalStartMonth !== null)
      ? arrivalStartMonth
      : (monthlyArr[n - 1].calMonth + 1) % 12;
    const olderData = monthlyArr.slice(0, -12);

    const lowResult = calcSPLY(olderData, startCalMonth, coverageLow ?? 2);
    samePeriodLY_low = lowResult.total;
    splyLowMonthNames = lowResult.label;

    const highResult = calcSPLY(olderData, startCalMonth, coverageHigh ?? 4);
    samePeriodLY_high = highResult.total;
    splyHighMonthNames = highResult.label;
  }
  const samePeriodLY = samePeriodLY_high; // backwards compat

  // Build flags array
  const flagList = [];

  // NEW: barely any data
  if (n <= 2) {
    flagList.push('NEW');
    return { avg3, avg6, avg12, samePeriodLY, samePeriodLY_low, samePeriodLY_high, splyLowMonthNames, splyHighMonthNames, flags: flagList.join(', ') };
  }

  // SHORT HISTORY: less than 6 months
  if (n < 6) {
    flagList.push('SHORT HISTORY');
  }

  // GROWING / DECLINING: year-on-year comparison when 13+ months available, else regression
  if (n >= 13) {
    // Compare recent 6 months vs same 6 months a year ago
    const recentPeriod = monthlyArr.slice(-6);
    const recentTotal = recentPeriod.reduce((s, m) => s + m.qty, 0);
    // Find matching calendar months from ~12 months earlier
    let priorTotal = 0;
    let priorCount = 0;
    recentPeriod.forEach(rm => {
      const match = monthlyArr.find(m => m.calMonth === rm.calMonth && m.year === rm.year - 1);
      if (match) { priorTotal += match.qty; priorCount++; }
    });
    if (priorCount >= 3 && priorTotal > 0) {
      const yoyChange = (recentTotal - priorTotal) / priorTotal;
      // Cross-check: don't flag DECLINING if recent avg trend is up, or GROWING if trend is down
      if (yoyChange > 0.25 && avg3 >= avg12 * 0.8) flagList.push('GROWING');
      else if (yoyChange < -0.25 && avg3 <= avg12 * 1.2) flagList.push('DECLINING');
    }
  } else if (n >= 6) {
    // Fallback: compare first half vs second half
    const half = Math.floor(n / 2);
    const firstHalf = monthlyArr.slice(0, half);
    const secondHalf = monthlyArr.slice(-half);
    const firstAvg = firstHalf.reduce((s, m) => s + m.qty, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((s, m) => s + m.qty, 0) / secondHalf.length;
    if (firstAvg > 0) {
      const change = (secondAvg - firstAvg) / firstAvg;
      if (change > 0.30 && avg3 >= avg12 * 0.8) flagList.push('GROWING');
      else if (change < -0.30 && avg3 <= avg12 * 1.2) flagList.push('DECLINING');
    }
  }

  // SPIKE: one abnormally large month (possibly a bulk order skewing the average)
  if (n >= 4) {
    const sorted = [...monthlyArr].map(m => m.qty).sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    if (median > 0) {
      const maxQty = Math.max(...monthlyArr.map(m => m.qty));
      if (maxQty > median * 3) flagList.push('SPIKE');
    }
  }

  // STOCKOUT: Core products with months flagged as likely stockouts (averages adjusted)
  if (stockoutInfo.hasStockouts) {
    flagList.push('STOCKOUT (' + stockoutInfo.stockoutIndices.size + 'mo)');
  }

  return { avg3, avg6, avg12, samePeriodLY, samePeriodLY_low, samePeriodLY_high, splyLowMonthNames, splyHighMonthNames, flags: flagList.join(', '), stockoutMonths: stockoutInfo.stockoutIndices.size };
}

// ============================================================
// SOPHISTICATED COVER CALCULATION
// 13+ months: month-by-month depletion using last year's actuals
// <13 months: average of all available months (simple division)
// ============================================================
function calcCoverFromMonthly(monthlyArr, projectedStock, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex) {
  // No monthly data — can't calculate cover
  if (!monthlyArr || monthlyArr.length === 0) {
    return { cover: 0, burnRate: 0, dataMonths: 0, coverMethod: 'none' };
  }
  // Zero or negative stock at arrival — cover is 0.0 (not N/A)
  if (projectedStock <= 0) {
    var avgAll = monthlyArr.reduce(function(s, m) { return s + m.qty; }, 0) / monthlyArr.length;
    return { cover: 0, burnRate: Math.round(avgAll), dataMonths: monthlyArr.length, coverMethod: monthlyArr.length >= 13 ? 'seasonal' : 'average' };
  }
  const n = monthlyArr.length;
  const startCalMonth = (arrivalStartMonth !== undefined && arrivalStartMonth !== null)
    ? arrivalStartMonth
    : (monthlyArr[n - 1].calMonth + 1) % 12;

  if (n >= 13) {
    // For Core products, use clean calendar month averages (excluding stockout months)
    const isCore = lifecycle === 'Core';
    const stockoutInfo = (isCore && n >= 6) ? detectStockoutMonths(monthlyArr, coreSeasonalIndex) : { stockoutIndices: new Set(), hasStockouts: false };
    const calMonthAvg = (isCore && stockoutInfo.hasStockouts)
      ? buildCleanCalMonthAvg(monthlyArr, stockoutInfo.stockoutIndices)
      : (function() {
          const olderData = monthlyArr.slice(0, -12);
          const avg = {};
          for (let cm = 0; cm < 12; cm++) {
            const entries = olderData.filter(m => m.calMonth === cm);
            avg[cm] = entries.length > 0 ? entries.reduce((s, m) => s + m.qty, 0) / entries.length : 0;
          }
          return avg;
        })();
    const recent12 = monthlyArr.slice(-12);
    const recent12Avg = recent12.reduce((s, m) => s + m.qty, 0) / 12;

    // Pro-rate first month based on arrival day
    const sDay = arrivalStartDay || 1;
    const firstMonthDays = _dimDefault[startCalMonth] || 30;
    const firstMonthFrac = Math.min(1, (firstMonthDays - sDay + 1) / firstMonthDays);

    let stock = projectedStock;
    let months = 0;
    const maxMonths = 24;

    // First (partial) month
    let firstSales = calMonthAvg[startCalMonth % 12];
    if (firstSales === 0 && recent12Avg > 0) firstSales = recent12Avg;
    if (firstSales <= 0) { return { cover: projectedStock > 0 ? 24 : 0, burnRate: 0, dataMonths: n, coverMethod: 'seasonal', noDemand: true }; }
    const firstMonthSales = firstSales * firstMonthFrac;
    if (stock >= firstMonthSales) {
      stock -= firstMonthSales;
      months += firstMonthFrac;
    } else {
      months += (stock / firstSales);
      stock = 0;
    }

    // Subsequent full months
    let i = 1;
    while (stock > 0 && months < maxMonths) {
      const cm = (startCalMonth + i) % 12;
      let monthSales = calMonthAvg[cm];
      if (monthSales === 0 && recent12Avg > 0) monthSales = recent12Avg;
      if (monthSales <= 0) { months = maxMonths; break; } // Cap at maxMonths if no demand data
      if (stock >= monthSales) {
        stock -= monthSales;
        months += 1;
      } else {
        months += stock / monthSales;
        stock = 0;
      }
      i++;
    }
    const burnRate = recent12Avg;
    return { cover: Math.round(Math.min(months, 24) * 10) / 10, burnRate: Math.round(burnRate), dataMonths: n, coverMethod: 'seasonal' };
  } else {
    // SIMPLE: average of all available months
    const totalQty = monthlyArr.reduce((s, m) => s + m.qty, 0);
    const avgAll = totalQty / n;
    const cover = avgAll > 0 ? projectedStock / avgAll : (projectedStock > 0 ? 24 : 0);
    return { cover: Math.round(Math.min(cover, 24) * 10) / 10, burnRate: Math.round(avgAll), dataMonths: n, coverMethod: 'average', noDemand: avgAll <= 0 && projectedStock > 0 };
  }
}

// Calculate seasonal order quantity needed for a coverage period
// Returns the total sales expected over the next coverMonths, using seasonal data if available
// arrivalStartMonth: optional 0-11 calendar month of warehouse arrival date to anchor demand window
// arrivalStartDay: optional 1-31 day of month — used to pro-rate the first month
function calcSeasonalDemand(monthlyArr, coverMonths, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex) {
  if (!monthlyArr || monthlyArr.length === 0 || coverMonths <= 0) return 0;
  const n = monthlyArr.length;
  const startCalMonth = (arrivalStartMonth !== undefined && arrivalStartMonth !== null)
    ? arrivalStartMonth
    : (monthlyArr[n - 1].calMonth + 1) % 12;

  if (n >= 13) {
    // For Core products, use clean calendar month averages (excluding stockout months)
    const isCore = lifecycle === 'Core';
    const stockoutInfo = (isCore && n >= 6) ? detectStockoutMonths(monthlyArr, coreSeasonalIndex) : { stockoutIndices: new Set(), hasStockouts: false };
    const calMonthAvg = (isCore && stockoutInfo.hasStockouts)
      ? buildCleanCalMonthAvg(monthlyArr, stockoutInfo.stockoutIndices)
      : (function() {
          const olderData = monthlyArr.slice(0, -12);
          const avg = {};
          for (let cm = 0; cm < 12; cm++) {
            const entries = olderData.filter(m => m.calMonth === cm);
            avg[cm] = entries.length > 0 ? entries.reduce((s, m) => s + m.qty, 0) / entries.length : 0;
          }
          return avg;
        })();
    const recent12 = monthlyArr.slice(-12);
    const recent12Avg = recent12.reduce((s, m) => s + m.qty, 0) / 12;
    // Pro-rate first month based on arrival day
    const sDay = arrivalStartDay || 1;
    const firstMonthDays = _dimDefault[startCalMonth] || 30;
    const firstMonthFrac = Math.min(1, (firstMonthDays - sDay + 1) / firstMonthDays);

    let total = 0;
    // First (partial) month
    let qty0 = calMonthAvg[startCalMonth % 12];
    if (qty0 === 0 && recent12Avg > 0) qty0 = recent12Avg;
    total += qty0 * firstMonthFrac;
    let remaining = coverMonths - firstMonthFrac;
    // Subsequent months
    let i = 1;
    while (remaining >= 1 - 0.01) {
      const cm = (startCalMonth + i) % 12;
      let qty = calMonthAvg[cm];
      if (qty === 0 && recent12Avg > 0) qty = recent12Avg;
      total += qty;
      remaining -= 1;
      i++;
    }
    if (remaining > 0.01) {
      const cm = (startCalMonth + i) % 12;
      let qty = calMonthAvg[cm];
      if (qty === 0 && recent12Avg > 0) qty = recent12Avg;
      total += qty * remaining;
    }
    return Math.round(total);
  } else {
    const totalQty = monthlyArr.reduce((s, m) => s + m.qty, 0);
    const avgAll = totalQty / n;
    return Math.round(avgAll * coverMonths);
  }
}

// Legacy fallback for products without monthly data
function calcWeightedMonthly(s12, s6, s3) {
  if (s3 === 0 && s12 === 0) return 0;
  if (s3 === 0) return s12 / 12;
  if (s12 === 0) return s3 / 12;
  return (s3 * 0.6 + s12 * 0.4) / 12;
}

function mergeData() {
  const orderProducts = STATE.orderForm.products;
  const sohItems = STATE.soh.items;
  const st12 = STATE.monthlySales.s12Items;
  const st6 = STATE.monthlySales.s6Items;
  const st3 = STATE.monthlySales.s3Items;
  const stPrior = STATE.monthlySales.sPriorItems;
  const coverLow = STATE.config.lowCoverage ?? 2;
  const coverHigh = STATE.config.highCoverage ?? 4;

  // Build lookup maps
  const sohByCode = {};
  sohItems.forEach(s => { if (s.normCode) sohByCode[s.normCode] = s; });

  // Sales lookup by normalised description (nanoblock items have no codes)
  const st12ByDesc = {};
  st12.forEach(s => { if (s.normDesc) st12ByDesc[s.normDesc] = s; });
  const st6ByDesc = {};
  st6.forEach(s => { if (s.normDesc) st6ByDesc[s.normDesc] = s; });
  const st3ByDesc = {};
  st3.forEach(s => { if (s.normDesc) st3ByDesc[s.normDesc] = s; });
  const stPriorByDesc = {};
  stPrior.forEach(s => { if (s.normDesc) stPriorByDesc[s.normDesc] = s; });

  // Also try code-based lookup for sales
  const st12ByCode = {};
  st12.forEach(s => { if (s.normCode) st12ByCode[s.normCode] = s; });
  const st6ByCode = {};
  st6.forEach(s => { if (s.normCode) st6ByCode[s.normCode] = s; });
  const st3ByCode = {};
  st3.forEach(s => { if (s.normCode) st3ByCode[s.normCode] = s; });
  const stPriorByCode = {};
  stPrior.forEach(s => { if (s.normCode) stPriorByCode[s.normCode] = s; });

  // PO incoming aggregated by code — total and per-PO
  const incomingByCode = {};
  const perPoByCode = {}; // { normCode: [qty_po1, qty_po2, ...] }
  STATE.pos.forEach((po, poIdx) => {
    po.items.forEach(p => {
      const nc = normaliseCode(p.code);
      incomingByCode[nc] = (incomingByCode[nc] || 0) + p.qty;
      if (!perPoByCode[nc]) perPoByCode[nc] = new Array(STATE.pos.length).fill(0);
      perPoByCode[nc][poIdx] += p.qty;
    });
  });

  // Back orders aggregated by code
  const backOrderByCode = {};
  STATE.backOrders.items.forEach(b => {
    const nc = b.normCode || normaliseCode(b.code);
    backOrderByCode[nc] = (backOrderByCode[nc] || 0) + b.qty;
  });

  // Build monthly data lookup maps
  const monthlyByCode = {};
  const monthlyByDesc = {};
  STATE.monthlySales.items.forEach(item => {
    if (item.normCode) monthlyByCode[item.normCode] = item;
    if (item.normDesc) monthlyByDesc[item.normDesc] = item;
  });

  // Track which sales items are consumed (by normCode AND normDesc) for unmatched detection
  const matchedSalesKeys = new Set();
  function findSales(normCode, description) {
    const normD = normaliseDesc(description);
    const s12v = st12ByCode[normCode] || st12ByDesc[normD];
    const s6v = st6ByCode[normCode] || st6ByDesc[normD];
    const s3v = st3ByCode[normCode] || st3ByDesc[normD];
    const sPriorV = stPriorByCode[normCode] || stPriorByDesc[normD];
    const monthlyItem = monthlyByCode[normCode] || monthlyByDesc[normD];
    const anyMatch = s12v || s6v || s3v || sPriorV || monthlyItem;
    if (anyMatch) {
      if (normCode) matchedSalesKeys.add(normCode);
      if (normD) matchedSalesKeys.add(normD);
      [s12v, s6v, s3v, sPriorV].forEach(v => {
        if (v) {
          if (v.normCode) matchedSalesKeys.add(v.normCode);
          if (v.normDesc) matchedSalesKeys.add(v.normDesc);
        }
      });
      if (monthlyItem) {
        if (monthlyItem.normCode) matchedSalesKeys.add(monthlyItem.normCode);
        if (monthlyItem.normDesc) matchedSalesKeys.add(monthlyItem.normDesc);
      }
    }
    return {
      s12: s12v ? s12v.sold : 0,
      s6: s6v ? s6v.sold : 0,
      s3: s3v ? s3v.sold : 0,
      sPrior: sPriorV ? sPriorV.sold : 0,
      monthlyArr: monthlyItem ? monthlyItem.monthlyArr : null,
    };
  }

  // Derive arrival start month and day from current order's warehouse arrival date
  const arrivalStartMonth = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
    ? STATE.schedule.currentRun.arrivalDate.getMonth()
    : undefined;
  const arrivalStartDay = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
    ? STATE.schedule.currentRun.arrivalDate.getDate()
    : 1;

  // Pre-arrival burn: months from today until warehouse arrival of current order
  // Sales will continue during this period, depleting SOH before the new stock arrives
  const today = new Date();
  const burnMonths = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
    ? Math.max(0, monthsBetween(today, STATE.schedule.currentRun.arrivalDate))
    : 0;
  const todayCalMonth = today.getMonth(); // 0-11, start month for burn period

  // Calculate expected sales from today until arrival for a product
  function calcBurnToArrival(monthlyArr) {
    if (burnMonths <= 0 || !monthlyArr || monthlyArr.length === 0) return 0;
    return calcSeasonalDemand(monthlyArr, burnMonths, todayCalMonth);
  }

  // ============================================================
  // Weekly stock simulation: walk forward week by week from today
  // to the current run's arrival date. Each week, add any PO stock
  // that arrives (matched by run number from the production schedule)
  // and subtract seasonal weekly sales. Returns the projected stock
  // level at the current run's arrival date.
  // ============================================================
  function simulateStockAtArrival(soh, backOrder, perPo, monthlyArr, lifecycle) {
    var currentRunArrival = STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate;
    var totalIncoming = perPo.reduce(function(s, q) { return s + q; }, 0);

    // No arrival date or no schedule — fall back to simple calculation
    if (!currentRunArrival) {
      return { stock: Math.max(0, soh + totalIncoming - backOrder), simulated: false, weeklyLog: null };
    }

    var simToday = new Date();
    simToday.setHours(0, 0, 0, 0);

    // If arrival is today or in the past, all stock is available
    if (currentRunArrival <= simToday) {
      return { stock: Math.max(0, soh + totalIncoming - backOrder), simulated: false, weeklyLog: null };
    }

    // Build calendar month average sales (stockout-adjusted for Core products)
    var calMonthAvg;
    if (monthlyArr && monthlyArr.length >= 1) {
      var isCore = lifecycle === 'Core';
      if (isCore && monthlyArr.length >= 6) {
        var stockoutInfo = detectStockoutMonths(monthlyArr, coreSeasonalIndex);
        calMonthAvg = stockoutInfo.hasStockouts
          ? buildCleanCalMonthAvg(monthlyArr, stockoutInfo.stockoutIndices)
          : buildCleanCalMonthAvg(monthlyArr, new Set());
      } else {
        calMonthAvg = buildCleanCalMonthAvg(monthlyArr, new Set());
      }
    } else {
      // No monthly data — can't simulate seasonally, fall back
      return { stock: Math.max(0, soh + totalIncoming - backOrder), simulated: false, weeklyLog: null };
    }

    // Map each PO index to its arrival date from the production schedule
    var poArrivals = [];
    for (var i = 0; i < STATE.pos.length; i++) {
      var poName = STATE.pos[i].name;
      var poNumMatch = poName.match(/(\d+\.?\d*)/);
      var poNum = poNumMatch ? parseFloat(poNumMatch[1]) : -1;
      var poArrivalDate = null;
      var poStatus = '';

      // Find matching schedule row by run number
      if (poNum >= 0) {
        for (var j = 0; j < STATE.schedule.rows.length; j++) {
          var row = STATE.schedule.rows[j];
          if (row.runNum === poNum) {
            // Prefer crossDocksReady date (actual warehouse arrival) if available
            var cdr = parseDate(row.crossDocksReady);
            poArrivalDate = cdr || row.arrivalDate;
            poStatus = row.statusLower || '';
            break;
          }
        }
      }

      poArrivals.push({
        idx: i,
        qty: perPo[i],
        arrivalDate: poArrivalDate,
        status: poStatus,
        name: poName
      });
    }

    // Start simulation with current SOH minus back orders
    var stock = soh - backOrder;
    var WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    var weekStart = new Date(simToday);
    var weeklyLog = []; // For debugging: track what happens each week

    // First: add PO stock that has already arrived (status=delivered or arrival in past)
    for (var p = 0; p < poArrivals.length; p++) {
      var pa = poArrivals[p];
      if (pa.qty <= 0) continue;
      var alreadyArrived = (pa.status.indexOf('deliver') >= 0) ||
                           (pa.arrivalDate && pa.arrivalDate < simToday) ||
                           (!pa.arrivalDate); // No schedule match — assume available
      if (alreadyArrived) {
        stock += pa.qty;
        pa.qty = 0; // Mark as added so we don't add again
      }
    }

    // Walk forward week by week
    while (weekStart < currentRunArrival) {
      var weekEnd = new Date(weekStart.getTime() + WEEK_MS);
      // Clamp weekEnd to arrival date so we don't overshoot
      if (weekEnd > currentRunArrival) weekEnd = currentRunArrival;
      var daysInWeek = (weekEnd - weekStart) / (1000 * 60 * 60 * 24);

      // Add any PO stock arriving this week
      var arrivedThisWeek = 0;
      for (var p2 = 0; p2 < poArrivals.length; p2++) {
        var pa2 = poArrivals[p2];
        if (pa2.qty > 0 && pa2.arrivalDate && pa2.arrivalDate >= weekStart && pa2.arrivalDate < weekEnd) {
          stock += pa2.qty;
          arrivedThisWeek += pa2.qty;
          pa2.qty = 0; // Mark as added
        }
      }

      // Subtract weekly sales (seasonal monthly avg / 4.3, pro-rated for partial weeks)
      var cm = weekStart.getMonth();
      var weeklySales = ((calMonthAvg[cm] || 0) / 4.3) * (daysInWeek / 7);
      stock -= weeklySales;

      weeklyLog.push({
        weekStart: weekStart.toISOString().slice(0, 10),
        days: Math.round(daysInWeek),
        arrived: arrivedThisWeek,
        sales: Math.round(weeklySales),
        stockEnd: Math.round(stock)
      });

      weekStart = weekEnd;
    }

    return { stock: Math.max(0, Math.round(stock)), simulated: true, weeklyLog: weeklyLog };
  }

  // Compute averages + flags for a product using new transparent engine
  function computeAverages(sales, lifecycle) {
    if (sales.monthlyArr && sales.monthlyArr.length >= 1) {
      return calcAveragesAndFlags(sales.monthlyArr, coverLow, coverHigh, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
    }
    // Fallback: no monthly data — use legacy annualised values to derive rough averages
    const wm = calcWeightedMonthly(sales.s12, sales.s6, sales.s3);
    return {
      avg3: sales.s3 > 0 ? Math.round(sales.s3 / 3) : 0,
      avg6: sales.s6 > 0 ? Math.round(sales.s6 / 6) : 0,
      avg12: sales.s12 > 0 ? Math.round(sales.s12 / 12) : 0,
      samePeriodLY: 0, samePeriodLY_low: 0, samePeriodLY_high: 0,
      splyLowMonthNames: '', splyHighMonthNames: '',
      flags: (sales.s12 === 0 && sales.s3 === 0) ? '' : 'NO MONTHLY DATA',
    };
  }

  // ============================================================
  // Pre-pass: build Core product seasonal index for stockout detection.
  // For each calendar month 0-11, compute the average ratio of (actual qty / product median)
  // across all Core products. This tells us which months are genuinely slow (seasonal)
  // vs which products individually dipped (likely stockout).
  // ============================================================
  var coreSeasonalIndex = {};
  for (var _ci = 0; _ci < 12; _ci++) coreSeasonalIndex[_ci] = 1.0; // default: no adjustment
  try { (function buildCoreSeasonalIndex() {
    var coreProducts = []; // collect { monthlyArr, median }
    // Helper: determine lifecycle for a product using raw averages
    function quickLifecycle(nc, monthlyArr, sohQty, incoming, sPrior) {
      if (!monthlyArr || monthlyArr.length < 1) return null;
      var n = monthlyArr.length;
      var a12 = monthlyArr.slice(-Math.min(n, 12)).reduce(function(s, m) { return s + m.qty; }, 0) / Math.min(n, 12);
      var a6 = monthlyArr.slice(-Math.min(n, 6)).reduce(function(s, m) { return s + m.qty; }, 0) / Math.min(n, 6);
      var a3 = monthlyArr.slice(-Math.min(n, 3)).reduce(function(s, m) { return s + m.qty; }, 0) / Math.min(n, 3);
      var autoLC = detectLifecycle(Math.round(a12 * 12), Math.round(a6 * 6), Math.round(a3 * 3), sohQty, incoming, sPrior);
      return (STATE.lifecycleOverrides && STATE.lifecycleOverrides[nc]) || autoLC;
    }
    // Scan order form products
    orderProducts.forEach(function(p) {
      var nc = p.normCode;
      var soh = sohByCode[nc];
      var sales = findSales(nc, p.description);
      if (!sales.monthlyArr || sales.monthlyArr.length < 6) return;
      var lc = quickLifecycle(nc, sales.monthlyArr, soh ? soh.qty : 0, incomingByCode[nc] || 0, sales.sPrior);
      if (lc !== 'Core') return;
      // Compute median for this product
      var sorted = sales.monthlyArr.map(function(m) { return m.qty; }).sort(function(a, b) { return a - b; });
      var mid = Math.floor(sorted.length / 2);
      var med = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      if (med > 0) coreProducts.push({ monthlyArr: sales.monthlyArr, median: med });
    });
    // Also scan SOH items that might be Core
    sohItems.forEach(function(s) {
      if (!s.normCode) return;
      var sales = findSales(s.normCode, s.description);
      if (!sales.monthlyArr || sales.monthlyArr.length < 6) return;
      var lc = quickLifecycle(s.normCode, sales.monthlyArr, s.qty, incomingByCode[s.normCode] || 0, sales.sPrior);
      if (lc !== 'Core') return;
      var sorted = sales.monthlyArr.map(function(m) { return m.qty; }).sort(function(a, b) { return a - b; });
      var mid = Math.floor(sorted.length / 2);
      var med = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      if (med > 0) coreProducts.push({ monthlyArr: sales.monthlyArr, median: med });
    });
    // Need at least 3 Core products for a meaningful index
    if (coreProducts.length < 3) {
      for (var cm = 0; cm < 12; cm++) coreSeasonalIndex[cm] = 1.0;
      console.warn('[Stockout] Only ' + coreProducts.length + ' Core products found — need 3+ for seasonal index. Using flat baseline.');
      return;
    }
    // For each calendar month, collect ratios of (actual / median) across all Core products
    var cmRatios = {};
    for (var cm = 0; cm < 12; cm++) cmRatios[cm] = [];
    coreProducts.forEach(function(cp) {
      cp.monthlyArr.forEach(function(m) {
        cmRatios[m.calMonth].push(m.qty / cp.median);
      });
    });
    // Average ratio per calendar month (use median of ratios to reduce outlier impact)
    for (var cm = 0; cm < 12; cm++) {
      if (cmRatios[cm].length >= 3) {
        cmRatios[cm].sort(function(a, b) { return a - b; });
        var rmid = Math.floor(cmRatios[cm].length / 2);
        coreSeasonalIndex[cm] = cmRatios[cm].length % 2 === 0
          ? (cmRatios[cm][rmid - 1] + cmRatios[cm][rmid]) / 2
          : cmRatios[cm][rmid];
      } else {
        coreSeasonalIndex[cm] = 1.0; // not enough data, no adjustment
      }
    }
    console.log('[Stockout] Core seasonal index built from ' + coreProducts.length + ' products:', coreSeasonalIndex);
  })(); } catch(e) { console.error('[Stockout] Seasonal index build failed, using defaults:', e); }

  // Build Section 1: Order form products
  const orderCodeSet = new Set();
  STATE.results.section1 = orderProducts.map(p => {
    const nc = p.normCode;
    orderCodeSet.add(nc);
    const soh = sohByCode[nc];
    const sales = findSales(nc, p.description);
    const incoming = incomingByCode[nc] || 0;
    const backOrder = backOrderByCode[nc] || 0;
    const perPo = perPoByCode[nc] || new Array(STATE.pos.length).fill(0);
    const exoCode = soh ? soh.code : p.code.replace(/-/g, '_');
    const sohQty = soh ? soh.qty : 0;
    // Determine lifecycle first (needed for stockout adjustment on Core products)
    // Use raw averages for lifecycle detection, then re-compute with stockout adjustment
    const rawAvgs = (sales.monthlyArr && sales.monthlyArr.length >= 1)
      ? { avg12: sales.monthlyArr.slice(-12).reduce((s, m) => s + m.qty, 0) / Math.min(sales.monthlyArr.length, 12),
          avg6: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 6)).reduce((s, m) => s + m.qty, 0) / Math.min(sales.monthlyArr.length, 6),
          avg3: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 3)).reduce((s, m) => s + m.qty, 0) / Math.min(sales.monthlyArr.length, 3) }
      : { avg12: sales.s12 > 0 ? sales.s12 / 12 : 0, avg6: sales.s6 > 0 ? sales.s6 / 6 : 0, avg3: sales.s3 > 0 ? sales.s3 / 3 : 0 };
    const autoLifecycle = detectLifecycle(
      Math.round(rawAvgs.avg12 * 12), Math.round(rawAvgs.avg6 * 6), Math.round(rawAvgs.avg3 * 3),
      sohQty, incoming, sales.sPrior
    );
    const lifecycle = (STATE.lifecycleOverrides && STATE.lifecycleOverrides[nc]) || autoLifecycle;
    // Now compute averages with stockout adjustment for Core products
    const avgs = computeAverages(sales, lifecycle);
    // Weekly stock simulation: replaces simple projStock - burnToArrival
    const simResult = simulateStockAtArrival(sohQty, backOrder, perPo, sales.monthlyArr, lifecycle);
    const stockAtArrival = simResult.stock;
    const burnToArrival = Math.round((sohQty + incoming - backOrder) - stockAtArrival); // derived for display
    const coverInfo = calcCoverFromMonthly(sales.monthlyArr, stockAtArrival, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
    const demandLow = calcSeasonalDemand(sales.monthlyArr, coverLow, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
    const demandHigh = calcSeasonalDemand(sales.monthlyArr, coverHigh, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
    return {
      code: p.code,
      exoCode,
      description: p.description,
      series: p.series,
      category: categoriseByCode(normaliseCode(p.code), p.description),
      qtyCtn: p.qtyCtn,
      price: p.price,
      soh: sohQty,
      incoming,
      perPo,
      backOrder,
      burnToArrival,
      stockAtArrival,
      simulated: simResult.simulated,
      weeklyLog: simResult.weeklyLog,
      lifecycle,
      monthlyArr: sales.monthlyArr,
      coverInfo,
      demandLow,
      demandHigh,
      ...avgs,
    };
  });

  // Build Section 2: SOH items NOT on order form
  STATE.results.section2 = sohItems
    .filter(s => s.normCode && !orderCodeSet.has(s.normCode))
    .filter(s => {
      const c = s.normCode;
      return c.startsWith('NB') || c.startsWith('NBA') || c.startsWith('NBMC');
    })
    .map(s => {
      const sales = findSales(s.normCode, s.description);
      const incoming = incomingByCode[s.normCode] || 0;
      const backOrder = backOrderByCode[s.normCode] || 0;
      const perPo = perPoByCode[s.normCode] || new Array(STATE.pos.length).fill(0);
      // Determine lifecycle first for stockout adjustment
      const rawAvgs2 = (sales.monthlyArr && sales.monthlyArr.length >= 1)
        ? { avg12: sales.monthlyArr.slice(-12).reduce((s2, m) => s2 + m.qty, 0) / Math.min(sales.monthlyArr.length, 12),
            avg6: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 6)).reduce((s2, m) => s2 + m.qty, 0) / Math.min(sales.monthlyArr.length, 6),
            avg3: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 3)).reduce((s2, m) => s2 + m.qty, 0) / Math.min(sales.monthlyArr.length, 3) }
        : { avg12: sales.s12 > 0 ? sales.s12 / 12 : 0, avg6: sales.s6 > 0 ? sales.s6 / 6 : 0, avg3: sales.s3 > 0 ? sales.s3 / 3 : 0 };
      const autoLifecycle = detectLifecycle(
        Math.round(rawAvgs2.avg12 * 12), Math.round(rawAvgs2.avg6 * 6), Math.round(rawAvgs2.avg3 * 3),
        s.qty, incoming, sales.sPrior
      );
      const lifecycle = (STATE.lifecycleOverrides && STATE.lifecycleOverrides[s.normCode]) || autoLifecycle;
      const avgs = computeAverages(sales, lifecycle);
      // Weekly stock simulation: replaces simple projStock - burnToArrival
      const simResult = simulateStockAtArrival(s.qty, backOrder, perPo, sales.monthlyArr, lifecycle);
      const stockAtArrival = simResult.stock;
      const burnToArrival = Math.round((s.qty + incoming - backOrder) - stockAtArrival); // derived for display
      const coverInfo = calcCoverFromMonthly(sales.monthlyArr, stockAtArrival, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
      const demandLow = calcSeasonalDemand(sales.monthlyArr, coverLow, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
      const demandHigh = calcSeasonalDemand(sales.monthlyArr, coverHigh, arrivalStartMonth, arrivalStartDay, lifecycle, coreSeasonalIndex);
      return {
        code: s.code,
        description: s.description,
        category: categoriseByCode(s.normCode, s.description),
        soh: s.qty,
        incoming,
        perPo,
        backOrder,
        burnToArrival,
        stockAtArrival,
        simulated: simResult.simulated,
        weeklyLog: simResult.weeklyLog,
        lifecycle,
        monthlyArr: sales.monthlyArr,
        coverInfo,
        demandLow,
        demandHigh,
        ...avgs,
      };
    });

  // Add monthly sales items that aren't in Section 1 or Section 2
  const s2CodeSet = new Set(STATE.results.section2.map(p => normaliseCode(p.code)));
  STATE.monthlySales.items.forEach(item => {
    const nc = item.normCode;
    if (!nc) return;
    if (orderCodeSet.has(nc)) return;
    if (s2CodeSet.has(nc)) return;
    const nd = item.normDesc;
    const alreadyInS1 = STATE.results.section1.some(p => normaliseCode(p.code) === nc || normaliseDesc(p.description) === nd);
    const alreadyInS2 = STATE.results.section2.some(p => normaliseCode(p.code) === nc || normaliseDesc(p.description) === nd);
    if (alreadyInS1 || alreadyInS2) return;
    const sales = findSales(nc, item.description);
    // Use raw averages first for lifecycle detection (same pattern as S1/S2)
    const rawAvgs3 = (sales.monthlyArr && sales.monthlyArr.length >= 1)
      ? { avg12: sales.monthlyArr.slice(-12).reduce(function(s2, m) { return s2 + m.qty; }, 0) / Math.min(sales.monthlyArr.length, 12),
          avg6: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 6)).reduce(function(s2, m) { return s2 + m.qty; }, 0) / Math.min(sales.monthlyArr.length, 6),
          avg3: sales.monthlyArr.slice(-Math.min(sales.monthlyArr.length, 3)).reduce(function(s2, m) { return s2 + m.qty; }, 0) / Math.min(sales.monthlyArr.length, 3) }
      : { avg12: sales.s12 > 0 ? sales.s12 / 12 : 0, avg6: sales.s6 > 0 ? sales.s6 / 6 : 0, avg3: sales.s3 > 0 ? sales.s3 / 3 : 0 };
    if (rawAvgs3.avg3 === 0 && rawAvgs3.avg6 === 0 && rawAvgs3.avg12 === 0) return;
    const incoming = incomingByCode[nc] || 0;
    const backOrder = backOrderByCode[nc] || 0;
    const perPo = perPoByCode[nc] || new Array(STATE.pos.length).fill(0);
    const autoLifecycle = detectLifecycle(
      Math.round(rawAvgs3.avg12 * 12), Math.round(rawAvgs3.avg6 * 6), Math.round(rawAvgs3.avg3 * 3),
      0, incoming, sales.sPrior
    );
    const lifecycle = (STATE.lifecycleOverrides && STATE.lifecycleOverrides[nc]) || autoLifecycle;
    // Now compute averages with stockout adjustment for Core products
    const avgs = computeAverages(sales, lifecycle);
    STATE.results.section2.push({
      code: item.code,
      description: item.description,
      category: categoriseByCode(nc, item.description),
      soh: 0,
      incoming,
      perPo,
      backOrder,
      lifecycle,
      monthlyArr: sales.monthlyArr,
      ...avgs,
    });
    s2CodeSet.add(nc);
  });

  // Sort both sections by lifecycle group, then by months of cover ascending (most urgent first)
  const LC_ORDER = { Core: 0, Active: 1, New: 2, 'Selling Through': 3, Archived: 4 };
  function lifecycleSort(a, b) {
    const lcA = LC_ORDER[a.lifecycle] !== undefined ? LC_ORDER[a.lifecycle] : 9;
    const lcB = LC_ORDER[b.lifecycle] !== undefined ? LC_ORDER[b.lifecycle] : 9;
    if (lcA !== lcB) return lcA - lcB;
    // Within same lifecycle, sort by months of cover ascending (lowest cover = most urgent)
    const ciA = a.coverInfo || { cover: 0, coverMethod: 'none' };
    const ciB = b.coverInfo || { cover: 0, coverMethod: 'none' };
    const coverA = ciA.coverMethod === 'none' ? 999 : ciA.cover;
    const coverB = ciB.coverMethod === 'none' ? 999 : ciB.cover;
    return coverA - coverB;
  }
  STATE.results.section1.sort(lifecycleSort);
  STATE.results.section2.sort(lifecycleSort);

  // Also add Section 1/2 product codes and exo codes to matchedSalesKeys
  [...STATE.results.section1, ...STATE.results.section2].forEach(p => {
    const nc = normaliseCode(p.code);
    if (nc) matchedSalesKeys.add(nc);
    if (p.exoCode) matchedSalesKeys.add(normaliseCode(p.exoCode));
    const nd = normaliseDesc(p.description);
    if (nd) matchedSalesKeys.add(nd);
  });

  // Find unmatched sales items
  const stAllByCode = {};
  const allStItems = [...st12, ...st6, ...st3, ...stPrior];
  allStItems.forEach(s => {
    const key = s.normCode || s.normDesc || '';
    if (!key) return;
    if (!stAllByCode[key]) stAllByCode[key] = { code: s.code, description: s.description, normCode: s.normCode, normDesc: s.normDesc, sold12: 0, sold6: 0, sold3: 0, soldPrior: 0 };
  });
  st12.forEach(s => { const key = s.normCode || s.normDesc; if (key && stAllByCode[key]) stAllByCode[key].sold12 = s.sold; });
  st6.forEach(s => { const key = s.normCode || s.normDesc; if (key && stAllByCode[key]) stAllByCode[key].sold6 = s.sold; });
  st3.forEach(s => { const key = s.normCode || s.normDesc; if (key && stAllByCode[key]) stAllByCode[key].sold3 = s.sold; });
  stPrior.forEach(s => { const key = s.normCode || s.normDesc; if (key && stAllByCode[key]) stAllByCode[key].soldPrior = s.sold; });

  STATE.results.unmatchedSales = Object.values(stAllByCode).filter(s => {
    if (s.normCode && matchedSalesKeys.has(s.normCode)) return false;
    if (s.normDesc && matchedSalesKeys.has(s.normDesc)) return false;
    return true;
  }).map(s => ({
    code: s.code || '',
    description: s.description || '',
    s12: Math.round(s.sold12 || 0),
    s6: Math.round(s.sold6 || 0),
    s3: Math.round(s.sold3 || 0),
  })).filter(s => s.s12 > 0 || s.s6 > 0 || s.s3 > 0);

  // Build flags
  STATE.results.flags = [];
  const unmatchedCount = STATE.results.section1.filter(p => p.avg3 === 0 && p.avg6 === 0 && p.avg12 === 0).length;
  if (unmatchedCount > 0) {
    STATE.results.flags.push({ type: 'warn', message: `${unmatchedCount} order form products have no sales data — these may be new items. Order quantities will be 0 unless adjusted manually.` });
  }
  if (STATE.results.section2.length === 0 && sohItems.length > 0) {
    STATE.results.flags.push({ type: 'info', message: 'All SOH items are on the order form — no special production candidates found.' });
  }
  if (st12.length === 0 && st6.length === 0 && st3.length === 0) {
    STATE.results.flags.push({ type: 'warn', message: 'No monthly sales data provided. Velocity calculations will be blank — all order suggestions will be 0.' });
  }
  const totalBORaw = STATE.backOrders.items.reduce((a, b) => a + b.qty, 0);
  const matchedBOCalc = [...STATE.results.section1, ...STATE.results.section2].reduce((a, p) => a + p.backOrder, 0);
  const unmatchedBOCalc = totalBORaw - matchedBOCalc;
  if (totalBORaw > 0) {
    let boMsg = `${matchedBOCalc.toLocaleString()} back order units matched to products and subtracted from projected stock.`;
    if (unmatchedBOCalc > 0) boMsg += ` ${unmatchedBOCalc.toLocaleString()} additional units didn't match any product code (non-nanoblock or unrecognised codes).`;
    STATE.results.flags.push({ type: 'info', message: boMsg });
  }
  if (STATE.results.unmatchedSales && STATE.results.unmatchedSales.length > 0) {
    const sigUnmatched = STATE.results.unmatchedSales.filter(p => (p.avg3||0) >= 5);
    STATE.results.flags.push({ type: sigUnmatched.length > 0 ? 'warn' : 'info',
      message: `${STATE.results.unmatchedSales.length} monthly sales items didn't match any product code${sigUnmatched.length > 0 ? ` (${sigUnmatched.length} with significant sales)` : ''}. See "Unmatched Sales Items" section.` });
  }
  const highSalesLowStock = STATE.results.section1.filter(p => {
    const wm = p.avg3 || 0;
    return wm > 50 && (p.soh - p.backOrder) < wm;
  });
  if (highSalesLowStock.length > 0) {
    STATE.results.flags.push({ type: 'warn', message: `${highSalesLowStock.length} high-velocity products have less than 1 month of stock: ${highSalesLowStock.slice(0,3).map(p=>p.code).join(', ')}${highSalesLowStock.length > 3 ? '...' : ''}` });
  }
}

function updateGenSummary() {
  const s1 = STATE.results.section1;
  const s2 = STATE.results.section2;
  const runNum = STATE.config.runNumber;
  document.getElementById('gen-title').textContent = `Ready to generate Run ${runNum} Order Calculator`;
  document.getElementById('gen-subtitle').textContent =
    `${s1.length} order form products + ${s2.length} special production items. Coverage: ${STATE.config.lowCoverage}–${STATE.config.highCoverage} months.`;

  // Build pre-generate checklist
  const hasSalesDta = STATE.monthlySales.items && STATE.monthlySales.items.length > 0;
  const s1Matched = s1.filter(p => p.avg3 > 0 || p.avg6 > 0 || p.avg12 > 0).length;
  const s1Unmatched = s1.length - s1Matched;
  const totalIncoming = STATE.pos.reduce((a, po) => a + po.items.reduce((b, it) => b + it.qty, 0), 0);
  const totalBO = STATE.backOrders.items.length;

  const checks = [
    { label: 'Order Form', icon: STATE.orderForm.products.length > 0 ? 'ok' : 'miss',
      detail: STATE.orderForm.products.length > 0 ? `${STATE.orderForm.products.length} products` : 'No data — cannot generate' },
    { label: 'Stock on Hand', icon: STATE.soh.items.length > 0 ? 'ok' : 'miss',
      detail: STATE.soh.items.length > 0 ? `${STATE.soh.items.length} items` : 'No data — SOH will be 0' },
    { label: 'Sales Data (Monthly)', icon: hasSalesDta ? 'ok' : 'warn',
      detail: hasSalesDta ? `${s1Matched} of ${s1.length} matched (${s1Unmatched} unmatched)` : 'No sales data — all order suggestions will be 0' },
    { label: 'Purchase Orders', icon: STATE.pos.length > 0 ? 'ok' : 'warn',
      detail: STATE.pos.length > 0 ? `${STATE.pos.length} PO(s): ${STATE.pos.map(p => p.name).join(', ')} — ${totalIncoming.toLocaleString()} units` : 'None — incoming stock will be 0' },
    { label: 'Back Orders', icon: totalBO > 0 ? 'ok' : 'warn',
      detail: totalBO > 0 ? `${totalBO} lines` : 'None — back orders will be 0' },
    { label: 'Coverage Targets', icon: 'ok',
      detail: `LOW: ${STATE.config.lowCoverage} months, HIGH: ${STATE.config.highCoverage} months` },
  ];

  const iconMap = { ok: '✓', warn: '—', miss: '✗' };
  let checkHtml = '<div class="gen-checklist">';
  checks.forEach(c => {
    checkHtml += `<div class="gen-check-item"><div class="gen-check-icon ${c.icon}">${iconMap[c.icon]}</div><span>${c.label}</span><span class="gen-check-detail">${c.detail}</span></div>`;
  });
  checkHtml += '</div>';

  // Insert checklist before button
  let existing = document.getElementById('gen-checklist-container');
  if (!existing) {
    existing = document.createElement('div');
    existing.id = 'gen-checklist-container';
    document.getElementById('gen-btn').parentNode.insertBefore(existing, document.getElementById('gen-btn'));
  }
  existing.innerHTML = checkHtml;

  // Disable generate if no order form data
  const btn = document.getElementById('gen-btn');
  if (STATE.orderForm.products.length === 0) {
    btn.disabled = true;
    btn.textContent = 'Cannot Generate — No Order Form Data';
  } else {
    btn.disabled = false;
    btn.textContent = 'Generate & Download .xlsx';
  }
}

// ============================================================
// XLSX GENERATION
// ============================================================
async function generateXlsx() {
  const overlay = document.getElementById('genOverlay');
  overlay.classList.add('active');
  document.getElementById('gen-status').textContent = 'Building spreadsheet...';

  // Small delay to let overlay render
  await new Promise(r => setTimeout(r, 100));

  try {
    const wb = new ExcelJS.Workbook();
    const runNum = STATE.config.runNumber;
    const low = STATE.config.lowCoverage;
    const high = STATE.config.highCoverage;

    // Burn period: months from today until warehouse arrival
    const genToday = new Date();
    const burnMonths = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
      ? Math.max(0, monthsBetween(genToday, STATE.schedule.currentRun.arrivalDate))
      : 0;
    const arrivalStr = STATE.schedule.currentRun ? STATE.schedule.currentRun.arrivalStr : '?';

    // Apply any lifecycle overrides to product data before sorting
    // This ensures overrides from Review step and localStorage are reflected
    if (STATE.lifecycleOverrides) {
      [...STATE.results.section1, ...STATE.results.section2].forEach(p => {
        const nc = normaliseCode(p.code);
        if (STATE.lifecycleOverrides[nc]) p.lifecycle = STATE.lifecycleOverrides[nc];
      });
    }

    // Re-sort by lifecycle (Core → Active → New → Selling Through → Archived) then months of cover ascending
    const GEN_LC_ORDER = { Core: 0, Active: 1, New: 2, 'Selling Through': 3, Archived: 4 };
    function genLifecycleSort(a, b) {
      const lcA = GEN_LC_ORDER[a.lifecycle] !== undefined ? GEN_LC_ORDER[a.lifecycle] : 9;
      const lcB = GEN_LC_ORDER[b.lifecycle] !== undefined ? GEN_LC_ORDER[b.lifecycle] : 9;
      if (lcA !== lcB) return lcA - lcB;
      // Within same lifecycle, sort by months of cover ascending (most urgent first)
      const ciA = a.coverInfo || { cover: 0, coverMethod: 'none' };
      const ciB = b.coverInfo || { cover: 0, coverMethod: 'none' };
      const coverA = ciA.coverMethod === 'none' ? 999 : ciA.cover;
      const coverB = ciB.coverMethod === 'none' ? 999 : ciB.cover;
      return coverA - coverB;
    }
    STATE.results.section1.sort(genLifecycleSort);
    STATE.results.section2.sort(genLifecycleSort);

    const s1 = STATE.results.section1;
    const s2 = STATE.results.section2;
    const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

    // Styles
    const blueFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } };
    const blueFont = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    const subFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } };
    const inputFont = { name: 'Arial', color: { argb: 'FF0000FF' }, size: 9 };
    const formulaFont = { name: 'Arial', color: { argb: 'FF000000' }, size: 9 };
    const greenFont = { name: 'Arial', color: { argb: 'FF008000' }, size: 9 };
    const yellowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
    const ltGreenFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
    const ltRedFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } };
    const ltYellowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
    const ltBlueFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDAEEF3' } };
    const purpleFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B2D8E' } };
    const ltPurpleFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8D5F0' } };
    const purpleRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5EEF8' } };
    const purpleAltFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE3F2' } };
    const thinBorder = {
      top: { style: 'thin', color: { argb: 'FFB4C6E7' } },
      bottom: { style: 'thin', color: { argb: 'FFB4C6E7' } },
      left: { style: 'thin', color: { argb: 'FFB4C6E7' } },
      right: { style: 'thin', color: { argb: 'FFB4C6E7' } },
    };

    // ---- SHEET 1: ORDER CALCULATOR ----
    const ws = wb.addWorksheet('Order Calculator');

    // Dynamic column layout based on number of POs
    const numPOs = STATE.pos.length;
    const poNames = STATE.pos.map(po => po.name);
    // Layout: Code, Exo, Desc, Qty/Ctn, UnitPrice, Category, Lifecycle, Flags, Avg12, Avg6, Avg3,
    //   UnitsSoldLow, UnitsSoldHigh, SOH, [PO cols], Incoming, BO, Proj, Burn, StockAtArrival, Cover,
    //   LOW, HIGH, ValueLOW, ValueHIGH
    const PO = numPOs;
    function colLetter(colNum) {
      let s = '';
      while (colNum > 0) { colNum--; s = String.fromCharCode(65 + (colNum % 26)) + s; colNum = Math.floor(colNum / 26); }
      return s;
    }
    const COL_QTYCTN = 4;   // Qty/Ctn
    const COL_PRICE = 5;    // Unit Price — next to Qty/Ctn
    const COL_FLAGS = 8;    // Flags (between Lifecycle and Avg cols)
    const COL_AVG12 = 9;    // AVG Units/Month 12 Months
    const COL_AVG6 = 10;    // AVG Units/Month 6 Months
    const COL_AVG3 = 11;    // AVG Units/Month 3 Months
    const COL_SPLY_LOW = 12;  // Units Sold For Low Cover
    const COL_SPLY_HIGH = 13; // Units Sold For High Cover
    const COL_SOH = 14;
    // PO cols: 15 .. 14+PO
    const COL_PO_START = COL_SOH + 1; // First per-PO column = 15
    const COL_INCOMING = 15 + PO; // Consolidated single column
    const COL_BO = 16 + PO;      // Back Orders
    const COL_PROJ = 17 + PO;
    const COL_BURN = 18 + PO;       // Est. Sales to Arrival
    const COL_STARR = 19 + PO;      // Stock at Arrival
    const COL_COVER = 20 + PO;
    const COL_LOW = 21 + PO;
    const COL_HIGH = 22 + PO;
    const COL_MANUAL = 23 + PO;    // Manual Order Qty (user input)
    const COL_VAL_LOW = 24 + PO;
    const COL_VAL_HIGH = 25 + PO;
    const COL_VAL_MANUAL = 26 + PO; // Manual Order Value = Manual Qty x Unit Price
    const TOTAL_COLS = 26 + PO;
    const LET_FLAGS = colLetter(COL_FLAGS);
    const LET_AVG12 = colLetter(COL_AVG12);
    const LET_AVG6 = colLetter(COL_AVG6);
    const LET_AVG3 = colLetter(COL_AVG3);
    const LET_SPLY_LOW = colLetter(COL_SPLY_LOW);
    const LET_SPLY_HIGH = colLetter(COL_SPLY_HIGH);
    const LET_SOH = colLetter(COL_SOH);
    const LET_INCOMING = colLetter(COL_INCOMING);
    const LET_PROJ = colLetter(COL_PROJ);
    const LET_BURN = colLetter(COL_BURN);
    const LET_STARR = colLetter(COL_STARR);
    const LET_COVER = colLetter(COL_COVER);
    const LET_BO = colLetter(COL_BO);
    const LET_LOW = colLetter(COL_LOW);
    const LET_HIGH = colLetter(COL_HIGH);
    const LET_MANUAL = colLetter(COL_MANUAL);
    const LET_PRICE = colLetter(COL_PRICE);
    const LET_VAL_LOW = colLetter(COL_VAL_LOW);
    const LET_VAL_HIGH = colLetter(COL_VAL_HIGH);
    const LET_VAL_MANUAL = colLetter(COL_VAL_MANUAL);
    const LET_LAST = colLetter(TOTAL_COLS);

    // Title rows (no merged cells — allows easy column hiding)
    const titleCell = ws.getCell('A1');
    titleCell.value = `KAWADA AUSTRALIA \u2014 Run ${runNum} Order Calculator`;
    titleCell.font = { name: 'Arial', bold: true, size: 14, color: { argb: 'FF2F5496' } };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
    ws.getRow(1).height = 30;

    const sohDateStr = STATE.config.sohDate ? (() => { const d = new Date(STATE.config.sohDate); return ` | SOH as at: ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })() : '';
    ws.getCell('A2').value = `Generated: ${today}${sohDateStr} | MOQ: 3,000 pcs/model | Min Order: US$28,000`;
    ws.getCell('A2').font = { name: 'Arial', size: 10, color: { argb: 'FFC00000' }, bold: true };

    // Assumptions
    ws.getCell('A3').value = 'ASSUMPTIONS:';
    ws.getCell('A3').font = { name: 'Arial', bold: true, size: 9 };
    ws.getCell('B3').value = 'Low Coverage (months):';
    ws.getCell('B3').font = { name: 'Arial', size: 9 };
    ws.getCell('C3').value = low;
    ws.getCell('C3').font = inputFont;
    ws.getCell('C3').fill = yellowFill;
    ws.getCell('D3').value = 'High Coverage (months):';
    ws.getCell('D3').font = { name: 'Arial', size: 9 };
    ws.getCell('E3').value = high;
    ws.getCell('E3').font = inputFont;
    ws.getCell('E3').fill = yellowFill;
    ws.getCell('F3').value = 'Transit (weeks):';
    ws.getCell('F3').font = { name: 'Arial', size: 9 };
    ws.getCell('G3').value = 4;
    ws.getCell('G3').font = { name: 'Arial', size: 9, color: { argb: 'FF666666' } };

    // Get SPLY month labels from first product that has them
    const splyLowLabel = s1.find(p => p.splyLowMonthNames)?.splyLowMonthNames || '';
    const splyHighLabel = s1.find(p => p.splyHighMonthNames)?.splyHighMonthNames || '';

    // Section 1 banner (no merge — fill each cell for column hiding support)
    ws.getCell('A4').value = `\u25bc  SECTION 1: RUN ${runNum} ORDER FORM (${s1.length} products available to order this run)`;
    ws.getCell('A4').font = { name: 'Arial', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    ws.getCell('A4').fill = blueFill;
    ws.getCell('A4').alignment = { horizontal: 'left', vertical: 'middle' };
    ws.getRow(4).height = 26;
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(4).getCell(c).fill = blueFill;

    // Headers row 5
    const currentRunLabel = STATE.schedule.currentRun ? STATE.schedule.currentRun.run : `Run ${runNum}`;
    const nextRunLabel = STATE.schedule.nextRun ? STATE.schedule.nextRun.run : `Run ${runNum + 1}`;
    const runAfterNextLabel = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : `Run ${runNum + 2}`;
    const baseHeaders = ['Product\nCode','Exo\nCode','Description','Qty/\nCtn','Unit Price\n(US$)','Category','Product\nLifecycle\nCategory',
      'Flags','AVG Units/Month\n12 Months','AVG Units/Month\n6 Months','AVG Units/Month\n3 Months',
      `Units Sold Previous Year\nFor LOW Order Cover\n${splyLowLabel || '?'}`,`Units Sold Previous Year\nFor HIGH Order Cover\n${splyHighLabel || '?'}`,
      'Current\nSOH'];
    const poHeaders = poNames.map(n => `Incoming\n${n}`);
    const burnLabel = burnMonths > 0 ? `Est. Sales\nNow to Arrival\n(${burnMonths.toFixed(1)} mo)` : 'Est. Sales\nto Arrival';
    const tailHeaders = ['Incoming\n(Open POs)','Back\nOrders','Projected\nStock',burnLabel,`Stock at\nArrival\n(${arrivalStr})`,'Months of\nCover',
      `Order Qty LOW\nCover ${currentRunLabel}\nto ${nextRunLabel}`,`Order Qty HIGH\nCover ${currentRunLabel}\nto ${runAfterNextLabel}`,'Manual\nOrder Qty',`Order Value\nLOW (US$)`,`Order Value\nHIGH (US$)`,'Manual Order\nValue (US$)'];
    const headers = [...baseHeaders, ...poHeaders, ...tailHeaders];
    ws.getRow(5).height = 60;
    // Column group color fills for headers
    const grpProductFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } }; // Dark blue: A-F
    const grpSalesFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF548235' } }; // Green: G-K
    const grpStockFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B2D8E' } }; // Purple: L-R (stock/inventory)
    const grpOrderFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBF8F00' } }; // Gold: S-X (order decisions)
    function headerFillForCol(colNum) {
      if (colNum <= 8) return grpProductFill;       // A-H (Code, Exo, Desc, Qty/Ctn, Price, Category, Lifecycle, Flags)
      if (colNum <= 13) return grpSalesFill;         // I-M (Avg12, Avg6, Avg3, SPLY Low, SPLY High)
      if (colNum <= COL_COVER) return grpStockFill;  // N through Cover (stock/inventory group)
      return grpOrderFill;                           // Rest (order decisions)
    }
    headers.forEach((h, i) => {
      const cell = ws.getRow(5).getCell(i + 1);
      cell.value = h;
      cell.font = blueFont;
      cell.fill = headerFillForCol(i + 1);
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = thinBorder;
    });

    // Header tooltips — hover over any header to see how it's calculated
    const headerTooltips = {
      1: 'Product code from the Kawada Japan order form.',
      2: 'Exo stock code — auto-mapped from product code (underscores instead of hyphens).',
      3: 'Product description from the order form.',
      4: 'Product category (series/type), auto-detected from product code prefix.',
      5: 'Lifecycle stage: Core, Active, New, Selling Through, or Archived.\nAuto-detected from sales patterns, can be overridden in the Review step.',
      [COL_FLAGS]: 'Flags highlight items needing attention:\n• GROWING = sales trending up YoY\n• DECLINING = sales trending down\n• SPIKE = one abnormally large month\n• SHORT HISTORY = less than 6 months data\n• NEW = 3 months or less of data\n• STOCKOUT = Core product had months below 75% of median (data adjusted)',
      [COL_AVG12]: 'Average units sold per month over the last 12 months.\nCalculated: total units sold in last 12 months ÷ 12.',
      [COL_AVG6]: 'Average units sold per month over the last 6 months.\nCalculated: total units sold in last 6 months ÷ 6.',
      [COL_AVG3]: 'Average units sold per month over the last 3 months.\nCalculated: total units sold in last 3 months ÷ 3.\nUseful for spotting recent trends.',
      [COL_SPLY_LOW]: `Units sold in the same calendar months last year that match the LOW coverage window.\nStarts from the warehouse arrival day (${arrivalStr}) and looks ${low} months forward, then checks last year's sales for those same months.\nThe first and last months are pro-rated based on the actual arrival day.`,
      [COL_SPLY_HIGH]: `Units sold in the same calendar months last year that match the HIGH coverage window.\nStarts from the warehouse arrival day (${arrivalStr}) and looks ${high} months forward, then checks last year's sales for those same months.\nThe first and last months are pro-rated based on the actual arrival day.`,
      [COL_SOH]: 'Current Stock on Hand — from your SOH export.',
      [COL_INCOMING]: 'Total incoming units from all open Purchase Orders.\nFormula: sum of all individual PO quantities for this product.',
      [COL_BO]: 'Back Orders — units already committed to customers but not yet shipped.\nThese are subtracted from available stock.',
      [COL_PROJ]: 'Projected Stock = SOH + Incoming - Back Orders.\nThis is your stock position TODAY, before accounting for sales between now and order arrival.',
      [COL_BURN]: `Estimated net stock change between now and when ${currentRunLabel} arrives in warehouse (${arrivalStr}).\nWhen weekly simulation is active: accounts for seasonal sales AND timing of each incoming PO arrival.\nOtherwise: seasonal sales patterns over ${burnMonths > 0 ? burnMonths.toFixed(1) : '?'} months.`,
      [COL_STARR]: `Stock at Arrival — estimated stock when ${currentRunLabel} lands in warehouse (${arrivalStr}).\nUses weekly simulation: walks forward from today, subtracting seasonal weekly sales and adding each incoming PO's stock at its scheduled arrival date.\nAccounts for the timing of when each PO actually arrives rather than lumping all incoming together.`,
      [COL_COVER]: `Months of Cover — how many months your Stock at Arrival will last.\n13+ months data: seasonal depletion (walks month-by-month using last year's calendar months from arrival date).\n<13 months data: Stock at Arrival ÷ average monthly sales (marked with * in the cell).`,
      [COL_LOW]: `Order Qty LOW — units needed to cover demand from ${currentRunLabel} arrival until ${nextRunLabel} arrival (${low} months).\nFormula: expected seasonal demand over ${low} months - Stock at Arrival.\nRounded up to full cartons (Qty/Ctn).`,
      [COL_HIGH]: `Order Qty HIGH — units needed to cover demand from ${currentRunLabel} arrival until ${runAfterNextLabel} arrival (${high} months).\nFormula: expected seasonal demand over ${high} months - Stock at Arrival.\nRounded up to full cartons (Qty/Ctn).`,
      [COL_MANUAL]: 'Manual Order Qty — enter your actual order quantity here.\nUse LOW and HIGH as guides, then decide your final order amount.\nManual Order Value will calculate automatically.',
      [COL_VAL_MANUAL]: 'Manual Order Value = Manual Order Qty × Unit Price.\nYour actual order cost based on the quantity you entered.',
      [COL_PRICE]: 'Unit price in US$ — from the Kawada Japan order form.',
      [COL_VAL_LOW]: 'Order Value LOW = Order Qty LOW × Unit Price.\nTotal cost if ordering the LOW scenario quantity.',
      [COL_VAL_HIGH]: 'Order Value HIGH = Order Qty HIGH × Unit Price.\nTotal cost if ordering the HIGH scenario quantity.',
    };
    // Add per-PO column tooltips
    for (let pi = 0; pi < numPOs; pi++) {
      headerTooltips[COL_PO_START + pi] = `Units incoming on ${poNames[pi] || 'PO ' + (pi + 1)}.\nFrom your Purchase Order data.`;
    }
    // Apply tooltips to S1 header row
    Object.keys(headerTooltips).forEach(col => {
      const c = parseInt(col);
      if (c >= 1 && c <= TOTAL_COLS) {
        ws.getRow(5).getCell(c).note = headerTooltips[c];
      }
    });

    // Source labels row 6
    const baseSrcLabels = ['Order Form','AUTO MAP','Order Form','Order Form','Order Form','MANUAL','CALCULATED',
      'CALCULATED','Monthly Sales','Monthly Sales','Monthly Sales','Monthly Sales','Monthly Sales','SOH'];
    const poSrcLabels = poNames.map(n => 'PO');
    const tailSrcLabels = ['FORMULA','Back Orders','FORMULA','CALCULATED','CALCULATED','CALCULATED','FORMULA','FORMULA','YOUR INPUT','FORMULA','FORMULA','FORMULA'];
    const srcLabels = [...baseSrcLabels, ...poSrcLabels, ...tailSrcLabels];
    srcLabels.forEach((s, i) => {
      const cell = ws.getRow(6).getCell(i + 1);
      cell.value = s;
      cell.font = { name: 'Arial', italic: true, size: 7, color: { argb: 'FF666666' } };
      cell.fill = subFill;
      cell.alignment = { horizontal: 'center' };
      cell.border = thinBorder;
    });

    // Data rows starting at row 7 — lifecycle-tinted backgrounds
    const DATA_START = 7;
    // Lifecycle background tints (alternating light/lighter per group)
    const LC_ROW_FILLS = {
      Core:             [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDF2FA' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }],
      Active:           [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2F1' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }],
      New:              [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8F9' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }],
      'Selling Through':[{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E7' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }],
      Archived:         [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE8EC' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } }],
    };
    const LC_LIFECYCLE_FONTS = {
      Core:             { name: 'Arial', bold: true, size: 9, color: { argb: 'FF1565C0' } },
      Active:           { name: 'Arial', bold: true, size: 9, color: { argb: 'FF00796B' } },
      New:              { name: 'Arial', bold: true, size: 9, color: { argb: 'FF6A1B9A' } },
      'Selling Through':{ name: 'Arial', bold: true, size: 9, color: { argb: 'FFE65100' } },
      Archived:         { name: 'Arial', bold: true, size: 9, color: { argb: 'FFC62828' } },
    };
    let lcCounter = {}; // track row index within each lifecycle group
    s1.forEach((p, i) => {
      const row = DATA_START + i;
      const r = ws.getRow(row);
      // Lifecycle-tinted alternating background
      const lcIdx = lcCounter[p.lifecycle] = (lcCounter[p.lifecycle] || 0);
      lcCounter[p.lifecycle]++;
      const fills = LC_ROW_FILLS[p.lifecycle] || LC_ROW_FILLS.Core;
      const rowBg = fills[lcIdx % 2];
      for (let cc = 1; cc <= TOTAL_COLS; cc++) r.getCell(cc).fill = rowBg;
      const isNoOrder = (p.lifecycle === 'Archived');
      const greyFont = { ...inputFont, color: { argb: 'FF999999' } };

      // A-G: Code, Exo, Desc, Qty/Ctn, Unit Price, Category, Lifecycle
      [p.code, p.exoCode, p.description, p.qtyCtn || '', p.price || '', p.category, p.lifecycle].forEach((v, j) => {
        const c = r.getCell(j + 1);
        c.value = v;
        if (j === 6) { // Lifecycle column — colour-coded
          c.font = LC_LIFECYCLE_FONTS[p.lifecycle] || inputFont;
        } else if (j === 3) { // Qty/Ctn — numeric
          c.font = isNoOrder ? greyFont : inputFont;
          c.numFmt = '#,##0';
        } else if (j === 4) { // Unit Price — currency
          c.font = isNoOrder ? greyFont : inputFont;
          c.numFmt = '$#,##0.00';
        } else {
          c.font = isNoOrder ? greyFont : inputFont;
        }
        c.border = thinBorder;
      });

      // Flags (col 6)
      r.getCell(COL_FLAGS).value = p.flags || '';
      r.getCell(COL_FLAGS).font = { name: 'Arial', size: 8, color: { argb: 'FFE65100' } }; r.getCell(COL_FLAGS).border = thinBorder;
      r.getCell(COL_FLAGS).alignment = { horizontal: 'center', wrapText: true };

      // Avg Monthly columns (12mo, 6mo, 3mo — descending period)
      r.getCell(COL_AVG12).value = Math.round(p.avg12); r.getCell(COL_AVG12).font = greenFont; r.getCell(COL_AVG12).border = thinBorder; r.getCell(COL_AVG12).numFmt = '#,##0';
      r.getCell(COL_AVG6).value = Math.round(p.avg6); r.getCell(COL_AVG6).font = greenFont; r.getCell(COL_AVG6).border = thinBorder; r.getCell(COL_AVG6).numFmt = '#,##0';
      r.getCell(COL_AVG3).value = Math.round(p.avg3); r.getCell(COL_AVG3).font = { name: 'Arial', bold: true, size: 9 }; r.getCell(COL_AVG3).border = thinBorder; r.getCell(COL_AVG3).numFmt = '#,##0';

      // Units Sold For Low/High Cover
      r.getCell(COL_SPLY_LOW).value = Math.round(p.samePeriodLY_low || 0);
      r.getCell(COL_SPLY_LOW).font = { name: 'Arial', color: { argb: 'FF9E9E9E' }, size: 9 }; r.getCell(COL_SPLY_LOW).border = thinBorder; r.getCell(COL_SPLY_LOW).numFmt = '#,##0';
      r.getCell(COL_SPLY_HIGH).value = Math.round(p.samePeriodLY_high || 0);
      r.getCell(COL_SPLY_HIGH).font = { name: 'Arial', color: { argb: 'FF9E9E9E' }, size: 9 }; r.getCell(COL_SPLY_HIGH).border = thinBorder; r.getCell(COL_SPLY_HIGH).numFmt = '#,##0';

      // SOH
      r.getCell(COL_SOH).value = p.soh; r.getCell(COL_SOH).font = greenFont; r.getCell(COL_SOH).border = thinBorder; r.getCell(COL_SOH).numFmt = '#,##0';

      // Per-PO columns (cols 13 through 12+PO)
      for (let pi = 0; pi < numPOs; pi++) {
        const c = r.getCell(COL_PO_START + pi);
        c.value = p.perPo[pi] || 0;
        c.font = { name: 'Arial', size: 8, color: { argb: 'FF888888' } };
        c.border = thinBorder; c.numFmt = '#,##0';
      }

      // Incoming (Open POs) — consolidated SUM of per-PO cols
      if (numPOs > 0) {
        const firstPoLet = colLetter(COL_PO_START);
        const lastPoLet = colLetter(COL_PO_START + numPOs - 1);
        r.getCell(COL_INCOMING).value = { formula: `SUM(${firstPoLet}${row}:${lastPoLet}${row})` };
      } else {
        r.getCell(COL_INCOMING).value = p.incoming;
      }
      r.getCell(COL_INCOMING).font = greenFont; r.getCell(COL_INCOMING).border = thinBorder; r.getCell(COL_INCOMING).numFmt = '#,##0';

      // Back Orders (now between Incoming and Projected)
      r.getCell(COL_BO).value = p.backOrder;
      r.getCell(COL_BO).font = { name: 'Arial', color: { argb: 'FFE65100' }, size: 9 }; r.getCell(COL_BO).border = thinBorder; r.getCell(COL_BO).numFmt = '#,##0';

      // Projected (SOH + Incoming - Back Orders)
      r.getCell(COL_PROJ).value = { formula: `${LET_SOH}${row}+${LET_INCOMING}${row}-${LET_BO}${row}` };
      r.getCell(COL_PROJ).font = formulaFont; r.getCell(COL_PROJ).border = thinBorder; r.getCell(COL_PROJ).numFmt = '#,##0';

      // Est. Sales to Arrival (burn between now and warehouse arrival)
      r.getCell(COL_BURN).value = p.burnToArrival || 0;
      r.getCell(COL_BURN).font = { name: 'Arial', color: { argb: 'FFE65100' }, size: 9 }; r.getCell(COL_BURN).border = thinBorder; r.getCell(COL_BURN).numFmt = '#,##0';

      // Stock at Arrival — use pre-calculated simulation value (accounts for PO arrival timing)
      if (p.simulated) {
        r.getCell(COL_STARR).value = p.stockAtArrival || 0;
      } else {
        r.getCell(COL_STARR).value = { formula: `MAX(0,${LET_PROJ}${row}-${LET_BURN}${row})` };
      }
      r.getCell(COL_STARR).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF1565C0' } }; r.getCell(COL_STARR).border = thinBorder; r.getCell(COL_STARR).numFmt = '#,##0';

      // Months of Cover — pre-calculated using seasonal depletion (13+ months) or simple avg (fewer months)
      const ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
      if (ci.coverMethod === 'none' && p.lifecycle === 'New') {
        r.getCell(COL_COVER).value = 'NEW';
        r.getCell(COL_COVER).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF6A1B9A' } };
      } else if (ci.coverMethod === 'none') {
        r.getCell(COL_COVER).value = 'N/A';
        r.getCell(COL_COVER).font = { name: 'Arial', size: 9, color: { argb: 'FF999999' } };
      } else {
        r.getCell(COL_COVER).value = ci.cover;
        r.getCell(COL_COVER).font = formulaFont;
        if (ci.dataMonths < 13 && ci.dataMonths > 0) {
          r.getCell(COL_COVER).note = `Based on ${ci.dataMonths} month${ci.dataMonths === 1 ? '' : 's'} of data (simple average). Less reliable than seasonal calc.`;
        }
      }
      r.getCell(COL_COVER).border = thinBorder; r.getCell(COL_COVER).numFmt = '0.0';

      // Order LOW, Order HIGH — uses seasonal demand projection
      // demandLow/High = total units expected over LOW/HIGH coverage months from arrival date
      // stockAtArrival = projected stock minus expected sales between now and arrival
      const effStock = p.stockAtArrival !== undefined ? p.stockAtArrival : (p.soh + p.incoming - p.backOrder);
      if (isNoOrder) {
        r.getCell(COL_LOW).value = 0;
        r.getCell(COL_HIGH).value = 0;
      } else {
        const rawLow = Math.max(0, (p.demandLow || 0) - effStock);
        const rawHigh = Math.max(0, (p.demandHigh || 0) - effStock);
        const qtyCtn = p.qtyCtn || 0;
        r.getCell(COL_LOW).value = qtyCtn > 0 ? Math.ceil(rawLow / qtyCtn) * qtyCtn : Math.round(rawLow);
        r.getCell(COL_HIGH).value = qtyCtn > 0 ? Math.ceil(rawHigh / qtyCtn) * qtyCtn : Math.round(rawHigh);
      }
      r.getCell(COL_LOW).font = isNoOrder ? { ...formulaFont, color: { argb: 'FF999999' } } : formulaFont; r.getCell(COL_LOW).border = thinBorder; r.getCell(COL_LOW).numFmt = '#,##0'; r.getCell(COL_LOW).fill = ltBlueFill;
      r.getCell(COL_HIGH).font = isNoOrder ? { ...formulaFont, color: { argb: 'FF999999' } } : formulaFont; r.getCell(COL_HIGH).border = thinBorder; r.getCell(COL_HIGH).numFmt = '#,##0'; r.getCell(COL_HIGH).fill = ltBlueFill;

      // Manual Order Qty — empty cell for user input, light green background
      r.getCell(COL_MANUAL).value = null;
      r.getCell(COL_MANUAL).font = { name: 'Arial', bold: true, size: 10 }; r.getCell(COL_MANUAL).border = thinBorder; r.getCell(COL_MANUAL).numFmt = '#,##0';
      r.getCell(COL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };

      // Value LOW and Value HIGH (Price is in col 5)
      r.getCell(COL_VAL_LOW).value = { formula: `${LET_LOW}${row}*${LET_PRICE}${row}` };
      r.getCell(COL_VAL_LOW).font = formulaFont; r.getCell(COL_VAL_LOW).border = thinBorder; r.getCell(COL_VAL_LOW).numFmt = '$#,##0.00'; r.getCell(COL_VAL_LOW).fill = ltYellowFill;
      r.getCell(COL_VAL_HIGH).value = { formula: `${LET_HIGH}${row}*${LET_PRICE}${row}` };
      r.getCell(COL_VAL_HIGH).font = formulaFont; r.getCell(COL_VAL_HIGH).border = thinBorder; r.getCell(COL_VAL_HIGH).numFmt = '$#,##0.00'; r.getCell(COL_VAL_HIGH).fill = ltYellowFill;

      // Manual Order Value = Manual Order Qty × Unit Price
      r.getCell(COL_VAL_MANUAL).value = { formula: `IF(${LET_MANUAL}${row}="","",${LET_MANUAL}${row}*${LET_PRICE}${row})` };
      r.getCell(COL_VAL_MANUAL).font = formulaFont; r.getCell(COL_VAL_MANUAL).border = thinBorder; r.getCell(COL_VAL_MANUAL).numFmt = '$#,##0.00';
      r.getCell(COL_VAL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
    });

    const lastDataRow = DATA_START + s1.length - 1;
    const totalRow = lastDataRow + 2;

    // Totals
    ws.getCell(`A${totalRow}`).value = 'TOTALS';
    ws.getCell(`A${totalRow}`).font = { name: 'Arial', bold: true, size: 10 };
    const totFont = { name: 'Arial', bold: true, size: 10 };
    // SOH total
    ws.getRow(totalRow).getCell(COL_SOH).value = { formula: `SUM(${LET_SOH}${DATA_START}:${LET_SOH}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_SOH).font = totFont; ws.getRow(totalRow).getCell(COL_SOH).numFmt = '#,##0'; ws.getRow(totalRow).getCell(COL_SOH).border = thinBorder;
    // Per-PO totals
    for (let pi = 0; pi < numPOs; pi++) {
      const c = COL_PO_START + pi;
      const cell = ws.getRow(totalRow).getCell(c);
      cell.value = { formula: `SUM(${colLetter(c)}${DATA_START}:${colLetter(c)}${lastDataRow})` };
      cell.font = { name: 'Arial', bold: true, size: 8, color: { argb: 'FF888888' } }; cell.numFmt = '#,##0'; cell.border = thinBorder;
    }
    // Incoming total
    ws.getRow(totalRow).getCell(COL_INCOMING).value = { formula: `SUM(${LET_INCOMING}${DATA_START}:${LET_INCOMING}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_INCOMING).font = totFont; ws.getRow(totalRow).getCell(COL_INCOMING).numFmt = '#,##0'; ws.getRow(totalRow).getCell(COL_INCOMING).border = thinBorder;
    // Back Orders total
    ws.getRow(totalRow).getCell(COL_BO).value = { formula: `SUM(${LET_BO}${DATA_START}:${LET_BO}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_BO).font = { name: 'Arial', bold: true, size: 10, color: { argb: 'FFE65100' } }; ws.getRow(totalRow).getCell(COL_BO).numFmt = '#,##0'; ws.getRow(totalRow).getCell(COL_BO).border = thinBorder;
    // Order LOW, Order HIGH
    [COL_LOW, COL_HIGH].forEach(c => {
      const cell = ws.getRow(totalRow).getCell(c);
      cell.value = { formula: `SUM(${colLetter(c)}${DATA_START}:${colLetter(c)}${lastDataRow})` };
      cell.font = totFont; cell.numFmt = '#,##0'; cell.fill = ltBlueFill; cell.border = thinBorder;
    });
    // Value LOW and Value HIGH
    ws.getRow(totalRow).getCell(COL_VAL_LOW).value = { formula: `SUM(${LET_VAL_LOW}${DATA_START}:${LET_VAL_LOW}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_VAL_LOW).font = totFont; ws.getRow(totalRow).getCell(COL_VAL_LOW).numFmt = '$#,##0.00'; ws.getRow(totalRow).getCell(COL_VAL_LOW).fill = ltYellowFill; ws.getRow(totalRow).getCell(COL_VAL_LOW).border = thinBorder;
    ws.getRow(totalRow).getCell(COL_VAL_HIGH).value = { formula: `SUM(${LET_VAL_HIGH}${DATA_START}:${LET_VAL_HIGH}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_VAL_HIGH).font = totFont; ws.getRow(totalRow).getCell(COL_VAL_HIGH).numFmt = '$#,##0.00'; ws.getRow(totalRow).getCell(COL_VAL_HIGH).fill = ltYellowFill; ws.getRow(totalRow).getCell(COL_VAL_HIGH).border = thinBorder;
    // Manual Order totals
    ws.getRow(totalRow).getCell(COL_MANUAL).value = { formula: `SUM(${LET_MANUAL}${DATA_START}:${LET_MANUAL}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_MANUAL).font = totFont; ws.getRow(totalRow).getCell(COL_MANUAL).numFmt = '#,##0'; ws.getRow(totalRow).getCell(COL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }; ws.getRow(totalRow).getCell(COL_MANUAL).border = thinBorder;
    ws.getRow(totalRow).getCell(COL_VAL_MANUAL).value = { formula: `SUM(${LET_VAL_MANUAL}${DATA_START}:${LET_VAL_MANUAL}${lastDataRow})` };
    ws.getRow(totalRow).getCell(COL_VAL_MANUAL).font = totFont; ws.getRow(totalRow).getCell(COL_VAL_MANUAL).numFmt = '$#,##0.00'; ws.getRow(totalRow).getCell(COL_VAL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }; ws.getRow(totalRow).getCell(COL_VAL_MANUAL).border = thinBorder;
    // "X with sales" count (based on Avg Monthly 3mo > 0)
    ws.getRow(totalRow).getCell(COL_AVG3).value = { formula: `COUNTIF(${LET_AVG3}${DATA_START}:${LET_AVG3}${lastDataRow},">0")` };
    ws.getRow(totalRow).getCell(COL_AVG3).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF666666' } };
    ws.getRow(totalRow).getCell(COL_AVG3).numFmt = '0" with sales"';

    const moqRow = totalRow + 1;
    ws.getCell(`${LET_HIGH}${moqRow}`).value = 'Min Order Check:';
    ws.getCell(`${LET_HIGH}${moqRow}`).font = { name: 'Arial', bold: true, size: 9 };
    ws.getCell(`${LET_VAL_HIGH}${moqRow}`).value = { formula: `IF(${LET_VAL_HIGH}${totalRow}>=28000,"MEETS MINIMUM","BELOW US$28,000")` };
    ws.getCell(`${LET_VAL_HIGH}${moqRow}`).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FFC00000' } };

    // ---- SECTION 2 ----
    // Section 2 does NOT have per-PO columns (products aren't on order form),
    // but column offsets still shift due to PO columns in the layout.
    // S2 uses: cols 1-13 same, COL_INCOMING for incoming total, then COL_BO..COL_HIGH
    // (no Cartons or Value columns since no qty/ctn or price data)
    const dividerRow = moqRow + 3;
    ws.getRow(dividerRow).height = 6;
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(dividerRow).getCell(c).fill = purpleFill;

    const s2TitleRow = dividerRow + 1;
    ws.getCell(`A${s2TitleRow}`).value = `\u25bc  SECTION 2: SPECIAL PRODUCTION REQUESTS \u2014 Products Not on Run ${runNum} Order Form`;
    ws.getCell(`A${s2TitleRow}`).font = { name: 'Arial', bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    ws.getCell(`A${s2TitleRow}`).fill = purpleFill;
    ws.getCell(`A${s2TitleRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    ws.getRow(s2TitleRow).height = 36;
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(s2TitleRow).getCell(c).fill = purpleFill;

    const s2SubRow = s2TitleRow + 1;
    ws.getCell(`A${s2SubRow}`).value = `${s2.length} products in stock but not on order form. Review for special production requests.`;
    ws.getCell(`A${s2SubRow}`).font = { name: 'Arial', size: 10, color: { argb: 'FF7B2D8E' }, bold: true };
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(s2SubRow).getCell(c).fill = ltPurpleFill;

    const s2NoteRow = s2SubRow + 1;
    ws.getCell(`A${s2NoteRow}`).value = 'Same coverage assumptions from C3/E3. No Qty/Ctn or Unit Price available (not on order form). Order quantities shown in units only.';
    ws.getCell(`A${s2NoteRow}`).font = { name: 'Arial', italic: true, size: 8, color: { argb: 'FF666666' } };
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(s2NoteRow).getCell(c).fill = ltPurpleFill;

    // Section 2 headers — same column layout as S1 but no PO breakdown, no Qty/Ctn, Price, Ctns, Value
    const s2HeaderRow = s2NoteRow + 1;
    // Build S2 headers matching S1 layout but blank out unused columns
    const s2Headers = new Array(TOTAL_COLS).fill('');
    s2Headers[0] = 'Product\nCode';
    // col 2 (Exo) blank for S2
    s2Headers[2] = 'Description';
    // col 4 (Qty/Ctn) blank for S2
    // col 5 (Price) blank for S2
    s2Headers[5] = 'Category';
    s2Headers[6] = 'Product\nLifecycle\nCategory';
    s2Headers[COL_FLAGS - 1] = 'Flags';
    s2Headers[COL_AVG12 - 1] = 'AVG Units/Month\n12 Months';
    s2Headers[COL_AVG6 - 1] = 'AVG Units/Month\n6 Months';
    s2Headers[COL_AVG3 - 1] = 'AVG Units/Month\n3 Months';
    s2Headers[COL_SPLY_LOW - 1] = `Units Sold Previous Year\nFor LOW Order Cover\n${splyLowLabel || '?'}`;
    s2Headers[COL_SPLY_HIGH - 1] = `Units Sold Previous Year\nFor HIGH Order Cover\n${splyHighLabel || '?'}`;
    s2Headers[COL_SOH - 1] = 'Current\nSOH';
    s2Headers[COL_INCOMING - 1] = 'Incoming\n(Open POs)';
    s2Headers[COL_BO - 1] = 'Back\nOrders';
    s2Headers[COL_PROJ - 1] = 'Projected\nStock';
    s2Headers[COL_BURN - 1] = burnLabel;
    s2Headers[COL_STARR - 1] = `Stock at\nArrival\n(${arrivalStr})`;
    s2Headers[COL_COVER - 1] = 'Months of\nCover';
    s2Headers[COL_LOW - 1] = `Order Qty LOW\nCover ${currentRunLabel}\nto ${nextRunLabel}`;
    s2Headers[COL_HIGH - 1] = `Order Qty HIGH\nCover ${currentRunLabel}\nto ${runAfterNextLabel}`;
    s2Headers[COL_MANUAL - 1] = 'Manual\nOrder Qty';
    ws.getRow(s2HeaderRow).height = 60;
    s2Headers.forEach((h, i) => {
      const cell = ws.getRow(s2HeaderRow).getCell(i + 1);
      cell.fill = h ? headerFillForCol(i + 1) : purpleFill;
      cell.border = thinBorder;
      if (h) {
        cell.value = h;
        cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        // Add tooltip if available
        if (headerTooltips[i + 1]) cell.note = headerTooltips[i + 1];
      }
    });

    // Section 2 source labels
    const s2SrcRow = s2HeaderRow + 1;
    const s2Sources = new Array(TOTAL_COLS).fill('');
    s2Sources[0] = 'SOH'; s2Sources[2] = 'SOH'; s2Sources[5] = 'AUTO'; s2Sources[6] = 'CALCULATED';
    s2Sources[COL_FLAGS - 1] = 'CALCULATED';
    s2Sources[COL_AVG12 - 1] = 'Monthly Sales'; s2Sources[COL_AVG6 - 1] = 'Monthly Sales'; s2Sources[COL_AVG3 - 1] = 'Monthly Sales';
    s2Sources[COL_SPLY_LOW - 1] = 'Monthly Sales'; s2Sources[COL_SPLY_HIGH - 1] = 'Monthly Sales';
    s2Sources[COL_SOH - 1] = 'SOH';
    s2Sources[COL_INCOMING - 1] = 'PO'; s2Sources[COL_BO - 1] = 'Back Orders'; s2Sources[COL_PROJ - 1] = 'FORMULA';
    s2Sources[COL_BURN - 1] = 'CALCULATED'; s2Sources[COL_STARR - 1] = 'CALCULATED';
    s2Sources[COL_COVER - 1] = 'CALCULATED'; s2Sources[COL_LOW - 1] = 'FORMULA'; s2Sources[COL_HIGH - 1] = 'FORMULA';
    s2Sources.forEach((s, i) => {
      const cell = ws.getRow(s2SrcRow).getCell(i + 1);
      cell.fill = ltPurpleFill;
      cell.border = thinBorder;
      if (s) {
        cell.value = s;
        cell.font = { name: 'Arial', italic: true, size: 7, color: { argb: 'FF666666' } };
        cell.alignment = { horizontal: 'center' };
      }
    });

    // Section 2 data — lifecycle-tinted backgrounds
    const S2_START = s2SrcRow + 1;
    const S2_LC_FILLS = {
      Core:             [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEE6F3' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F4FB' } }],
      Active:           [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0EDEC' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F9F8' } }],
      New:              [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8DDF0' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3EEF8' } }],
      'Selling Through':[{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5EEE0' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF6F0' } }],
      Archived:         [{ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0E0E4' } }, { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F0F2' } }],
    };
    let s2LcCounter = {};
    s2.forEach((p, i) => {
      const row = S2_START + i;
      const r = ws.getRow(row);
      const s2LcIdx = s2LcCounter[p.lifecycle] = (s2LcCounter[p.lifecycle] || 0);
      s2LcCounter[p.lifecycle]++;
      const s2Fills = S2_LC_FILLS[p.lifecycle] || S2_LC_FILLS.Core;
      const rowBg = s2Fills[s2LcIdx % 2];
      for (let c = 1; c <= TOTAL_COLS; c++) r.getCell(c).fill = rowBg;

      const s2NoOrder = (p.lifecycle === 'Archived');
      const s2Font = s2NoOrder ? { ...inputFont, color: { argb: 'FF999999' } } : inputFont;

      // Code, Desc, Category, Lifecycle (S2 has no Exo, Qty/Ctn, or Price)
      r.getCell(1).value = p.code; r.getCell(1).font = s2Font; r.getCell(1).border = thinBorder;
      r.getCell(3).value = p.description; r.getCell(3).font = s2Font; r.getCell(3).border = thinBorder;
      r.getCell(6).value = p.category; r.getCell(6).font = s2Font; r.getCell(6).border = thinBorder;
      r.getCell(7).value = p.lifecycle; r.getCell(7).font = LC_LIFECYCLE_FONTS[p.lifecycle] || s2Font; r.getCell(7).border = thinBorder;

      // Flags
      r.getCell(COL_FLAGS).value = p.flags || '';
      r.getCell(COL_FLAGS).font = { name: 'Arial', size: 8, color: { argb: 'FFE65100' } }; r.getCell(COL_FLAGS).border = thinBorder;
      r.getCell(COL_FLAGS).alignment = { horizontal: 'center', wrapText: true };

      // Avg Monthly (12mo, 6mo, 3mo)
      r.getCell(COL_AVG12).value = Math.round(p.avg12); r.getCell(COL_AVG12).font = greenFont; r.getCell(COL_AVG12).border = thinBorder; r.getCell(COL_AVG12).numFmt = '#,##0';
      r.getCell(COL_AVG6).value = Math.round(p.avg6); r.getCell(COL_AVG6).font = greenFont; r.getCell(COL_AVG6).border = thinBorder; r.getCell(COL_AVG6).numFmt = '#,##0';
      r.getCell(COL_AVG3).value = Math.round(p.avg3); r.getCell(COL_AVG3).font = { name: 'Arial', bold: true, size: 9 }; r.getCell(COL_AVG3).border = thinBorder; r.getCell(COL_AVG3).numFmt = '#,##0';

      // Units Sold For Low/High Cover
      r.getCell(COL_SPLY_LOW).value = Math.round(p.samePeriodLY_low || 0);
      r.getCell(COL_SPLY_LOW).font = { name: 'Arial', color: { argb: 'FF9E9E9E' }, size: 9 }; r.getCell(COL_SPLY_LOW).border = thinBorder; r.getCell(COL_SPLY_LOW).numFmt = '#,##0';
      r.getCell(COL_SPLY_HIGH).value = Math.round(p.samePeriodLY_high || 0);
      r.getCell(COL_SPLY_HIGH).font = { name: 'Arial', color: { argb: 'FF9E9E9E' }, size: 9 }; r.getCell(COL_SPLY_HIGH).border = thinBorder; r.getCell(COL_SPLY_HIGH).numFmt = '#,##0';

      // SOH
      r.getCell(COL_SOH).value = p.soh; r.getCell(COL_SOH).font = greenFont; r.getCell(COL_SOH).border = thinBorder; r.getCell(COL_SOH).numFmt = '#,##0';

      // Per-PO cols in S2 — leave blank (not on order form)
      for (let pi = 0; pi < numPOs; pi++) {
        r.getCell(COL_PO_START + pi).value = null; r.getCell(COL_PO_START + pi).border = thinBorder;
      }

      // Incoming (flat value for S2)
      r.getCell(COL_INCOMING).value = p.incoming; r.getCell(COL_INCOMING).font = greenFont; r.getCell(COL_INCOMING).border = thinBorder; r.getCell(COL_INCOMING).numFmt = '#,##0';
      // Back Orders (between Incoming and Projected)
      r.getCell(COL_BO).value = p.backOrder; r.getCell(COL_BO).font = { name: 'Arial', color: { argb: 'FFE65100' }, size: 9 }; r.getCell(COL_BO).border = thinBorder; r.getCell(COL_BO).numFmt = '#,##0';
      // Projected Stock = SOH + Incoming - Back Orders
      r.getCell(COL_PROJ).value = { formula: `${LET_SOH}${row}+${LET_INCOMING}${row}-${LET_BO}${row}` }; r.getCell(COL_PROJ).font = formulaFont; r.getCell(COL_PROJ).border = thinBorder; r.getCell(COL_PROJ).numFmt = '#,##0';
      // Est. Sales to Arrival
      r.getCell(COL_BURN).value = p.burnToArrival || 0;
      r.getCell(COL_BURN).font = { name: 'Arial', color: { argb: 'FFE65100' }, size: 9 }; r.getCell(COL_BURN).border = thinBorder; r.getCell(COL_BURN).numFmt = '#,##0';
      // Stock at Arrival
      r.getCell(COL_STARR).value = { formula: `MAX(0,${LET_PROJ}${row}-${LET_BURN}${row})` };
      r.getCell(COL_STARR).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF1565C0' } }; r.getCell(COL_STARR).border = thinBorder; r.getCell(COL_STARR).numFmt = '#,##0';
      // Months of Cover — pre-calculated using seasonal depletion or simple avg
      const s2ci = p.coverInfo || { cover: 0, coverMethod: 'none', dataMonths: 0 };
      if (s2ci.coverMethod === 'none' && p.lifecycle === 'New') {
        r.getCell(COL_COVER).value = 'NEW';
        r.getCell(COL_COVER).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF6A1B9A' } };
      } else if (s2ci.coverMethod === 'none') {
        r.getCell(COL_COVER).value = 'N/A';
        r.getCell(COL_COVER).font = { name: 'Arial', size: 9, color: { argb: 'FF999999' } };
      } else {
        r.getCell(COL_COVER).value = s2ci.cover;
        r.getCell(COL_COVER).font = formulaFont;
        if (s2ci.dataMonths < 13 && s2ci.dataMonths > 0) {
          r.getCell(COL_COVER).note = `Based on ${s2ci.dataMonths} month${s2ci.dataMonths === 1 ? '' : 's'} of data (simple average).`;
        }
      }
      r.getCell(COL_COVER).border = thinBorder; r.getCell(COL_COVER).numFmt = '0.0';
      // Order LOW / HIGH — uses seasonal demand (S2 has no qty/ctn, round to whole units)
      // stockAtArrival accounts for expected sales between now and arrival
      const s2eff = p.stockAtArrival !== undefined ? p.stockAtArrival : (p.soh + p.incoming - p.backOrder);
      if (s2NoOrder) {
        r.getCell(COL_LOW).value = 0; r.getCell(COL_HIGH).value = 0;
      } else {
        r.getCell(COL_LOW).value = Math.max(0, Math.round((p.demandLow || 0) - s2eff));
        r.getCell(COL_HIGH).value = Math.max(0, Math.round((p.demandHigh || 0) - s2eff));
      }
      r.getCell(COL_LOW).font = s2NoOrder ? { ...formulaFont, color: { argb: 'FF999999' } } : formulaFont; r.getCell(COL_LOW).border = thinBorder; r.getCell(COL_LOW).numFmt = '#,##0'; r.getCell(COL_LOW).fill = ltBlueFill;
      r.getCell(COL_HIGH).font = s2NoOrder ? { ...formulaFont, color: { argb: 'FF999999' } } : formulaFont; r.getCell(COL_HIGH).border = thinBorder; r.getCell(COL_HIGH).numFmt = '#,##0'; r.getCell(COL_HIGH).fill = ltBlueFill;

      // Manual Order Qty — empty cell for user input
      r.getCell(COL_MANUAL).value = null;
      r.getCell(COL_MANUAL).font = { name: 'Arial', bold: true, size: 10 }; r.getCell(COL_MANUAL).border = thinBorder; r.getCell(COL_MANUAL).numFmt = '#,##0';
      r.getCell(COL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
    });

    const s2LastRow = S2_START + s2.length - 1;
    const s2TotalRow2 = s2LastRow + 2;
    for (let c = 1; c <= TOTAL_COLS; c++) ws.getRow(s2TotalRow2).getCell(c).fill = ltPurpleFill;
    ws.getCell(`A${s2TotalRow2}`).value = 'SPECIAL PRODUCTION TOTALS';
    ws.getCell(`A${s2TotalRow2}`).font = { name: 'Arial', bold: true, size: 10, color: { argb: 'FF7B2D8E' } };
    const s2TotalFont = { name: 'Arial', bold: true, size: 10, color: { argb: 'FF7B2D8E' } };
    // "X with sales" count
    ws.getRow(s2TotalRow2).getCell(COL_AVG3).value = { formula: `COUNTIF(${colLetter(COL_AVG3)}${S2_START}:${colLetter(COL_AVG3)}${s2LastRow},">0")` };
    ws.getRow(s2TotalRow2).getCell(COL_AVG3).font = { name: 'Arial', bold: true, size: 9, color: { argb: 'FF7B2D8E' } };
    ws.getRow(s2TotalRow2).getCell(COL_AVG3).numFmt = '0" with sales"'; ws.getRow(s2TotalRow2).getCell(COL_AVG3).border = thinBorder;
    // SOH total
    ws.getRow(s2TotalRow2).getCell(COL_SOH).value = { formula: `SUM(${LET_SOH}${S2_START}:${LET_SOH}${s2LastRow})` };
    ws.getRow(s2TotalRow2).getCell(COL_SOH).font = s2TotalFont; ws.getRow(s2TotalRow2).getCell(COL_SOH).numFmt = '#,##0'; ws.getRow(s2TotalRow2).getCell(COL_SOH).border = thinBorder;
    // Incoming total
    ws.getRow(s2TotalRow2).getCell(COL_INCOMING).value = { formula: `SUM(${colLetter(COL_INCOMING)}${S2_START}:${colLetter(COL_INCOMING)}${s2LastRow})` };
    ws.getRow(s2TotalRow2).getCell(COL_INCOMING).font = s2TotalFont; ws.getRow(s2TotalRow2).getCell(COL_INCOMING).numFmt = '#,##0'; ws.getRow(s2TotalRow2).getCell(COL_INCOMING).border = thinBorder;
    // Back Orders total
    ws.getRow(s2TotalRow2).getCell(COL_BO).value = { formula: `SUM(${LET_BO}${S2_START}:${LET_BO}${s2LastRow})` };
    ws.getRow(s2TotalRow2).getCell(COL_BO).font = { name: 'Arial', bold: true, size: 10, color: { argb: 'FFE65100' } }; ws.getRow(s2TotalRow2).getCell(COL_BO).numFmt = '#,##0'; ws.getRow(s2TotalRow2).getCell(COL_BO).border = thinBorder;
    // Projected Stock total
    ws.getRow(s2TotalRow2).getCell(COL_PROJ).value = { formula: `SUM(${colLetter(COL_PROJ)}${S2_START}:${colLetter(COL_PROJ)}${s2LastRow})` };
    ws.getRow(s2TotalRow2).getCell(COL_PROJ).font = s2TotalFont; ws.getRow(s2TotalRow2).getCell(COL_PROJ).numFmt = '#,##0'; ws.getRow(s2TotalRow2).getCell(COL_PROJ).border = thinBorder;
    // Order LOW & HIGH totals
    [COL_LOW, COL_HIGH].forEach(c => {
      const cell = ws.getRow(s2TotalRow2).getCell(c);
      cell.value = { formula: `SUM(${colLetter(c)}${S2_START}:${colLetter(c)}${s2LastRow})` };
      cell.font = { name: 'Arial', bold: true, size: 10 }; cell.numFmt = '#,##0'; cell.fill = ltBlueFill; cell.border = thinBorder;
    });
    // Manual Order total
    ws.getRow(s2TotalRow2).getCell(COL_MANUAL).value = { formula: `SUM(${LET_MANUAL}${S2_START}:${LET_MANUAL}${s2LastRow})` };
    ws.getRow(s2TotalRow2).getCell(COL_MANUAL).font = s2TotalFont; ws.getRow(s2TotalRow2).getCell(COL_MANUAL).numFmt = '#,##0';
    ws.getRow(s2TotalRow2).getCell(COL_MANUAL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }; ws.getRow(s2TotalRow2).getCell(COL_MANUAL).border = thinBorder;

    // Column widths: Code, Exo, Desc, Qty/Ctn, Price, Category, Lifecycle, Flags, Avg12, Avg6, Avg3, UnitsSoldLow, UnitsSoldHigh, SOH, [POs], Incoming, BO, Proj, Burn, StockAtArrival, Cover, LOW, HIGH, ManualQty, ValueLOW, ValueHIGH, ManualValue
    const baseWidths = [14,14,32,8,12,15,16,16,14,14,14,16,16,10];
    const poWidths = new Array(numPOs).fill(10);
    const endWidths = [13,11,14,14,15,11,16,16,14,15,15,15];
    const widths = [...baseWidths, ...poWidths, ...endWidths];
    widths.forEach((w, i) => {
      ws.getColumn(i + 1).width = w;
    });

    // Freeze panes
    // Freeze: rows 1-6 (headers) and columns A+B+C (code, exo, description)
    ws.views = [{ state: 'frozen', ySplit: 6, xSplit: 3 }];
    // Hide column B (Exo Code) by default
    ws.getColumn(2).hidden = true;

    // Conditional formatting - Months of Cover (Section 1)
    ws.addConditionalFormatting({
      ref: `${LET_COVER}${DATA_START}:${LET_COVER}${lastDataRow}`,
      rules: [
        { type: 'cellIs', operator: 'lessThan', formulae: [2], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } }, priority: 1 },
        { type: 'cellIs', operator: 'between', formulae: [2, 4], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFF2CC' } } }, priority: 2 },
        { type: 'cellIs', operator: 'greaterThan', formulae: [4], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' } } }, priority: 3 },
      ]
    });
    // Conditional formatting - Flags (Section 1) — highlight DECLINING/GROWING
    ws.addConditionalFormatting({
      ref: `${LET_FLAGS}${DATA_START}:${LET_FLAGS}${lastDataRow}`,
      rules: [
        { type: 'containsText', operator: 'containsText', text: 'GROWING', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' } } }, priority: 4 },
        { type: 'containsText', operator: 'containsText', text: 'DECLINING', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } }, priority: 5 },
        { type: 'containsText', operator: 'containsText', text: 'SPIKE', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFF2CC' } } }, priority: 6 },
      ]
    });
    // Same for Section 2
    if (s2.length > 0) {
      ws.addConditionalFormatting({
        ref: `${LET_COVER}${S2_START}:${LET_COVER}${s2LastRow}`,
        rules: [
          { type: 'cellIs', operator: 'lessThan', formulae: [2], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } }, priority: 7 },
          { type: 'cellIs', operator: 'between', formulae: [2, 4], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFF2CC' } } }, priority: 8 },
          { type: 'cellIs', operator: 'greaterThan', formulae: [4], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' } } }, priority: 9 },
        ]
      });
      ws.addConditionalFormatting({
        ref: `${LET_FLAGS}${S2_START}:${LET_FLAGS}${s2LastRow}`,
        rules: [
          { type: 'containsText', operator: 'containsText', text: 'GROWING', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' } } }, priority: 10 },
          { type: 'containsText', operator: 'containsText', text: 'DECLINING', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } }, priority: 11 },
          { type: 'containsText', operator: 'containsText', text: 'SPIKE', style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFF2CC' } } }, priority: 12 },
        ]
      });
    }

    // Auto-filter
    ws.autoFilter = { from: { row: 6, column: 1 }, to: { row: lastDataRow, column: TOTAL_COLS } };

    // ---- SHEET 2: MONTHLY SALES DATA ----
    const ws2 = wb.addWorksheet('Monthly Sales');
    {
      // Build a tab showing raw monthly sales data per product
      const allProducts = [...s1.map(p => ({...p, sect: 1})), ...s2.map(p => ({...p, sect: 2}))];
      // Collect all unique month labels across all products
      const monthSet = new Set();
      allProducts.forEach(p => {
        if (p.monthlyArr) p.monthlyArr.forEach(m => monthSet.add(m.label));
      });
      // Sort month labels chronologically (they're like "Jan 24", "Feb 24"...)
      const monthLabels = Array.from(monthSet).sort((a, b) => {
        const parse = s => { const [m, y] = s.split(' '); const mi = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].indexOf(m); return (parseInt(y) + 2000) * 12 + mi; };
        return parse(a) - parse(b);
      });

      // Headers
      const msHeaders = ['Code', 'Description', 'Lifecycle', ...monthLabels, 'Total'];
      const msHeaderFont = { name: 'Arial', bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      const msBlueFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } };
      const msPurpleFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B2D8E' } };
      ws2.getRow(1).height = 28;
      msHeaders.forEach((h, i) => {
        const cell = ws2.getRow(1).getCell(i + 1);
        cell.value = h;
        cell.font = msHeaderFont;
        cell.fill = msBlueFill;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = thinBorder;
      });

      // Data rows
      let msRow = 2;
      let curSect = 0;
      allProducts.forEach(p => {
        // Section divider
        if (p.sect !== curSect) {
          curSect = p.sect;
          if (msRow > 2) msRow++; // blank row between sections
          const divCell = ws2.getRow(msRow).getCell(1);
          divCell.value = curSect === 1 ? 'SECTION 1: ORDER FORM PRODUCTS' : 'SECTION 2: SPECIAL PRODUCTION';
          divCell.font = { name: 'Arial', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
          const sFill = curSect === 1 ? msBlueFill : msPurpleFill;
          for (let c = 1; c <= msHeaders.length; c++) ws2.getRow(msRow).getCell(c).fill = sFill;
          msRow++;
        }
        const r = ws2.getRow(msRow);
        r.getCell(1).value = p.code; r.getCell(1).font = inputFont; r.getCell(1).border = thinBorder;
        r.getCell(2).value = p.description; r.getCell(2).font = inputFont; r.getCell(2).border = thinBorder;
        r.getCell(3).value = p.lifecycle;
        r.getCell(3).font = LC_LIFECYCLE_FONTS[p.lifecycle] || inputFont; r.getCell(3).border = thinBorder;

        // Monthly data
        let total = 0;
        if (p.monthlyArr && p.monthlyArr.length > 0) {
          const byLabel = {};
          p.monthlyArr.forEach(m => { byLabel[m.label] = m.qty; });
          monthLabels.forEach((ml, mi) => {
            const cell = r.getCell(4 + mi);
            const qty = byLabel[ml] || 0;
            cell.value = qty;
            cell.font = qty > 0 ? { name: 'Arial', size: 9 } : { name: 'Arial', size: 9, color: { argb: 'FFCCCCCC' } };
            cell.border = thinBorder;
            cell.numFmt = '#,##0';
            total += qty;
          });
        } else {
          monthLabels.forEach((ml, mi) => {
            const cell = r.getCell(4 + mi);
            cell.value = 0;
            cell.font = { name: 'Arial', size: 9, color: { argb: 'FFCCCCCC' } };
            cell.border = thinBorder; cell.numFmt = '#,##0';
          });
        }
        // Total
        const totalCell = r.getCell(4 + monthLabels.length);
        totalCell.value = total;
        totalCell.font = { name: 'Arial', bold: true, size: 9 };
        totalCell.border = thinBorder; totalCell.numFmt = '#,##0';
        msRow++;
      });

      // Column widths
      ws2.getColumn(1).width = 14;
      ws2.getColumn(2).width = 30;
      ws2.getColumn(3).width = 14;
      for (let i = 0; i < monthLabels.length; i++) ws2.getColumn(4 + i).width = 8;
      ws2.getColumn(4 + monthLabels.length).width = 10;

      // Freeze header row
      ws2.views = [{ state: 'frozen', ySplit: 1, xSplit: 3 }];
    }

    // ---- SHEET 3: PRODUCTION SCHEDULE (SVG timeline as image) ----
    {
      const ws4 = wb.addWorksheet('Production Schedule');
      const lowM = STATE.config.lowCoverage ?? 2;
      const highM = STATE.config.highCoverage ?? 4;

      // Title row
      ws4.getCell('A1').value = 'PRODUCTION SCHEDULE & COVERAGE TIMELINE';
      ws4.getCell('A1').font = { name: 'Arial', bold: true, size: 14, color: { argb: 'FF2F5496' } };
      ws4.mergeCells('A1:H1');

      // Coverage info
      ws4.getCell('A2').value = `Generated: ${new Date().toLocaleDateString('en-AU')}  |  LOW coverage: ${lowM} months  |  HIGH coverage: ${highM} months`;
      ws4.getCell('A2').font = { name: 'Arial', size: 9, color: { argb: 'FF666666' } };
      ws4.mergeCells('A2:H2');

      // Coverage explanation
      const curLbl = STATE.schedule.currentRun ? STATE.schedule.currentRun.run : '?';
      const curArr = STATE.schedule.currentRun ? STATE.schedule.currentRun.arrivalStr : '?';
      const nxtLbl = STATE.schedule.nextRun ? STATE.schedule.nextRun.run : '?';
      const nxtArr = STATE.schedule.nextRun ? STATE.schedule.nextRun.arrivalStr : '?';
      const ranLbl = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : '?';
      const ranArr = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.arrivalStr : '?';
      ws4.getCell('A3').value = `LOW = ${curLbl} in warehouse (${curArr}) → ${nxtLbl} in warehouse (${nxtArr}).  HIGH = ${curLbl} in warehouse → ${ranLbl} in warehouse (${ranArr}).  Transit = 6 weeks from ex-factory.`;
      ws4.getCell('A3').font = { name: 'Arial', size: 9, color: { argb: 'FF666666' } };
      ws4.mergeCells('A3:H3');

      // Capture SVG from DOM and convert to PNG
      const svgEl = document.querySelector('#timeline-graphic svg');
      if (svgEl) {
        try {
          // Inline the CSS styles into the SVG
          const styleBlock = `<defs><style>
            .tl-label{font-family:Arial,sans-serif;font-size:11px;fill:#333}
            .tl-label-bold{font-family:Arial,sans-serif;font-size:11px;fill:#333;font-weight:700}
            .tl-date{font-family:Arial,sans-serif;font-size:9px;fill:#888}
            .tl-legend{font-family:Arial,sans-serif;font-size:10px;fill:#555}
          </style></defs>`;

          // Serialize SVG and inject styles
          let svgStr = new XMLSerializer().serializeToString(svgEl);
          svgStr = svgStr.replace(/<svg([^>]*)>/, `<svg$1>${styleBlock}`);

          // Add white background
          const bgRect = '<rect width="100%" height="100%" fill="white"/>';
          svgStr = svgStr.replace(styleBlock, styleBlock + bgRect);

          // Convert to data URL
          const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
          const svgUrl = URL.createObjectURL(svgBlob);

          // Render to canvas at 2x for crisp output
          const img = new Image();
          img.src = svgUrl;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            setTimeout(reject, 5000);
          });

          const scale = 2;
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth * scale;
          canvas.height = img.naturalHeight * scale;
          const ctx = canvas.getContext('2d');
          ctx.scale(scale, scale);
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, img.naturalWidth, img.naturalHeight);
          ctx.drawImage(img, 0, 0);

          URL.revokeObjectURL(svgUrl);

          // Get PNG base64
          const pngDataUrl = canvas.toDataURL('image/png');
          const base64 = pngDataUrl.split(',')[1];

          // Add image to workbook
          const imageId = wb.addImage({ base64, extension: 'png' });

          // Calculate image dimensions in Excel
          const svgW = img.naturalWidth;
          const svgH = img.naturalHeight;
          const aspect = svgH / svgW;
          const targetCols = 18;
          const targetRows = Math.max(Math.ceil(targetCols * 12 * aspect / 15), 30);

          ws4.addImage(imageId, {
            tl: { col: 0, row: 4 },
            br: { col: targetCols, row: 4 + targetRows }
          });

          // Set column widths to accommodate the image
          for (let c = 1; c <= targetCols; c++) {
            ws4.getColumn(c).width = 10;
          }
        } catch (e) {
          // Fallback: just note that the image couldn't be generated
          ws4.getCell('A5').value = 'Timeline image could not be generated. View the timeline in the order tool.';
          ws4.getCell('A5').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF999999' } };
        }
      } else {
        ws4.getCell('A5').value = 'No timeline data available. Complete the Production Schedule step first.';
        ws4.getCell('A5').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF999999' } };
      }
    }

    // ---- SHEET 4: HOW TO USE ----
    const ws3 = wb.addWorksheet('How To Use');
    ws3.getColumn(1).width = 100;
    ws3.getColumn(2).width = 20;
    ws3.getCell('A1').value = 'HOW TO USE THIS ORDER CALCULATOR';
    ws3.getCell('A1').font = { name: 'Arial', bold: true, size: 14, color: { argb: 'FF2F5496' } };

    // Build coverage timeline diagram text
    const curLabel = STATE.schedule.currentRun ? STATE.schedule.currentRun.run : `Run ${runNum}`;
    const curETA = STATE.schedule.currentRun ? STATE.schedule.currentRun.arrivalStr : '?';
    const nxtLabel = STATE.schedule.nextRun ? STATE.schedule.nextRun.run : `Run ${runNum + 1}`;
    const nxtETA = STATE.schedule.nextRun ? STATE.schedule.nextRun.arrivalStr : '?';
    const ranLabel = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : `Run ${runNum + 2}`;
    const ranETA = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.arrivalStr : '?';

    const instructions = [
      ['', true], ['OVERVIEW', true],
      [`This spreadsheet has ${s1.length} products from the Run ${runNum} Order Form plus ${s2.length} special production candidates, matched with real sales and stock data.`, false],
      ['Products are sorted by lifecycle: CORE first, then ACTIVE, NEW, SELLING THROUGH, and ARCHIVED last. Within each category, sorted by Months of Cover (lowest first = most urgent).', false],
      ['', true], ['ORDER COVERAGE TIMELINE', true],
      [`You are ordering ${curLabel} now. The order quantities ensure you have enough stock to cover demand until the next runs arrive in your warehouse.`, false],
      [`Warehouse arrival = Ex Factory date + 6 weeks transit.`, false],
      ['', false],
      [`   ${curLabel} in warehouse           ${nxtLabel} in warehouse            ${ranLabel} in warehouse`, false],
      [`   ${curETA}                                ${nxtETA}                                  ${ranETA}`, false],
      [`       |                                  |                                   |`, false],
      [`       |◄══════ LOW coverage ══════►|                                   |`, false],
      [`       |◄══════════════════ HIGH coverage ═══════════════════►|`, false],
      ['', false],
      [`LOW order qty  = enough stock from ${curLabel} in warehouse until ${nxtLabel} in warehouse  (${low} months)`, false],
      [`HIGH order qty = enough stock from ${curLabel} in warehouse until ${ranLabel} in warehouse  (${high} months)`, false],
      ['Tip: Use LOW if you are confident the next run will arrive on time. Use HIGH for a safety buffer.', false],
      ['', true], ['PRODUCT LIFECYCLE CATEGORIES', true],
      ['CORE — Strong, consistent sellers. High volume with recent demand. These items should always be in stock.', false],
      ['ACTIVE — Regular sellers with steady moderate demand. Not top-tier Core but reliably moving stock.', false],
      ['NEW — New items that may not have had their first delivery yet. Limited stock or pre-orders. Order quantities calculated once sales data builds.', false],
      ['SELLING THROUGH — Items we may top up but are generally selling through remaining stock. Declining or low sales.', false],
      ['ARCHIVED — Sold out and not coming back. No order quantities calculated. Zero stock, zero sales.', false],
      ['You can override any product\'s lifecycle in the Review step of the Order Calculator tool. Overrides are remembered between sessions.', false],
      ['', true], [`KEY COLUMNS — SECTION 1: RUN ${runNum} ORDER FORM`, true],
      ['Columns A-E: Product info from Order Form (code, Exo code, description, Qty/Ctn, Unit Price). Exo code column hidden by default.', false],
      ['Columns F-H: Category, Product Lifecycle Category, Flags', false],
      ['', false],
      ['Column F: Flags — short text flags highlighting items that need your attention (see below).', false],
      ['', false],
      ['Column G: AVG Units/Month 12 Months — average units sold per month over 12 months. Long-run baseline.', false],
      ['Column H: AVG Units/Month 6 Months — same thing over 6 months. Compare with 3mo to spot recent changes.', false],
      ['Column I: AVG Units/Month 3 Months — average units sold per month over the last 3 months. Useful for spotting recent trends.', false],
      ['  Bold in the spreadsheet for visibility. For items with 13+ months of data, seasonal demand (not this average) drives the order qty.', false],
      [`Column J: Units Sold Previous Year For LOW Order Cover (${splyLowLabel || '?'}) — total units sold in those calendar months last year, matching your LOW coverage window.`, false],
      [`Column K: Units Sold Previous Year For HIGH Order Cover (${splyHighLabel || '?'}) — total units sold in those calendar months last year, matching your HIGH coverage window.`, false],
      ['  The first month is pro-rated based on the actual warehouse arrival day (e.g. arriving 21 Aug = only 35% of Aug counted).', false],
      ['  Subsequent months are counted in full, with any remaining partial month at the end also pro-rated.', false],
      ['  Practical sanity check: compare your order qty against what actually sold in the same period last year.', false],
      ['', false],
      ['Column F (Flags) detail:', false],
      ['  SHORT HISTORY — less than 6 months of sales data. The 3mo average may be less reliable.', false],
      ['  GROWING — sales are trending up year-on-year (or recent half vs earlier half). You may want to order more.', false],
      ['  DECLINING — sales are trending down. You may want to order less or skip.', false],
      ['  NEW — barely any sales data (3 months or less). Treat the average with caution.', false],
      ['  SPIKE — one abnormally large month (3x+ median). Possibly a bulk order skewing the average. Check the Monthly Sales tab.', false],
      ['  STOCKOUT — Core products only. One or more months had zero sales after the product started selling, suggesting it was out of stock.', false],
      ['  Those months are excluded from averages, SPLY, and demand calculations to avoid understating true demand.', false],
      ['  Products with fewer than 6 active months of sales are too new for stockout detection.', false],
      ['  Flags can stack, e.g. "GROWING, SPIKE" means both apply.', false],
      ['', false],
      ['Projected Stock = SOH + Incoming - Back Orders (your stock position today)', false],
      ['Est. Sales to Arrival = expected units sold between now and when the current order arrives in your warehouse.', false],
      ['  Uses seasonal sales patterns (same calendar months last year) to estimate this burn period.', false],
      ['Stock at Arrival — uses a weekly simulation that walks forward from today, subtracting seasonal sales each week', false],
      ['  and adding each incoming PO\'s stock at its scheduled warehouse arrival date (Cross Docks Ready).', false],
      ['  This accounts for when each PO actually arrives rather than lumping all incoming stock together.', false],
      ['  This is the number used for Months of Cover and Order Qty calculations — more accurate than using today\'s stock.', false],
      ['', false],
      ['Months of Cover uses two methods depending on how much sales data is available:', false],
      ['  13+ months of data (SEASONAL): Simulates month-by-month stock depletion from Stock at Arrival, using last year\'s same calendar months.', false],
      ['  The first month is pro-rated based on the actual warehouse arrival day (e.g. arriving 21 Aug means only the remaining 11 days of Aug are counted).', false],
      ['  This accounts for seasonal peaks and troughs — a product that sells heavily in Dec will burn stock faster in Dec.', false],
      ['  Products with * next to cover have <13 months of data and use the simpler method below.', false],
      ['  <13 months of data (SIMPLE): Stock at Arrival / average of ALL available months. Less precise but best we can do with limited data.', false],
      ['', false],
      ['Order LOW/HIGH also use the same seasonal/simple logic — they calculate total expected demand over the coverage period,', false],
      ['then subtract what you already have. This means order quantities adapt to upcoming seasonal demand.', false],
      ['', false],
      ['Manual Order Qty — enter your actual order quantity here. Use LOW and HIGH as guides, then decide your final order.', false],
      ['Manual Order Value = Manual Order Qty × Unit Price. Calculates automatically when you enter a Manual Order Qty.', false],
      ['Order Value LOW/HIGH = Order Qty LOW/HIGH × Unit Price. Shows the cost of each scenario for comparison.', false],
      ['', true], ['MONTHLY SALES TAB', true],
      ['The "Monthly Sales" tab shows raw month-by-month unit sales for every product.', false],
      ['Use it to verify the averages, check for spikes, and understand why a product got flagged.', false],
      ['The averages on the main sheet are calculated from this raw data. No hidden formulas — you can check every number.', false],
      ['', true], ['WORKED EXAMPLE — SEASONAL (13+ months of data)', true],
      ['Say "nanoblock Koala NBC_020" has 15 months of sales history ending in Jul. Arrival is in 3 months.', false],
      ['  SOH: 80   |   Incoming: 50   |   Back Orders: 10   |   Projected Stock: 120', false],
      ['  Est. Sales to Arrival (3 months): ~90 units   |   Stock at Arrival: 120 - 90 = 30', false],
      ['', false],
      ['Step 1 — Averages (same as before):', false],
      ['  Avg 3mo (May=50, Jun=42, Jul=38): (50+42+38)/3 = 43/mo', false],
      ['  Avg 6mo, Avg 12mo: averages of last 6 and 12 months respectively', false],
      ['', false],
      ['Step 2 — Seasonal Cover: With 15 months of data, the tool walks forward month-by-month', false],
      ['  starting from the WAREHOUSE ARRIVAL DATE of the current order, using STOCK AT ARRIVAL (not Projected Stock).', false],
      [`  e.g. if ${curLabel} arrives ${curETA}, it starts from that month and looks at last year\'s sales for those months.`, false],
      ['  Say arrival is June, Stock at Arrival = 30, and last year: Jun=30, Jul=28, Aug=35', false],
      ['  Month 1 (Jun): 30 - 30 = 0 remaining → runs out at 1.0 months', false],
      ['  Total: 1.0 month of cover — much less than the naive 120/43 = 2.8 which ignores the burn before arrival.', false],
      ['', false],
      ['  The difference matters! If Dec is a big sales month and you are heading into it, seasonal', false],
      ['  cover warns you earlier. If you are heading into a quiet period, it gives you more time.', false],
      ['', false],
      ['Step 3 — Order Qty: Total seasonal demand from arrival date forward, minus Stock at Arrival.', false],
      [`  LOW: expected demand over ${low} months from arrival (using last year\'s calendar months) - 30 (stock at arrival) = order qty`, false],
      [`  HIGH: expected demand over ${high} months from arrival (using last year\'s calendar months) - 30 (stock at arrival) = order qty`, false],
      ['', false],
      ['', true], ['WORKED EXAMPLE — SIMPLE (<13 months of data)', true],
      ['Say "nanoblock Platypus NBC_299" is newer with only 5 months of sales: Mar=12, Apr=15, May=18, Jun=20, Jul=22', false],
      ['  SOH: 40   |   Incoming: 0   |   Back Orders: 5   |   Projected Stock: 35', false],
      ['  This product shows * next to its cover (indicating limited data).', false],
      ['', false],
      ['  Average of all 5 months: (12+15+18+20+22)/5 = 17.4/mo', false],
      ['  Months of Cover = 35 / 17.4 = 2.0 months', false],
      [`  LOW order = (17.4 x ${low}) - 35 = ${Math.round(17.4 * low - 35) > 0 ? Math.round(17.4 * low - 35) : 0} units`, false],
      [`  HIGH order = (17.4 x ${high}) - 35 = ${Math.round(17.4 * high - 35) > 0 ? Math.round(17.4 * high - 35) : 0} units`, false],
      ['', false],
      ['  Because we don\'t have a full year of data, we can\'t use seasonal patterns — the simple average is the best we have.', false],
      ['  Check the Monthly Sales tab and use your judgement for these products.', false],
      ['', true], ['SECTION 2: SPECIAL PRODUCTION (purple headers)', true],
      [`${s2.length} products in your stock but NOT on the Run ${runNum} Order Form.`, false],
      ['If months of cover is low (red), consider requesting special production from factory.', false],
      ['', true], ['HOW TO ADJUST', true],
      ['Coverage targets (LOW and HIGH months) are set in the tool before generating. To change them, re-run the tool with different values.', false],
      ['Use auto-filter (row 6) to filter Section 1 by any column.', false],
      ['', true], ['MONTHS OF COVER TRAFFIC LIGHTS', true],
      ['RED = less than 2 months cover (order urgently)', false],
      ['YELLOW = 2-4 months cover (monitor)', false],
      ['GREEN = more than 4 months cover (OK for now)', false],
    ];
    instructions.forEach(([text, isHeader], i) => {
      const cell = ws3.getCell(`A${2 + i}`);
      cell.value = text;
      if (isHeader && text) {
        cell.font = { name: 'Arial', bold: true, size: 11, color: { argb: 'FF2F5496' } };
      } else if (text && (text.includes('◄') || text.includes('|'))) {
        cell.font = { name: 'Courier New', size: 9 }; // monospace for the diagram
      } else {
        cell.font = { name: 'Arial', size: 9 };
      }
    });

    // ---- GENERATE & DOWNLOAD ----
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Nanoblock_${runNum}_Order_Calculator.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    document.getElementById('gen-status').textContent = `Downloaded Nanoblock_${runNum}_Order_Calculator.xlsx`;
    document.getElementById('gen-btn').textContent = 'Download Again';
    markStepComplete(7);
  } catch (err) {
    console.error('Generation error:', err);
    document.getElementById('gen-status').textContent = 'Error: ' + err.message;
    document.getElementById('gen-status').style.color = 'var(--red)';
  } finally {
    overlay.classList.remove('active');
  }
}

// ============================================================
// GOOGLE SHEETS OUTPUT
// ============================================================
async function sendToGoogleSheet() {
  const overlay = document.getElementById('genOverlay');
  overlay.classList.add('active');
  const statusEl = document.getElementById('gen-status');
  statusEl.textContent = 'Sending data to Google Sheets...';
  statusEl.style.color = '';

  await new Promise(r => setTimeout(r, 100));

  try {
    const runNum = STATE.config.runNumber;
    const low = STATE.config.lowCoverage;
    const high = STATE.config.highCoverage;
    const today = new Date();
    const todayStr = today.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const sohDateStr = STATE.config.sohDate ? (() => { const d = new Date(STATE.config.sohDate); return ` | SOH as at: ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })() : '';
    const arrivalStr = STATE.schedule.currentRun ? STATE.schedule.currentRun.arrivalStr : '?';
    const burnMonths = (STATE.schedule.currentRun && STATE.schedule.currentRun.arrivalDate)
      ? Math.max(0, monthsBetween(today, STATE.schedule.currentRun.arrivalDate))
      : 0;

    const currentRunLabel = STATE.schedule.currentRun ? STATE.schedule.currentRun.run : `Run ${runNum}`;
    const nextRunLabel = STATE.schedule.nextRun ? STATE.schedule.nextRun.run : `Run ${runNum + 1}`;
    const runAfterNextLabel = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : `Run ${runNum + 2}`;

    // Apply lifecycle overrides (same as generateXlsx)
    if (STATE.lifecycleOverrides) {
      [...STATE.results.section1, ...STATE.results.section2].forEach(p => {
        const nc = normaliseCode(p.code);
        if (STATE.lifecycleOverrides[nc]) p.lifecycle = STATE.lifecycleOverrides[nc];
      });
    }

    // Sort (same as generateXlsx) — lifecycle then months of cover ascending
    const GS_LC_ORDER = { Core: 0, Active: 1, New: 2, 'Selling Through': 3, Archived: 4 };
    function gsLifecycleSort(a, b) {
      const lcA = GS_LC_ORDER[a.lifecycle] !== undefined ? GS_LC_ORDER[a.lifecycle] : 9;
      const lcB = GS_LC_ORDER[b.lifecycle] !== undefined ? GS_LC_ORDER[b.lifecycle] : 9;
      if (lcA !== lcB) return lcA - lcB;
      const ciA = a.coverInfo || { cover: 0, coverMethod: 'none' };
      const ciB = b.coverInfo || { cover: 0, coverMethod: 'none' };
      const coverA = ciA.coverMethod === 'none' ? 999 : ciA.cover;
      const coverB = ciB.coverMethod === 'none' ? 999 : ciB.cover;
      return coverA - coverB;
    }
    STATE.results.section1.sort(gsLifecycleSort);
    STATE.results.section2.sort(gsLifecycleSort);

    const s1 = STATE.results.section1;
    const s2 = STATE.results.section2;
    const numPOs = STATE.pos.length;
    const poNames = STATE.pos.map(po => po.name);
    const splyLowLabel = s1.find(p => p.splyLowMonthNames)?.splyLowMonthNames || '';
    const splyHighLabel = s1.find(p => p.splyHighMonthNames)?.splyHighMonthNames || '';
    const burnLabel = burnMonths > 0 ? `Est. Sales Now to Arrival (${burnMonths.toFixed(1)} mo)` : 'Est. Sales to Arrival';

    // Build S1 headers
    const s1BaseHeaders = ['Product Code','Exo Code','Description','Qty/Ctn','Unit Price (US$)','Category','Lifecycle Category','Flags',
      'AVG Units/Month 12 Months','AVG Units/Month 6 Months','AVG Units/Month 3 Months',
      `Units Sold Prev Yr LOW (${splyLowLabel || '?'})`,`Units Sold Prev Yr HIGH (${splyHighLabel || '?'})`,
      'Current SOH'];
    const s1PoHeaders = poNames.map(n => `Incoming ${n}`);
    const s1TailHeaders = ['Incoming (Open POs)','Back Orders','Projected Stock',burnLabel,
      `Stock at Arrival (${arrivalStr})`,'Months of Cover',
      `Order Qty LOW (${currentRunLabel} to ${nextRunLabel})`,
      `Order Qty HIGH (${currentRunLabel} to ${runAfterNextLabel})`,
      'Manual Order Qty','Order Value LOW (US$)','Order Value HIGH (US$)','Manual Order Value (US$)'];
    const s1Headers = [...s1BaseHeaders, ...s1PoHeaders, ...s1TailHeaders];

    // Build S1 data rows
    const s1Data = s1.map(p => {
      const isNoOrder = (p.lifecycle === 'Archived');
      const effStock = p.stockAtArrival !== undefined ? p.stockAtArrival : (p.soh + p.incoming - p.backOrder);
      const projStock = p.soh + p.incoming - p.backOrder;
      const stockAtArr = Math.max(0, projStock - (p.burnToArrival || 0));
      const ci = p.coverInfo || { cover: 0, coverMethod: 'none' };
      const coverVal = ci.coverMethod === 'none' ? 'N/A' : ci.cover;
      let orderLow = 0, orderHigh = 0;
      if (!isNoOrder) {
        const rawLow = Math.max(0, (p.demandLow || 0) - effStock);
        const rawHigh = Math.max(0, (p.demandHigh || 0) - effStock);
        const qtyCtn = p.qtyCtn || 0;
        orderLow = qtyCtn > 0 ? Math.ceil(rawLow / qtyCtn) * qtyCtn : Math.round(rawLow);
        orderHigh = qtyCtn > 0 ? Math.ceil(rawHigh / qtyCtn) * qtyCtn : Math.round(rawHigh);
      }
      const ctns = (p.qtyCtn && orderHigh > 0) ? Math.ceil(orderHigh / p.qtyCtn) : 0;
      const valLow = orderLow * (p.price || 0);
      const valHigh = orderHigh * (p.price || 0);

      const baseRow = [
        p.code, p.exoCode || '', p.description, p.qtyCtn || 0, p.price || 0, p.category || '', p.lifecycle, p.flags || '',
        Math.round(p.avg12), Math.round(p.avg6), Math.round(p.avg3),
        Math.round(p.samePeriodLY_low || 0), Math.round(p.samePeriodLY_high || 0),
        p.soh
      ];
      const poRow = [];
      for (let pi = 0; pi < numPOs; pi++) poRow.push(p.perPo[pi] || 0);
      const tailRow = [
        p.incoming, p.backOrder, projStock, p.burnToArrival || 0, stockAtArr,
        coverVal, orderLow, orderHigh, '',
        Math.round(valLow * 100) / 100, Math.round(valHigh * 100) / 100, ''
      ];
      return [...baseRow, ...poRow, ...tailRow];
    });

    // Build S2 headers (no Exo, no Qty/Ctn, no Price, no Ctns, no Value)
    const s2BaseHeaders = ['Product Code','Description','Category','Lifecycle Category','Flags',
      'AVG Units/Month 12 Months','AVG Units/Month 6 Months','AVG Units/Month 3 Months',
      `Units Sold Prev Yr LOW (${splyLowLabel || '?'})`,`Units Sold Prev Yr HIGH (${splyHighLabel || '?'})`,
      'Current SOH','Incoming (Open POs)','Back Orders','Projected Stock',burnLabel,
      `Stock at Arrival (${arrivalStr})`,'Months of Cover',
      `Order Qty LOW (${currentRunLabel} to ${nextRunLabel})`,
      `Order Qty HIGH (${currentRunLabel} to ${runAfterNextLabel})`];
    const s2Headers = s2BaseHeaders;

    // Build S2 data rows
    const s2Data = s2.map(p => {
      const isNoOrder = (p.lifecycle === 'Archived');
      const effStock = p.stockAtArrival !== undefined ? p.stockAtArrival : (p.soh + p.incoming - p.backOrder);
      const projStock = p.soh + p.incoming - p.backOrder;
      const stockAtArr = Math.max(0, projStock - (p.burnToArrival || 0));
      const ci = p.coverInfo || { cover: 0, coverMethod: 'none' };
      const coverVal = ci.coverMethod === 'none' ? 'N/A' : ci.cover;
      let orderLow = 0, orderHigh = 0;
      if (!isNoOrder) {
        orderLow = Math.max(0, Math.round((p.demandLow || 0) - effStock));
        orderHigh = Math.max(0, Math.round((p.demandHigh || 0) - effStock));
      }
      return [
        p.code, p.description, p.category || '', p.lifecycle, p.flags || '',
        Math.round(p.avg12), Math.round(p.avg6), Math.round(p.avg3),
        Math.round(p.samePeriodLY_low || 0), Math.round(p.samePeriodLY_high || 0),
        p.soh, p.incoming, p.backOrder, projStock, p.burnToArrival || 0, stockAtArr,
        coverVal, orderLow, orderHigh
      ];
    });

    // ---- Monthly Sales data ----
    const allProducts = [...s1.map(p => ({...p, sect: 1})), ...s2.map(p => ({...p, sect: 2}))];
    const monthSet = new Set();
    allProducts.forEach(p => { if (p.monthlyArr) p.monthlyArr.forEach(m => monthSet.add(m.label)); });
    const monthLabels = Array.from(monthSet).sort((a, b) => {
      const parse = s => { const [m, y] = s.split(' '); const mi = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].indexOf(m); return (parseInt(y) + 2000) * 12 + mi; };
      return parse(a) - parse(b);
    });
    const msHeaders = ['Code', 'Description', 'Lifecycle', ...monthLabels, 'Total'];
    const msData = [];
    let curSect = 0;
    allProducts.forEach(p => {
      if (p.sect !== curSect) {
        curSect = p.sect;
        if (msData.length > 0) msData.push([]); // blank row
        msData.push([curSect === 1 ? 'SECTION 1: ORDER FORM PRODUCTS' : 'SECTION 2: SPECIAL PRODUCTION']);
      }
      const byLabel = {};
      let total = 0;
      if (p.monthlyArr) p.monthlyArr.forEach(m => { byLabel[m.label] = m.qty; });
      const monthVals = monthLabels.map(ml => { const q = byLabel[ml] || 0; total += q; return q; });
      msData.push([p.code, p.description, p.lifecycle, ...monthVals, total]);
    });

    // ---- Production Schedule data ----
    const schedHeaders = ['Run','Due Date','Ex Factory','Vessel Departure','AU Port Arrival','Cross Docks Ready','Lead Time','Status'];
    const schedData = STATE.schedule.rows.filter(r => r.exFactoryDate).map(r => {
      let status = '';
      if (STATE.schedule.currentRun && r.run === STATE.schedule.currentRun.run) status = '◄ CURRENT ORDER';
      else if (STATE.schedule.nextRun && r.run === STATE.schedule.nextRun.run) status = 'Next run';
      else if (STATE.schedule.runAfterNext && r.run === STATE.schedule.runAfterNext.run) status = 'Run after next';
      else if (r.arrivalDate && r.arrivalDate < new Date()) status = 'Completed';
      return [
        r.run,
        r.dueDate || '',
        r.exFactory || '',
        r.vesselDeparture || '',
        r.auPortArrival || '',
        r.crossDocksReady || '',
        r.leadTime || '',
        status
      ];
    });

    // ---- How To Use content ----
    const curLabel = STATE.schedule.currentRun ? STATE.schedule.currentRun.run : `Run ${runNum}`;
    const curETA = STATE.schedule.currentRun ? STATE.schedule.currentRun.arrivalStr : '?';
    const nxtLabel = STATE.schedule.nextRun ? STATE.schedule.nextRun.run : `Run ${runNum + 1}`;
    const nxtETA = STATE.schedule.nextRun ? STATE.schedule.nextRun.arrivalStr : '?';
    const ranLabel = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.run : `Run ${runNum + 2}`;
    const ranETA = STATE.schedule.runAfterNext ? STATE.schedule.runAfterNext.arrivalStr : '?';
    const howToUse = [
      'HOW TO USE THIS ORDER CALCULATOR',
      '',
      'OVERVIEW',
      `This spreadsheet has ${s1.length} products from the Run ${runNum} Order Form plus ${s2.length} special production candidates, matched with real sales and stock data.`,
      'Products are sorted by lifecycle: CORE first, then ACTIVE, NEW, SELLING THROUGH, and ARCHIVED last. Within each category, sorted by average monthly sales (highest first).',
      '',
      'ORDER COVERAGE TIMELINE',
      `You are ordering ${curLabel} now. The order quantities ensure you have enough stock to cover demand until the next runs arrive in your warehouse.`,
      `Warehouse arrival = Ex Factory date + 6 weeks transit.`,
      '',
      `   ${curLabel} in warehouse           ${nxtLabel} in warehouse            ${ranLabel} in warehouse`,
      `   ${curETA}                                ${nxtETA}                                  ${ranETA}`,
      `       |                                  |                                   |`,
      `       |◄══════ LOW coverage ══════►|                                   |`,
      `       |◄══════════════════ HIGH coverage ═══════════════════►|`,
      '',
      `LOW order qty  = enough stock from ${curLabel} in warehouse until ${nxtLabel} in warehouse  (${low} months)`,
      `HIGH order qty = enough stock from ${curLabel} in warehouse until ${ranLabel} in warehouse  (${high} months)`,
      'Tip: Use LOW if you are confident the next run will arrive on time. Use HIGH for a safety buffer.',
      '',
      'PRODUCT LIFECYCLE CATEGORIES',
      'CORE — Strong, consistent sellers. High volume with recent demand. These items should always be in stock.',
      'ACTIVE — Regular sellers with steady moderate demand. Not top-tier Core but reliably moving stock.',
      'NEW — New items that may not have had their first delivery yet. Limited stock or pre-orders.',
      'SELLING THROUGH — Items we may top up but are generally selling through remaining stock. Declining or low sales.',
      'ARCHIVED — Sold out and not coming back. No order quantities calculated. Zero stock, zero sales.',
      '',
      'MONTHS OF COVER TRAFFIC LIGHTS',
      'RED = less than 2 months cover (order urgently)',
      'YELLOW = 2-4 months cover (monitor)',
      'GREEN = more than 4 months cover (OK for now)'
    ];

    // Send to Apps Script
    const payload = {
      title: `Nanoblock Run ${runNum} Order Calculator — ${todayStr}`,
      s1Headers, s1Data, s2Headers, s2Data,
      msHeaders, msData, schedHeaders, schedData, howToUse,
      metadata: {
        title: `KAWADA AUSTRALIA — Run ${runNum} Order Calculator`,
        subtitle: `Generated: ${todayStr}${sohDateStr} | MOQ: 3,000 pcs/model | Min Order: US$28,000`,
        s1Banner: `SECTION 1: RUN ${runNum} ORDER FORM (${s1.length} products)`,
        s2Banner: `SECTION 2: SPECIAL PRODUCTION REQUESTS — Not on Run ${runNum} Order Form (${s2.length} products)`,
        lowCoverage: low,
        highCoverage: high,
        runNum: runNum
      }
    };

    statusEl.textContent = 'Opening Google Sheet in a new tab...';

    // Use form submission to bypass CORS entirely.
    // Apps Script receives the payload and redirects the new tab to the created sheet.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SYNC_API + '?action=output';
    form.target = '_blank';
    form.style.display = 'none';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'payload';
    input.value = JSON.stringify(payload);
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    statusEl.innerHTML = '<span style="color:var(--green)">Google Sheet opening in a new tab!</span> If it doesn\'t appear, check your pop-up blocker.';
    document.getElementById('gen-gsheet-btn').textContent = 'Create Another Sheet';
    markStepComplete(7);

  } catch (err) {
    console.error('Google Sheet error:', err);
    statusEl.textContent = 'Error: ' + err.message;
    statusEl.style.color = 'var(--red)';
  } finally {
    overlay.classList.remove('active');
  }
}

// ============================================================
// INIT
// ============================================================
function init() {
  renderStepsNav();
  renderNavButtons();
  document.getElementById('step-0').classList.add('active');
  updateDataStatus();

  // Load saved schedule if available
  initSavedSchedule();

  // Auto-process on paste
  ['paste-schedule','paste-orderform','paste-soh','paste-monthly','paste-backorders'].forEach(id => {
    document.getElementById(id).addEventListener('paste', () => {
      setTimeout(() => processCurrentStep(), 100);
    });
    document.getElementById(id).addEventListener('input', () => {
      clearTimeout(window._debounce);
      window._debounce = setTimeout(() => processCurrentStep(), 500);
    });
  });

  // Event delegation for PO paste areas (initial + dynamically added)
  document.getElementById('po-container').addEventListener('paste', (e) => {
    if (e.target.classList.contains('po-paste')) {
      setTimeout(() => processPOs(), 100);
    }
  });
  document.getElementById('po-container').addEventListener('input', (e) => {
    if (e.target.classList.contains('po-paste')) {
      clearTimeout(window._poDebounce);
      window._poDebounce = setTimeout(() => processPOs(), 500);
    }
  });

  // Load shared overrides from Google Sheet and prompt for user name
  loadSharedOverrides();
  promptSyncUser();

}

init();